// src/components/desk/ManualPropertyModal.jsx — the manual property workspace: PointMapPicker,
// the observation-capture option lists (PROPERTY_KIND_OPTIONS/LISTING_INTENT_OPTIONS/
// OBSERVED_FROM_OPTIONS), cityKey/districtInCity/districtReach/DistrictOptions, Facet,
// rental/sale terms recording (RentalTermsSummary, fieldMaturity, RentalTermsByAgent,
// RentalTermsModal), PropertyTimeline + evLabel, PrivateNotesPanel, AvailabilityFacet/
// AvailabilityLine, InventoryControls, PropertyDuplicates and ManualPropertyModal itself —
// extracted VERBATIM from IntakeTab.jsx (DESK_MODE_REWRITE_PLAN §3 decomposition,
// behavior-preserving). Everything IntakeTab (or a sibling desk module) still references is
// exported and imported back there.

import { useEffect, useMemo, useRef, useState } from 'react'
import { nextPetFee } from '../../lib/petFee.js'
import L from 'leaflet'
import { t, tLog, tOr, tIn, getLanguage } from '../../lib/i18n.js'
import { attrLabel } from '../../lib/labels.js'
import { factsDiff, refillGaps, clearedOwnKeys, rebaseAfterSave, failedFactKeys, rebaseAfterSaveWithFailures } from '../../lib/factsDiff.js'
import { KIND_SUBTYPES } from '../../lib/taxonomy.js'
import { ceilingPayload, ceilingRangeBad as isCeilingRangeBad, ceilingRetracts, withCeilingVariation } from '../../lib/ceilingFacts.js'
import { ParseConflictPanel } from './ParseConflictPanel.jsx'
import { splitParse, acceptedPatch } from '../../lib/parseConflicts.js'
import { Icon, IconText } from '../Icon.jsx'
import { card, input, btn, chip, Money, Modal, pill, timeAgo } from './primitives.jsx'
import { BuildingFacts, HEATING_CODES, changedChipStates } from './BuildingFacts.jsx'
import { FeatureCapture, featureLabel } from './FeatureCapture.jsx'
import { PropertyModalNav, PropertyModalProgress } from './PropertyModalNav.jsx'
import { PasteBatchHistory } from './PasteBatchPanel.jsx'
import { formSnapshotForBatch, provenanceByField, formPatchFromAccepted } from '../../lib/pasteBatch.js'
import { captureFeatureGroups, changedFeatureStates, featureAnswersFromRows, answerModeOf, MAX_CHIP_ANSWERS,
  featureRetractCodes, saveWarningsFrom, landAreaToSqm, sqmToLandUnit, yearBuiltValid, disagreeingFeatureCodes, failedFeatureCodes } from '../../lib/featureCapture.js'
import { RoadMarkPicker } from './RoadMarkPicker.jsx'
import { RunningNote } from './RunningNote.jsx'
import { readPhotoMeta } from '../../lib/exifRead.js'
import { ListingGallery, splitCityFromAddress } from './ListingGallery.jsx'
import { CooperationChips, coopIntentOf, coopMeetingSeed } from './CooperationChips.jsx'
import { CooperationModal } from './CooperationModal.jsx'
import { AdDescriptionCard, PlatformPill, adDescriptionOf } from './HistoryCards.jsx'
import { TimelineBody, usePropertyEvents } from './PropertyTimeline.jsx'
import { BetterToKnow, propertyGaps, STANCE_CYCLE, STANCE_ICON, STANCE_STYLE, StanceChip, stanceOptions, TIER_STYLE } from './MatchSuggestions.jsx'
import { moveInPayment } from '../../lib/tenancyTerms.js'
import { PREPAY_MONTHS, monthLabel, normLang } from '../../lib/prepaySentence.js'
import { downscalePhoto, estimateTotal, photoSizeSpec, DEFAULT_PHOTO_SIZE, PHOTO_SIZES, isHeic, heicToJpeg } from '../../lib/imageDownscale.js'

const PHOTO_SIZE_LS = 'sntv.desk.photoSize'
const fmtMB = b => b >= 1048576 ? (b / 1048576).toFixed(1) + ' MB' : Math.max(1, Math.round(b / 1024)) + ' KB'

// Upload size, asked the way a mail client asks about an attachment (owner request 2026-08-19,
// alongside the 413 fix — a proxy that refuses big photos and a UI that never mentions size are
// the same problem seen from two ends).
//
// Exported and standalone so it can be RENDERED in the harness: the modal around it is behind
// the Desk's login, and the two things most likely to be wrong here — a Georgian label that
// overflows its button, and an estimate that reads "≈ 0 KB" while a slow encode is still
// running — are invisible to `vite build` and to every unit test.
// ⚠️ Every parameterised string below goes through tLog, NOT t. `t(key)` takes one argument
// and ignores a second entirely, so `t('..._est', {size})` renders the literal "≈ {size}".
// It shipped that way for one commit here and no automated check objected: `vite build` is
// happy, and i18nKeys.test asserts only that a key EXISTS in both locales. Opening the
// harness is what found it.
export function PhotoSizePicker({ value, onPick, est }) {
  return (
    <div className="flex flex-wrap items-center gap-2 mt-2">
      <span className="text-xs text-muted-foreground">{t('desk_property_photo_size')}</span>
      <div className="flex rounded-full border border-border overflow-hidden" role="group" aria-label={t('desk_property_photo_size')}>
        {PHOTO_SIZES.map(s => (
          <button key={s.key} type="button"
            onClick={() => onPick(s.key)}
            title={s.edge ? tLog('desk_property_photo_size_px', { px: s.edge }) : t('desk_property_photo_size_original_hint')}
            aria-pressed={value === s.key}
            className={`px-3 py-1 text-xs whitespace-nowrap transition-colors ${value === s.key
              ? 'bg-foreground text-background' : 'text-muted-foreground hover:bg-muted/40'}`}>
            {t(`desk_property_photo_size_${s.key}`)}
          </button>
        ))}
      </div>
      {/* Absent, never zero, until the encode pass has actually measured something —
          a number that ticks up from 0 reads as a broken upload, which is the exact
          class of confusion this whole change exists to remove. */}
      {est && (
        <span className="text-[11px] text-muted-foreground">
          {est.bytes < est.original
            ? tLog('desk_property_photo_size_est_saved', { size: fmtMB(est.bytes), was: fmtMB(est.original) })
            : tLog('desk_property_photo_size_est', { size: fmtMB(est.bytes) })}
        </span>
      )}
    </div>
  )
}
import { APT_CLASS, FeatureDefPanel, FillLinkSection, InfoDot, PROJECT_TYPES, PROPERTY_CATEGORY_KEYS, PROP_STATE, PROP_TYPE_TAGS, featureDefOf, isNationalityFeature } from './ManualClientModal.jsx'
import BusinessSection from '../BusinessSection.jsx'
import RestrictionRules, { RestrictionRulesSummary } from './RestrictionRules.jsx'
// cityKey / districtInCity / PROPERTY_KIND_OPTIONS now live in the pure feed module so
// `node --test` can run the feed pipeline that uses them. Re-exported from this file's
// bottom export block exactly as before — no import site changed.
import { cityKey, districtInCity, PROPERTY_KIND_OPTIONS } from '../../lib/deskFeed.js'
import AddressBlock from '../AddressBlock.jsx'
import { DEFAULT_CITY } from '../../lib/districtModel.js'
export { cityKey, districtInCity }
import { getDistricts,
  addPropertyNote, amendPropertyPromises, getPropertyPromises, decideAssumption, getPropertyAssumptions, addressDistrictForPoint, addressForPoint, addressHouse, addressStreets, geoExtent,
  amendListingTerms, deletePropertyNote, dismissProposal,
  getDormancy, getFeatures, getKindFeatureCapability, getListingLineage, reviewPasteBatch, getPasteBatches, decidePasteBatch, undoPasteBatch, getListingTerms, getListingTermsBatch,
  getMyPropertyVersion, getPropertyContacts, getPropertyDuplicates, getPublicStatus,
  publishPropertyPublic, unpublishPropertyPublic,
  listCooperations, listListingImages, listListingPolicies, parseListingText,
  recordPropertyVerdict, removeListingImage, reorderListingImages, saveListingEntry,
  setListingImageKind, setListingPolicy, setPropertyAvailability, setPropertyInventory, setRestrictionsClear,
  uploadListingImage, getIntakeDefinition,
} from '../../db/core.js'
import { deskFieldRules, deskConditionRows, conditionAfterBuilding, classAfterCondition, conditionRowOf, placeHasMetro, CONDITION_CODES, CONDITION_ROW_KEYS, deskAparthotelRows, APARTHOTEL_FACT_KEYS } from '../../lib/intakeDesk.js'
import { DateInput } from '../SegmentedInput.jsx'
import { supabase } from '../../db/index.js'

// ─── Property location — single pin. Simpler than PolygonMapPicker: no Draw/Pan toggle
// needed because Leaflet only fires 'click' on an actual click, never after a drag-pan, so
// panning to find the spot just works with a plain click listener. The marker itself is
// draggable for a quick nudge without re-clicking. value/onChange carry {lat,lng} | null —
// sent as property.lat/property.lng (see core/LISTING_OBSERVATION_CONTRACT.md, "property
// identity signals": lat/lng sit alongside address/district_code/cadastral_code).
function PointMapPicker({ lat, lng, onChange, focus = null }) {
  const mapEl = useRef(null), mapRef = useRef(null), markerRef = useRef(null)

  useEffect(() => {
    if (mapRef.current || !mapEl.current) return
    const map = L.map(mapEl.current).setView([lat ?? 41.715, lng ?? 44.79], lat != null ? 15 : 12)
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19, attribution: '© OpenStreetMap' }).addTo(map)
    map.on('click', e => {
      const p = e.latlng
      if (markerRef.current) markerRef.current.setLatLng(p)
      else markerRef.current = makeMarker(map, p.lat, p.lng)
      onChange({ lat: p.lat, lng: p.lng })
    })
    mapRef.current = map
    return () => { map.remove(); mapRef.current = null }
  }, [])

  function makeMarker(map, la, ln) {
    const m = L.marker([la, ln], { draggable: true }).addTo(map)
    m.on('dragend', () => { const p = m.getLatLng(); onChange({ lat: p.lat, lng: p.lng }) })
    return m
  }

  // Reflect a pin arriving from outside a direct map click (AI paste-parse landing lat/lng,
  // or switching between drafts) — placed/moved without re-adding a duplicate marker.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    if (lat == null || lng == null) { markerRef.current?.remove(); markerRef.current = null; return }
    if (markerRef.current) {
      markerRef.current.setLatLng([lat, lng])
      // an externally-set pin (address suggestion picked) can land outside the current view —
      // recenter, but only then, so a drag within the visible map never fights the user
      if (!map.getBounds().contains([lat, lng])) map.setView([lat, lng], 15)
    }
    else { markerRef.current = makeMarker(map, lat, lng); map.setView([lat, lng], 15) }
  }, [lat, lng])

  // The chosen city / district moves the VIEW there (owner report 2026-09-17: choosing Batumi left the map on
  // Tbilisi). A pin already inside the chosen area keeps the view where it is; nothing here ever places or
  // moves a pin — a pin outside the area is the district-disagreement card's question, not the map's.
  const focusKey = focus ? [focus.south, focus.west, focus.north, focus.east].join(',') : ''
  useEffect(() => {
    const map = mapRef.current
    if (!map || !focus) return
    const bounds = L.latLngBounds([focus.south, focus.west], [focus.north, focus.east])
    if (lat != null && lng != null && bounds.contains([lat, lng])) return
    map.fitBounds(bounds, { maxZoom: 15, padding: [8, 8] })
  }, [focusKey])   // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <div ref={mapEl} className="isolate h-40 rounded-lg overflow-hidden border border-border" />
      <div className="flex items-center gap-2 mt-2">
        <button type="button" className={btn('ghost')} disabled={lat == null} onClick={() => onChange(null)}>{t('desk_map_clear_pin')}</button>
        <span className="text-xs text-muted-foreground">
          {lat != null ? tLog('desk_map_pin_coords', { lat: lat.toFixed(5), lng: lng.toFixed(5) }) : t('desk_map_pin_hint_empty')}
        </span>
      </div>
    </div>
  )
}

// ─── Manual property entry — OBSERVATION CAPTURE, not "filling a database" ─────
// core/LISTING_OBSERVATION_CONTRACT.md: this form is just another producer of
// observations. Typed entry: pressing Save IS the review (no proposal). Opened
// from an AI draft (PropertyDrafts below): the human corrects, Save = accept.
// Both go through the ONE supply path server-side (prepare → ingestListing).
// Confidence is never asked for — the "Observed from" choice (provenance) drives
// it via source_trust. Unknown = leave a field empty (omitted, never a 0).
// PROPERTY_KIND_OPTIONS: definition moved to ../../lib/deskFeed.js (imported above).
// building_material values: the exact strings the MyHome scraper stores (scrape_myhome.js MATERIAL_MAP),
// so a manual answer and a scraped one compare equal. A stored value outside this list is still shown.
// The 22 stations MyHome declares (scrape_myhome.js METRO_MAP) plus გოცირიძე — the exact strings
// metro_station claims already hold, so a manual answer and a scraped one compare equal.
const METRO_STATIONS = ['ახმეტელის თეატრი', 'სარაჯიშვილი', 'გურამიშვილი', 'ღრმაღელე', 'დიდუბე', 'გოცირიძე', 'ნაძალადევი',
  'სადგურის მოედანი', 'მარჯანიშვილი', 'რუსთაველი', 'თავისუფლების მოედანი', 'ავლაბარი', '300 არაგველი', 'ისანი', 'სამგორი',
  'ვარკეთილი', 'ელექტროდეპო', 'წერეთელი', 'ტექნიკური უნივერსიტეტი', 'სამედიცინო უნივერსიტეტი', 'დელისი', 'ვაჟა-ფშაველა',
  'სახელმწიფო უნივერსიტეტი']

const BUILDING_MATERIALS = ['ბლოკი', 'აგური', 'რკინა-ბეტონი', 'კომბინირებული', 'ხის მასალა', 'ხე', 'პლასტმასა', 'ალუმინი']

// review 2026-09-23: a stored composed address ("ჭავჭავაძის გამზ. 37") → { street, number } for
// AddressBlock. A trailing house-number token (37, 37ა, 12/4, №5) is the number; otherwise the
// whole text is the street and the number stays empty.
export function splitStreetNumber(address) {
  const s = String(address || '').trim()
  if (!s) return null
  const m = s.match(/^(.*\S)[\s,]+(?:№\s*)?(\d+\s*\p{L}?(?:\s*[/-]\s*\d+\s*\p{L}?)?)$/u)
  return m ? { street: m[1].replace(/[,\s]+$/, ''), number: m[2].replace(/\s+/g, '') } : { street: s, number: '' }
}

const LISTING_INTENT_OPTIONS = [
  { value: 'sale',       get label() { return t('desk_listing_intent_sale') } },
  { value: 'rent',       get label() { return t('desk_listing_intent_rent') } },
  { value: 'daily_rent', get label() { return t('desk_listing_intent_daily') } },
  // 0219. girao is offerable by hand because agents take these mandates directly, not
  // only via a portal ad. ijara is deliberately ABSENT: we label ijara ads that arrive
  // from a portal, but there is no reason to let an agent create one until we broker
  // them — an option nobody should pick is a mis-click waiting to happen.
  { value: 'girao',      get label() { return t('desk_listing_intent_girao') } },
]
const OBSERVED_FROM_OPTIONS = [
  { value: 'owner',    get label() { return t('desk_observed_from_owner') } },
  { value: 'agent',    get label() { return t('desk_observed_from_agent') } },
  { value: 'registry', get label() { return t('desk_observed_from_registry') } },
  { value: 'admin',    get label() { return t('desk_observed_from_admin') } },
  { value: 'ai',       get label() { return t('desk_observed_from_ai') } },
]

// ─── District <option>s grouped raion → ubnebi (core migration 0053 hierarchy) ──
// Raions render as <optgroup> with a "whole raion" option first, then their ubnebi;
// parentless non-raion codes (TBS_OLD cross-raion zone, PERI_*/MUN_* periphery) go
// under "Other". Falls back to the old flat list when parent_code is missing from
// the API (older server build) — the dropdown must never break on a stale field set.
// `prefer` keeps each call site's existing display language (panel=en, modal=ka).
// `city` (optional) scopes the list to one market city. The city filter's values are
// GEORGIAN address prefixes (splitCityFromAddress: "თბილისი, ..." → "თბილისი") while
// district.city is English — hence districtInCity matches Tbilisi explicitly and other
// cities by the row's own name_ka/name_en/city. 'all'/absent city = no scoping.
// Canonical key for a CITY label. Address prefixes and district names disagree in form
// ("ოზურგეთი" vs "ოზურგეთის მუნიციპალიტეტი" vs genitive "მცხეთის…" vs "მცხეთა") — strip
// the municipality suffix, stem what's left. Two labels with the same key ARE one city.
// cityKey: definition moved to ../../lib/deskFeed.js (imported above).
// districtInCity: definition moved to ../../lib/deskFeed.js (imported above).
// Roll the raw per-code OWN counts (0056) up the district hierarchy into what each dropdown
// option would actually surface — mirroring 0055's pass/unknown verdict so "grayed" means the
// exact same "nothing reachable" the search itself would find:
//   exactOf(code)     = own + children's own          — confirmed matches (a raion pulls its ubnebi)
//   reachableOf(code) = exactOf + parent's own coarse — plus the can't-rule-out coarse-coded rows
// An option grays when reachableOf == 0 (not even a possible match); the number shown is exactOf
// (confirmed only), so an ENABLED option never displays a misleading "(0)" for parent-coarse hits.
// ⚠️ `expanded` (B273 move 4b, 0370): the SEARCH now emits counts that already include a code's
// children, because summing them here double-counts. A disputed property counts under BOTH its
// candidate districts (Ruling B), so a cluster filed under Vake *and* under a Vake neighbourhood
// was added twice by the sum below while the click counted it once — Vake read 1,296 against a
// click of 1,292. That summation was correct for 0056, where a listing sat at exactly one code;
// it stopped being correct the moment a cluster could sit at two, and no client-side fix exists
// (the overlap is not derivable from per-code totals — it needs the rows).
// So: expanded counts are read straight through. The un-expanded path stays for 0056's numbers,
// which are still what the Properties bare page and the first paint have.
function districtReach(districts, counts, { expanded = false } = {}) {
  const own = code => counts[code] || 0
  const parentOf = new Map(), childrenOf = new Map()
  for (const d of districts) {
    parentOf.set(d.code, d.parent_code || null)
    if (d.parent_code) {
      if (!childrenOf.has(d.parent_code)) childrenOf.set(d.parent_code, [])
      childrenOf.get(d.parent_code).push(d.code)
    }
  }
  // ⚠️ ALL DESCENDANTS, not just direct children (B792, 2026-08-17). The un-expanded branch below
  // summed ONE level while the district tree is three deep, so a raion's chip silently omitted
  // every grandchild — ნაძალადევი was short 864, ისანი 717, სამგორი 648. That branch is live
  // whenever an agent filter is on (`usingHonestCounts` false), which is why it was never noticed
  // on the default view. Cycle-guarded: a bad parent_code must yield a short list, never a hang.
  const descendantsOf = code => {
    const out = [], seen = new Set([code])
    const stack = [...(childrenOf.get(code) || [])]
    while (stack.length) {
      const c = stack.pop()
      if (seen.has(c)) continue
      seen.add(c); out.push(c)
      for (const g of childrenOf.get(c) || []) stack.push(g)
    }
    return out
  }
  const exactOf = new Map(), reachableOf = new Map()
  for (const d of districts) {
    // expanded: the server already counted DISTINCT clusters across the code and its descendants —
    // exactly the membership test the filter applies (0370, transitive since 0625). Summing again
    // would re-introduce the double count this parameter exists to remove.
    const exact = expanded
      ? own(d.code)
      : own(d.code) + descendantsOf(d.code).reduce((s, c) => s + own(c), 0)
    const p = parentOf.get(d.code)
    exactOf.set(d.code, exact)
    // Gray-out fuel only — a district greys when this is 0. The parent term keeps a child
    // visible when only the parent carries coarse-coded rows that MIGHT be in it. Under
    // `expanded` the parent's number already contains this code, so the sum overstates; that is
    // harmless here because nothing reads the magnitude, only whether it is zero, and
    // overstating can only keep an option enabled — never hide one.
    reachableOf.set(d.code, exact + (p ? own(p) : 0))
  }
  return { exactOf, reachableOf }
}
// `exactOf`/`reachableOf` (from districtReach) opt into count display + gray-out; when absent
// (e.g. the Manual Property modal, where adding the first listing of an empty district is the
// whole point) the options render exactly as before — no numbers, nothing disabled.
function DistrictOptions({ districts: allDistricts, prefer = 'en', city = null, exactOf = null, reachableOf = null }) {
  const districts = allDistricts.filter(d => districtInCity(d, city, allDistricts))
  const label = d => (prefer === 'ka' ? (d.name_ka || d.name_en) : (d.name_en || d.name_ka)) || d.code
  const byLabel = (a, b) => label(a).localeCompare(label(b))
  const graying = !!(exactOf && reachableOf)
  // One option, decorated with its confirmed count and disabled when provably vain.
  const opt = (code, text) => {
    if (!graying) return <option key={code} value={code}>{text}</option>
    const n = exactOf.get(code) || 0
    return <option key={code} value={code} disabled={(reachableOf.get(code) || 0) === 0}>{text}{n > 0 ? ` (${n})` : ''}</option>
  }
  if (!districts.some(d => d.parent_code)) {
    return districts.map(d => opt(d.code, label(d)))
  }
  const childrenOf = code => districts.filter(d => d.parent_code === code).sort(byLabel)
  const roots = districts.filter(d => !d.parent_code)
  const raions = roots.filter(d => childrenOf(d.code).length).sort(byLabel)
  // Parentless leftovers split by code prefix, not lumped into one "Other":
  // TBS_* = cross-raion Tbilisi zones (TBS_OLD), PERI_* = the suburb/village belt
  // around Tbilisi (villages the market sells as "Tbilisi" — round2-villages.json),
  // MUN_* = genuinely separate cities/municipalities.
  const loners = roots.filter(d => !childrenOf(d.code).length)
  const buckets = [
    { key: 'zones', members: loners.filter(d => d.code.startsWith('TBS_')).sort(byLabel), en: 'Tbilisi zones', ka: 'თბილისის ზონები' },
    { key: 'belt', members: loners.filter(d => d.code.startsWith('PERI_')).sort(byLabel), en: 'Tbilisi suburbs', ka: 'თბილისის შემოგარენი' },
    { key: 'cities', members: loners.filter(d => !d.code.startsWith('TBS_') && !d.code.startsWith('PERI_')).sort(byLabel), en: 'Other cities', ka: 'სხვა ქალაქები' },
  ]
  return (
    <>
      {raions.map(r => (
        <optgroup key={r.code} label={label(r)}>
          {opt(r.code, `${label(r)} — ${prefer === 'ka' ? 'მთელი რაიონი' : 'whole raion'}`)}
          {childrenOf(r.code).map(c => opt(c.code, label(c)))}
        </optgroup>
      ))}
      {buckets.map(b => b.members.length > 0 && (
        <optgroup key={b.key} label={prefer === 'ka' ? b.ka : b.en}>
          {b.members.map(d => opt(d.code, label(d)))}
        </optgroup>
      ))}
    </>
  )
}

// ─── Facet — one collapsible lens of the property intake workspace ─────────────
// A facet is a VIEW over accumulated observations, not a container of truth
// (core/BACKLOG.md, 2026-07-06 origin note) — collapsing changes nothing about
// what is recorded. `locked` facets stay visible with the reason they're not
// usable yet, so the agent always sees the full shape of what CAN be captured.
function Facet({ title, hint, summary, locked, lockedNote, defaultOpen = true, children, num = null, labelKey = null, badge = null, summaryTone = null }) {
  // labelKey (redesign stage 2): a short section name shown in the UI language with the other language
  // beside it (the mockup's "01 · Property  ობიექტი"); the long `title` then reads as the line under it.
  const lang = getLanguage()
  const alt = labelKey ? tIn(lang === 'ka' ? 'en' : 'ka', labelKey) : ''
  const [open, setOpen] = useState(defaultOpen)
  const expanded = open && !locked
  return (
    <div className={`${card} ${locked ? 'opacity-70' : ''}`}>
      <button type="button" className="w-full flex items-center justify-between gap-2 text-left"
        onClick={() => !locked && setOpen(o => !o)}>
        <span className="min-w-0 text-xs font-semibold text-muted-foreground">
          {num && <span className="tabular-nums">{num} · </span>}
          {labelKey ? <><span className="text-foreground">{t(labelKey)}</span>{alt && alt !== t(labelKey) && <span className="ml-2 font-normal">{alt}</span>}</> : title}
          {badge}
          {labelKey && open && !locked && <span className="block font-normal text-[11px] mt-0.5">{title}</span>}
        </span>
        <span className="flex items-center gap-2 min-w-0 shrink-0">
          {!expanded && !locked && summary && (summaryTone === 'warn'
            ? <span className="rounded-full border border-amber-500/50 bg-amber-500/10 px-2 text-[11px] leading-5 text-amber-700 dark:text-amber-300 truncate max-w-[14rem]" data-summary-warn>{summary}</span>
            : <span className="text-xs text-muted-foreground truncate max-w-[14rem]">{summary}</span>)}
          {locked
            ? <span className="text-xs text-muted-foreground"><Icon name="lock" /> {lockedNote}</span>
            : <span className="text-xs text-muted-foreground">{open ? '▾' : '▸'}</span>}
        </span>
      </button>
      {expanded && (
        <div className="mt-2">
          {hint && <p className="text-xs text-muted-foreground mb-2">{hint}</p>}
          {children}
        </div>
      )}
    </div>
  )
}

// ─── Rental Restrictions facet — listing_terms through the 0055 evented gateway ──
// Loads the current row (the working interpretation), saves a DIFF as amend ops
// (never a blind PATCH). Every field is tri-state/nullable: '' = not stated by the
// owner, which must stay distinct from "no". tenant_pref is owner-asserted text
// with provenance — agent-visibility only, NEVER a matching filter (0010 header).
// furnished + registered_lease removed from this facet (2026-07-11): furnished is a
// PROPERTY fact (belongs with the facts/class section), and NAPR registration is a
// deal-terms detail — neither is "who does the owner accept". Columns stay in
// listing_terms; only the UI stopped asking here.
// pets/smoking/children were tri-state dropdowns here; removed 2026-07-11 as duplication
// of the pol_animals/pol_smoker/pol_children stance chips. The boolean columns stay in
// listing_terms (scrapes may set them) but the modal no longer edits them — so this list
// is empty and the record loop over it is inert (kept for shape, not behavior).
// ⚠️ EMPTY FOR A REASON, and pet_deposit_required is the ONE exception (0416, owner
// 2026-08-05). pets_allowed / smoking_allowed / children_allowed all LEFT this list when
// they became restriction chips and then rules — permissions belong to the restrictions
// panel, and re-adding one here would give the same fact two editors.
// A pet DEPOSIT is not a permission. It is money, which is why the stance vocabulary could
// never hold it (no axis, no predicate, and allowed/not_allowed answering a question about
// cost). It answers "does a pet cost extra here" — true/false/null(nobody asked) — while
// the single deposit box below still answers "how much in total".
// ⚠️ "the separate amount field stays retired per the 2026-07-27 ruling" stood here and is
// REVERSED (owner 2026-09-07): the amount box is back and is now the whole answer, with this
// boolean derived from it. Corrected in place rather than deleted — a stale note asserting a
// field does not exist, sitting beside that field, is the failure this repo pays for most.
// ⚠️ NO LONGER A PILL (owner 2026-09-07). pet_deposit_required is still diffed and saved from
// this list, but it is DERIVED from the amount box now — see nextPetFee. The pill that used to
// render here said the same sentence in all three states, one of which meant the opposite.
const TERMS_TRI = [['pet_deposit_required', 'desk_terms_pet_deposit_required']]

// The pet-fee rule lives in lib/petFee.js — pure, tested, and importable by node --test.
// deposit is NOT here: it is an amount + currency pair (core 0212), rendered below
// like the bank appraisal. A deposit is a fixed sum held for the whole tenancy, not
// a multiple of rent — `deposit_months` survives only for legacy rows and the
// scorer's max_deposit_months check, and nothing writes it any more.
// ⚠️ TERMS_NUM is the SAVE-DIFF field list now, not a render list (owner ruling
// 2026-07-27: min lease renders as a quick-select, pet_deposit_extra stopped being asked
// — one deposit box is enough). pet_deposit_extra stays HERE so a legacy value loaded
// into the form still diffs/clears correctly; it just has no input any more.
const TERMS_NUM = [['min_lease_months', 'desk_terms_min_lease'],
  ['max_occupants', 'desk_terms_max_occupants'], ['pet_deposit_extra', 'desk_terms_extra_pet_deposit'],
  // 1083 - MONTHS, not a percent (the input converts; see the deposit control). In the
  // numeric diff list so a share round-trips and CLEARS like every other number here.
  ['deposit_months', 'desk_terms_deposit_short']]
// SALE CONDITIONS (0080) — the seller-side mirror of the rental restrictions,
// validated against Tbilisi market practice (cash-only is the #1 restriction; the
// "tax game" and furniture bait-and-switch are the classic late deal-killers).
// Selects render option labels via `desk_sale_opt_<value>` i18n keys.
const SALE_SELECTS = [
  ['occupancy',          'desk_sale_occupancy',  ['vacant', 'owner', 'tenant']],
  ['furniture_included', 'desk_sale_furniture',  ['none', 'partial', 'as_seen']],
  ['parking_in_price',   'desk_sale_parking',    ['included', 'separate', 'none']],
  ['encumbrance',        'desk_sale_encumbrance', ['clean', 'bank_mortgage']],
]
const SALE_TRI = [['cash_only', 'desk_sale_cash_only'], ['full_price_declaration', 'desk_sale_full_price']]
// B301 step 2 (core 0400/0486) — "does this row say cash only?", answered from
// `financing_accepted` (the enumeration that supersedes `cash_only`) and falling back to
// the deprecated boolean while that column still exists; step 3 drops it. 'mortgage' in
// the set is the discriminator: the owner will take a financed buyer, which is precisely
// what cash_only=false meant.
// ⚠ ONE definition because THREE sites ask — the form's tri-state and both summary
// strips. Deriving it separately in each is how two of them would quietly keep reading
// the legacy column and stop agreeing with the third.
// Returns true | false | null(nobody said) — never a bare boolean, so unknown stays
// distinguishable from "no", which is the whole four-state philosophy here.
// ⚠ KNOWN GAP, stated rather than hidden: an EMPTY array reads as null here, but 0486
// deliberately stores [] as [] because "the owner accepts no financing at all" is a
// different answer from "nobody asked". This control cannot express that third state —
// it has three slots and they are taken — and mapping [] onto "cash only" would be a
// lie (accepts nothing ≠ accepts full payment). This UI cannot PRODUCE [], so the gap
// only shows on a row some other writer made; closing it needs a real control, not a
// cleverer read.
const cashOnlyOf = r => {
  const fin = r?.financing_accepted
  if (Array.isArray(fin) && fin.length) return !fin.includes('mortgage')
  return r?.cash_only == null ? null : !!r.cash_only
}
// MOVE-IN ADVANCE (core 0213) — what the tenant must produce before receiving keys,
// alongside the deposit above. The gallery deal panel has asked for these two ticks for
// months; until 0213 they were written nowhere at all.
// ⚠️ TRI-STATE HERE, CHECKBOXES THERE — deliberate, not an inconsistency. In the gallery
// the panel is on screen for the whole capture, so an unticked box honestly means "not
// required". This modal gets opened for a dozen unrelated reasons, and a checkbox
// defaulting to unchecked would silently record "last month not required" on every
// listing an agent ever glanced at — thousands of fabricated observations. Unknown has
// to stay sayable, so it is the default option.
// 1211 (owner 2026-09-16): the first-month / last-month ticks became the PREPAYMENT LIST below —
// the contract composer's rows, where a month can be paid in part. The two booleans are still
// stored, derived from the list by core, so nothing that reads them changes.
const RENT_TRI = [
  // 1083 - same tri-state grammar: on = required, off = unknown, never a fabricated "no".
  ['deposit_required', 'desk_terms_deposit_required']]

// ── PREPAYMENT MONTHS (1211, owner 2026-09-16: "select month and then part of it") ───────────
// The contract composer's own row grammar (ContractComposer PrepayListInput, prepaySentence.js):
// which month · paid in full, or a PART of it. A listing has no amounts yet, so a part is a share
// of the month (¼ ½ ¾); a contract drawn from this listing turns it into paid = part × rent.
// No rows = nothing recorded — never a fabricated "no prepayment".
const PREPAY_PARTS = [0.25, 0.5, 0.75]
const PART_LABEL = { 0.25: '¼', 0.5: '½', 0.75: '¾' }
function PrepayMonths({ list, set }) {
  const rows = Array.isArray(list) ? list : []
  const lang = normLang(getLanguage())
  const patch = (i, p) => set(rows.map((r, j) => (j === i ? { ...r, ...p } : r)))
  const next = () => {
    const used = new Set(rows.map(r => String(r.month)))
    if (used.has('1') && !used.has('last')) return 'last'
    return PREPAY_MONTHS.find(m => !used.has(m)) || '1'
  }
  const total = rows.reduce((s, r) => s + (r.full === false ? Number(r.part) || 0 : 1), 0)
  return (
    <div className="w-full space-y-1" data-testid="terms-prepay">
      <div className="text-xs text-muted-foreground">{t('desk_terms_prepay_months')}{rows.length ? ` · ${total}` : ''}</div>
      {rows.map((r, i) => {
        const full = r.full !== false
        return (
          <div key={i} className="flex items-center gap-1.5 flex-wrap" data-prepay-row={i}>
            <select className={`${input} w-auto`} value={String(r.month)} onChange={e => patch(i, { month: e.target.value })}>
              {PREPAY_MONTHS.map(m => <option key={m} value={m}>{monthLabel(m, lang)}</option>)}
            </select>
            <label className="flex items-center gap-1 text-xs">
              <input type="checkbox" checked={full}
                onChange={e => set(rows.map((x, j) => (j === i ? (e.target.checked ? { month: x.month, full: true } : { month: x.month, full: false, part: 0.5 }) : x)))} />
              {t('desk_contract_prepay_full')}
            </label>
            {!full && (
              <label className="flex items-center gap-1 text-xs text-muted-foreground">{t('desk_terms_prepay_part')}
                <select className={`${input} w-auto`} value={String(r.part)} onChange={e => patch(i, { part: Number(e.target.value) })}>
                  {PREPAY_PARTS.map(p => <option key={p} value={String(p)}>{PART_LABEL[p]}</option>)}
                </select>
              </label>
            )}
            <button type="button" className="text-muted-foreground hover:text-red-600 px-1" aria-label={t('desk_contract_prepay_remove')}
              title={t('desk_contract_prepay_remove')} onClick={() => set(rows.filter((_, j) => j !== i))}>×</button>
          </div>
        )
      })}
      <button type="button" className="inline-flex items-center rounded-full border border-dashed border-border px-2.5 py-0.5 text-[11px] text-muted-foreground hover:border-primary/50"
        onClick={() => set([...rows, { month: next(), full: true }])}>{t('desk_contract_prepay_add')}</button>
    </div>
  )
}
// GIRAO (core 0219) — გირავდება is a third deal shape, not a rent with a large deposit
// and not a cheap sale. One sum changes hands at the start and comes back at the end;
// there is no monthly payment, so none of the rental money fields apply.
// NOTE there is deliberately no GIRAO_NUM list next to TERMS_NUM/SALE_TRI: the four
// girao fields render inline in the girao branch and are diffed explicitly in
// handleRecord (amount+currency as a unit, like the deposit). A constant that neither
// drives rendering nor the save diff would just be a lie waiting to be trusted.

// ── Which shape should the terms editor open in? ─────────────────────────────
// THE AD IS NOT THE DEAL (bug found 2026-07-27, Saburtalo/Mariam). A property can be
// advertised one way and worked another: the owner posted a გირავდება ad, our agent
// reshared it and opened a RENT cooperation against it. The panel used to take its
// shape from `listing.intent`, so it opened the sale editor — which has no field for a
// deposit or a minimum lease — and the rent terms she had actually recorded were
// invisible on it. Her stance chips showed; her numbers had nowhere to render.
//
// Order of authority, strongest first:
//   1. an ACTIVE cooperation's transaction_intent — the agency's OWN engagement on this
//      property is the most direct statement of what is being worked, and it is a
//      deliberate human act, unlike the ad's label.
//   2. the listing's own intent — right whenever nobody has said otherwise.
// A listing intent with no editor shape of its own (ijara today) falls back to rent:
// its terms are lease-shaped, and a blank rent panel beats a wrong sale one.
//
// This is only the OPENING shape. RentalTermsModal itself detects data recorded in any
// other shape and offers a switch, so a pairing neither of us predicted still can't
// hide someone's work — see `otherShapes` there.
const TERMS_MODE_BY_INTENT = { sale: 'sale', girao: 'girao', rent: 'rent', daily_rent: 'rent', ijara: 'rent' }
export function termsModeFor(listingIntent, coopIntent = null) {
  return TERMS_MODE_BY_INTENT[coopIntent] || TERMS_MODE_BY_INTENT[listingIntent] || 'rent'
}
// The fields that PROVE someone recorded in a given shape. Availability is deliberately
// absent: "when can possession begin" is intent-agnostic (0220) and lives on the PROPERTY,
// so it discriminates nothing about the shape of a listing's terms.
const SHAPE_FIELDS = {
  rent: ['deposit_amount', 'min_lease_months', 'max_occupants', 'prepay_first_month', 'prepay_last_month', 'deposit_months', 'prepayments',
         // 1083 — a row whose only recorded fact is "a deposit is demanded, amount not
         // discussed" IS a recorded rental shape (278 adverts say exactly that). Without
         // these two it reads as a blank form and the panel offers the add-door instead.
         'deposit_required', 'deposit_basis'],
  // B301 step 2: `financing_accepted` sits beside `cash_only` here, not instead of it —
  // this list answers "does this row carry sale-shaped facts", and after step 2 a new
  // row carries only the enumeration while old rows carry only the boolean.
  sale: ['cash_only', 'financing_accepted', 'occupancy', 'furniture_included', 'parking_in_price', 'encumbrance', 'bank_appraisal_value', 'bank_appraisal_status', 'full_price_declaration'],
  girao: ['girao_amount', 'girao_period_years', 'girao_owner_resides'],
}
// STANCE_CYCLE / STANCE_STYLE / STANCE_ICON — moved to ./desk/MatchSuggestions.jsx (plan §3 decomposition).
// VIEWING_STATUS_STYLE — moved to ./desk/CooperationChips.jsx (plan §3 decomposition).
// At-a-glance state next to the 🏠＋ chip — the old inline Facet showed a summary for
// free; converting to a click-to-open modal (2026-07-11) lost that, so this renders the
// ACTUAL selected values (like COOPERATION shows "Rent · 1,450 GEL"): the recorded
// stances as mini colored chips + any numeric terms. refreshKey forces a refetch after
// the modal records a change.
function RentalTermsSummary({ listingId, refreshKey }) {
  const [s, setS] = useState('loading')   // { stances:[{code,name,stance}], nums:[str] } | null | 'loading'
  useEffect(() => {
    let gone = false
    setS('loading')
    Promise.all([
      getListingTerms(listingId),
      listListingPolicies(listingId).catch(() => ({ policies: [] })),
      getFeatures().catch(() => []),
    ]).then(([d, pol, feats]) => {
      if (gone) return
      const nameOf = code => { const f = feats.find(x => x.code === code); return f ? (f.name_ka || f.name_en || code) : code }
      // agent-recorded stances only (scraped stances aren't the agent's own record)
      const stances = (pol.policies || []).filter(p => p.source_kind === 'agent')
        .map(p => ({ code: p.subject, name: nameOf(p.subject), stance: p.stance }))
      // numeric terms worth surfacing at a glance (listing_terms, 0055)
      // `tm`, not `t` — a local named t would shadow the imported i18n t() below
      const tm = d.terms || {}
      const nums = []
      // amount first (0212); months only for rows recorded before the change
      if (tm.deposit_amount != null) nums.push(tLog('desk_terms_summary_deposit_amount', { v: Number(tm.deposit_amount).toLocaleString(), c: tm.deposit_currency || '' }))
      else if (tm.deposit_months != null) nums.push(tLog('desk_terms_summary_deposit', { n: tm.deposit_months }))
      if (tm.min_lease_months != null) nums.push(tLog('desk_terms_summary_minlease', { n: tm.min_lease_months }))
      if (tm.max_occupants != null) nums.push(tLog('desk_terms_summary_maxocc', { n: tm.max_occupants }))
      // sale-shaped bits (0080) — shown whenever recorded
      if (cashOnlyOf(tm) === true) nums.push(t('desk_sale_cash_only_yes'))
      if (tm.occupancy) nums.push(t(`desk_sale_opt_${tm.occupancy}`))
      if (tm.encumbrance === 'bank_mortgage') nums.push(t('desk_sale_opt_bank_mortgage'))
      if (tm.bank_appraisal_value != null) nums.push(tLog('desk_sale_summary_appraisal', { v: Number(tm.bank_appraisal_value).toLocaleString(), c: tm.bank_appraisal_currency || '' }))
      else if (tm.bank_appraisal_status === 'appraised') nums.push(t('desk_sale_appraisal_opt_appraised'))
      else if (tm.bank_appraisal_status === 'none') nums.push(t('desk_sale_appraisal_opt_none'))
      if (!stances.length && !nums.length) { setS(null); return }
      setS({ stances, nums })
    }).catch(() => { if (!gone) setS(null) })
    return () => { gone = true }
  }, [listingId, refreshKey])
  if (s === 'loading' || !s) return null   // empty chip already reads as "nothing recorded"
  return (
    <div className="flex flex-wrap items-center gap-1">
      {s.stances.map(x => (
        <span key={x.code} className={pill('none', { extra: STANCE_STYLE[x.stance] })}>
          {STANCE_ICON[x.stance]} {x.name}
        </span>
      ))}
      {s.nums.length > 0 && <span className="text-xs text-muted-foreground">{s.nums.join(' · ')}</span>}
    </div>
  )
}

// ── Per-field knowledge maturity (2026-07-19, v1) — a READ-TIME projection over the
// per-agent observations already captured in listing_policy (one row per recorder × source
// × subject). It aggregates existing evidence and invents nothing (PHILOSOPHY: projections
// aggregate, never fabricate). Status by INDEPENDENT sources — a distinct recorder, the
// scrape ad, the owner, the registry each count once: ≥2 agreeing = confirmed, ≥2
// disagreeing = conflict, exactly 1 = single-source (a nudge to verify, not yet truth).
// Working assumption = freshest, then heaviest source. Deliberately v1-narrow: numeric
// listing_terms maturity (deposit, min-lease) is a LATER slice — those rows carry no
// per-recorder attribution yet, so "N sources" would be untrustworthy there; the scorer
// conflict→partial and the "needs-verification" queue are also later slices.
const MATURITY_SOURCE_WEIGHT = { registry: 4, owner: 3, agent: 2, scrape: 1, ai: 0 }
function fieldMaturity(allIds, byListing) {
  const ZERO = '00000000-0000-0000-0000-000000000000'
  const bySubject = new Map()   // subject → Map(sourceKey → {stance, kind, ts}); freshest kept per source
  for (const id of allIds || []) for (const p of (byListing?.[id]?.policies || [])) {
    const kind = p.source_kind ?? 'agent'
    // one source = a distinct recorder (agent) OR the ad / owner / registry as a whole
    const src = kind === 'agent'
      ? (p.recorded_by_agent_id && p.recorded_by_agent_id !== ZERO ? `a:${p.recorded_by_agent_id}` : `a:anon:${id}`)
      : kind
    if (!bySubject.has(p.subject)) bySubject.set(p.subject, new Map())
    const m = bySubject.get(p.subject)
    const ts = p.last_seen_at || null
    const prev = m.get(src)
    if (!prev || (ts && (!prev.ts || ts > prev.ts))) m.set(src, { stance: p.stance, kind, ts })
  }
  const out = []
  for (const [subject, m] of bySubject) {
    const srcs = [...m.values()]
    const stances = new Set(srcs.map(s => s.stance))
    const working = [...srcs].sort((a, b) =>
      String(b.ts || '').localeCompare(String(a.ts || '')) ||
      ((MATURITY_SOURCE_WEIGHT[b.kind] ?? 0) - (MATURITY_SOURCE_WEIGHT[a.kind] ?? 0)))[0]
    out.push({
      subject,
      status: stances.size > 1 ? 'conflict' : (srcs.length >= 2 ? 'confirmed' : 'single'),
      stance: working.stance,
      sources: srcs.length,
      freshest: srcs.map(s => s.ts).filter(Boolean).sort().pop() || null,
    })
  }
  return out
}

// Per-agent rental terms rows for the lineage modal (2026-07-11) — the same insight
// POSTED ON already renders: each agent's reshare is its OWN listing row (0041), so
// each agent may have recorded a different version of the owner's conditions. One row
// per agent: their recorded stances/terms merged across their postings, and a 🏠 button
// opening the editor on THEIR listing (with-data one preferred, else their first).
// The OWNER never gets a row (2026-07-11): these are conditions agents heard FROM the
// owner, so a row labeled with the owner's name (their source ad is a listing too) read
// as nonsense. The source ad's listing ids fold into the VIEWER's row instead — an agent
// with no repost of their own still needs somewhere to record what they heard, and the
// source ad's row is the only listing they share.
const OWNER_TERMS_ROW = 'owner-terms'   // sentinel row for AGENT-recorded stances (batch is source_kind='agent' only) with no
// rep-viewer row to fold into. Labeled "recorded by an agent" — NOT "from the source ad"
// (2026-07-15); genuine ad-STATED conditions (source_kind='scrape') have no producer yet.
// canRecord=false (2026-07-14): a NON-representative viewer reads recorded conditions but
// records nothing — "owner's stated conditions" are what the REP heard from the owner; a
// viewer with no owner relationship has nothing to record. No viewer bucket (the "Your
// postings 🏠＋" row was nonsense on another agent's card), no editor buttons, no empty rows.
function RentalTermsByAgent({ primaryListing, primaryIdFallback, siblings, origin = null, viewerName, refreshKey, onOpen, ownerName = null, canRecord = true }) {
  const [data, setData] = useState(null)   // { byListing: {id:{terms,policies[]}}, features } | null
  const [loading, setLoading] = useState(false)   // batch in flight — show a loading line, not a blank section
  // agent → their listing ids, POSTED-ON order (primary first)
  const groups = useMemo(() => {
    const m = new Map()
    const ownerIds = []   // listings authored by the owner (source ad) — no row of their own
    const isOwner = name => ownerName && name && name === ownerName
    // isAgent: server-shipped author_is_agent (siblings, 2026-07-13). A non-agent author is
    // an OWNER identity even when their name differs from the direct origin's (the family
    // spans same_as postings — the owner authors several), so they fold into the source-ad
    // bucket instead of getting their own bogus row in "owner's stated conditions".
    // undefined (older payloads / primary listing) falls back to the name check alone.
    const add = (name, id, isAgent) => {
      if (!id) return
      if (isOwner(name) || !name || isAgent === false) { if (!ownerIds.includes(id)) ownerIds.push(id); return }
      const k = name
      if (!m.has(k)) m.set(k, [])
      if (!m.get(k).includes(id)) m.get(k).push(id)
    }
    add(primaryListing?.author_name, primaryListing?.listing_id || primaryIdFallback, primaryListing?.author_is_agent)
    for (const s of siblings || []) add(s.author_name, s.id, s.author_is_agent)
    // ⚠ The ORIGIN is NOT in `siblings` (server excludes it since 2026-07-14 to stop the
    // POSTED-ON double-row) — but gallery-recorded stances and scrape/ai source stances
    // land on the ORIGIN listing (the gallery-save's core ingest resolves the source ad).
    // Omitting it silently hid every gallery-recorded restriction on reshare cards (Ani/
    // Tolstoi-13, 2026-07-21). Owner-authored → folds into ownerIds like any owner ad.
    add(origin?.author_name, origin?.listing_id, origin?.author_is_agent)
    if (ownerIds.length) {
      // These stances were recorded by an AGENT against the scraped source-ad listing
      // (author = owner) — the gallery restrictions panel syncs there, and the batch only
      // returns source_kind='agent' (2026-07-12/15). Fold them into the viewer's row when
      // they have one (their own postings first, so their recordings win the merge; the
      // source ad last as edit fallback) — that attributes them to the viewer by name.
      // When there's NO viewer row (an admin with no roster row viewing), they still must
      // be VISIBLE — dropping them hid a stance that genuinely landed in the DB (shota/
      // Pinar case). Give them their own row labeled "recorded by an agent" — NOT "from
      // the source ad": an agent typed them, they aren't the ad's own text (2026-07-15).
      const bucket = (canRecord && viewerName) || OWNER_TERMS_ROW
      if (!m.has(bucket)) m.set(bucket, [])
      for (const id of ownerIds) if (!m.get(bucket).includes(id)) m.get(bucket).push(id)
    }
    return m
  }, [primaryListing, primaryIdFallback, siblings, origin, ownerName, viewerName, canRecord])
  const allIds = useMemo(() => [...groups.values()].flat(), [groups])

  useEffect(() => {
    let gone = false
    if (!allIds.length) { setData(null); setLoading(false); return }
    setLoading(true)
    Promise.all([getListingTermsBatch(allIds), getFeatures().catch(() => [])])
      .then(([batch, feats]) => {
        if (gone) return
        const byListing = {}
        for (const t of batch.terms || []) (byListing[t.listing_id] ??= { policies: [] }).terms = t
        for (const p of batch.policies || []) (byListing[p.listing_id] ??= { policies: [] }).policies.push(p)
        // Earned-knowledge stubs (2026-07-28): rows the server withheld because this
        // viewer hasn't recorded their own on this property. They carry WHO + HOW MANY
        // but no values — hidden must read as hidden, never as "nothing recorded".
        for (const h of batch.hidden?.policies || []) (byListing[h.listing_id] ??= { policies: [] }).hiddenPolicies = h
        for (const h of batch.hidden?.terms || []) (byListing[h.listing_id] ??= { policies: [] }).hiddenTerms = h
        setData({ byListing, features: feats, recorders: batch.recorders || {}, hidden: batch.hidden || null })
      }).catch(() => { if (!gone) setData(null) })
      .finally(() => { if (!gone) setLoading(false) })
    return () => { gone = true }
  }, [allIds.join(','), refreshKey])

  const nameOf = code => { const f = data?.features?.find(x => x.code === code); return f ? (f.name_ka || f.name_en || code) : code }
  // Scrape-sourced stances (structured owner-restriction booleans, source_kind!=='agent') are
  // the AD's own signal, not any agent's — collected across all listings into one shared
  // "პირობები წყაროდან" row (2026-07-15). Agent stances override on matching (server-side) and
  // in the single-listing modal; here both rows show, each labeled by its source.
  // 0238: 'admin' is MANAGEMENT's own recording, not the ad's text — it must NOT fall
  // into this "from the source ad" bucket, or the agency's knowledge gets mislabeled as
  // something the portal said. It routes to a named recorder row like an agent stance
  // (below), differing only in that it is never gated.
  const AD_SOURCE_KINDS = new Set(['scrape', 'ai'])
  const sourceStances = !data ? [] : (() => {
    const seen = new Set(); const out = []
    for (const id of allIds) for (const p of (data.byListing?.[id]?.policies || []))
      if (AD_SOURCE_KINDS.has(p.source_kind ?? 'agent') && !seen.has(p.subject)) { seen.add(p.subject); out.push(p) }
    return out
  })()
  // ATTRIBUTED agent stances → their RECORDER's row (plan A, 2026-07-15), regardless of
  // which listing they physically sit on (gallery writes land on the source ad, whose
  // author is the OWNER — routing by host-listing author is exactly the bug A fixes).
  // Only rows whose recorder id RESOLVES to a name are routed; zero-uuid/unnamed ids
  // stay on the legacy author-based path below — conservative, never a guessed name.
  const ZERO_REC = '00000000-0000-0000-0000-000000000000'
  // 0236: attribution resolves through the PARTY when the row carries one — an admin
  // is nameable there and nowhere else. The agents.id path stays for rows written
  // before 0236 and for the roster lookup's own answers.
  const recorderName = r => data?.recorders?.[r?.recorded_by_party_id] || data?.recorders?.[r?.recorded_by_agent_id] || null
  const isAttributed = p => !AD_SOURCE_KINDS.has(p.source_kind ?? 'agent')
    && (p.recorded_by_party_id || (p.recorded_by_agent_id ?? ZERO_REC) !== ZERO_REC) && !!recorderName(p)
  const recorderStances = !data ? new Map() : (() => {
    const m = new Map()   // recorder name → [{subject, stance}], first record per subject wins
    for (const id of allIds) for (const p of (data.byListing?.[id]?.policies || [])) {
      if (!isAttributed(p)) continue
      const name = recorderName(p)
      if (!m.has(name)) m.set(name, [])
      if (!m.get(name).some(x => x.subject === p.subject)) m.get(name).push(p)
    }
    return m
  })()
  // The SAME routing for the numbers (0221). Until listing_terms carried a recorder, a
  // terms row could only be attributed by which listing it sat on — and gallery writes
  // sit on the SOURCE AD, authored by the owner — so an agent's deposit/min-lease fell
  // to the anonymous OWNER_TERMS_ROW while their stances, typed in the same breath,
  // showed their name. One recording, two rows, for no reason a user could see.
  // A terms row is "attributed" only when its recorder id RESOLVES to a name; anything
  // else stays on the legacy listing-based path, exactly like stances.
  const isAttributedTerms = t => !!t
    && (t.recorded_by_party_id || (t.recorded_by_agent_id ?? ZERO_REC) !== ZERO_REC) && !!recorderName(t)
  const recorderTerms = !data ? new Map() : (() => {
    const m = new Map()   // recorder name → their terms row (first wins; one row per listing)
    for (const id of allIds) {
      const tr = data.byListing?.[id]?.terms
      if (!isAttributedTerms(tr)) continue
      const name = recorderName(tr)
      if (!m.has(name)) m.set(name, { terms: tr, listingId: id })
    }
    return m
  })()
  // Withheld-by-contribution stubs, grouped by the agent who recorded them (2026-07-28).
  // The rule is "show WHICH agents have filled their restrictions, not the values" — so
  // this row is the visible half, and it must survive `anyData` (a card that shows
  // "nothing recorded" while five stances sit withheld is a lie, not a redaction).
  const hiddenByRecorder = !data?.hidden ? [] : (() => {
    const m = new Map()   // recorder name (or null = unattributed) → count
    // Party first (0236): an ADMIN has a party and no roster row, so the agents.id
    // lookup can never name them — that is exactly why their recordings rendered as
    // the anonymous "an agent" line.
    const nameFor = h => data.recorders?.[h.recorded_by_party_id] || data.recorders?.[h.recorded_by_agent_id] || null
    for (const h of data.hidden.policies || []) {
      const name = nameFor(h)
      m.set(name, (m.get(name) || 0) + (h.count || 0))
    }
    for (const h of data.hidden.terms || []) {
      const name = nameFor(h)
      if (!m.has(name)) m.set(name, 0)
    }
    return [...m.entries()].map(([name, count]) => ({ name, count }))
  })()
  // Read-only viewer with nothing recorded anywhere → one honest line, not a bare heading.
  const anyData = !!data && (sourceStances.length > 0 || recorderStances.size > 0 || hiddenByRecorder.length > 0
    || [...groups.values()].flat().some(id => {
      const d = data.byListing?.[id]
      return d && (d.policies?.length > 0 || d.terms)
    }))
  // Per-field confidence summary (share-immediately, no gate): what's the agency's status
  // on each owner-policy fact, computed from the same per-agent stances rendered below.
  const maturity = data ? fieldMaturity(allIds, data.byListing) : []
  // Batch still in flight: show an honest loading line, NOT a blank section. Without this the
  // restrictions area rendered EMPTY while getListingTermsBatch resolved (2 core reads + a
  // scheduler recorder-name lookup) — which read as "the stance I just recorded isn't here"
  // when it was in the DB all along and simply hadn't loaded yet (2026-07-21). On error `loading`
  // clears (finally) with data still null, so this falls through to the normal render below —
  // never an infinite spinner. Guarded on allIds so a card with no listings stays silent.
  if (loading && !data && allIds.length) {
    return <p className="text-sm text-muted-foreground">{t('desk_timeline_loading')}</p>
  }
  if (!canRecord && data && !anyData) {
    return <p className="text-sm text-muted-foreground">{t('desk_shop_terms_none')}</p>
  }
  return (
    <div className="space-y-1.5">
      {/* WITHHELD until you record your own (2026-07-28 owner ruling). Same blind-first
          contract the cooperation chip already uses ("join to see terms"): the FACT that
          an agent asked the owner is agency knowledge, the ANSWERS they got are earned.
          Recording any stance or terms row on this property unlocks every other agent's
          in the same breath — no separate request, no approval step.
          ⚠️ The ROW must wrap and the pill must be shrinkable. A §1 pill is
          whitespace-nowrap and flex-none, so this row — a shrink-0 name beside it — can
          only SPILL when the wording grows, which the non-rep variant did: 47px past the
          panel edge at 420px, clipped at 280px, while the wording it replaces fitted.
          flex-wrap drops the pill to its own line first; shrink+truncate ellipsises it
          rather than letting it escape. Both are inert at the widths that already fitted. */}
      {hiddenByRecorder.map(h => (
        <div key={`hidden-${h.name || 'anon'}`} className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted-foreground shrink-0">{h.name || t('desk_terms_recorded_by_agent')}</span>
          <span className={pill('warn', { shrink: true, extra: 'border-dashed truncate' })}>
            <Icon name="lock" />
            {/* A LOCK MUST NAME AN ACTION THE VIEWER HAS (2026-08-06). The unlock —
                "record yours to see" — is real for a representative and impossible for
                anyone else: canRecord=false removes every editor door below, so this line
                was instructing a non-rep to press something that isn't rendered. Same
                counterparty the empty-state names one branch up (desk_shop_terms_none). */}
            {canRecord
              ? (h.count ? tLog('desk_terms_hidden_count', { n: h.count }) : t('desk_terms_hidden_some'))
              : (h.count ? tLog('desk_terms_hidden_count_norep', { n: h.count }) : t('desk_terms_hidden_some_norep'))}
          </span>
        </div>
      ))}
      {/* Needs verification (2026-07-19): the ACTION half of the confidence model — the
          owner facts that aren't solid yet (single unconfirmed source / sources disagree),
          as a "confirm these on your next owner call" checklist. A nudge, never a gate;
          shown only to the representing agent (who'd make the call), and tapping an item
          opens the same per-agent editor to record what the owner says. Confirmed facts
          (✓✓) drop off — you don't re-ask what the agency already knows well. */}
      {(() => {
        if (!canRecord) return null
        const todo = maturity.filter(m => m.status !== 'confirmed') // single + conflict
        if (!todo.length) return null
        const editId = primaryListing?.listing_id || primaryIdFallback
        const canTap = !!editId
        return (
          <div className="rounded-md border border-amber-500/40 bg-amber-500/5 px-2 py-1.5 space-y-1">
            <span className="text-xs font-medium text-amber-700 dark:text-amber-300">
              ☎ {tLog('desk_needs_verify_heading', { n: todo.length })}
            </span>
            <div className="flex flex-wrap gap-1">
              {todo.map(m => {
                const conflict = m.status === 'conflict'
                const Tag = canTap ? 'button' : 'span'
                return (
                  <Tag key={m.subject}
                    {...(canTap ? { type: 'button', onClick: () => onOpen?.(editId), title: t('desk_needs_verify_record_hint') } : {})}
                    className={pill(conflict ? 'warn' : 'neutral', { door: canTap, extra: canTap ? 'hover:opacity-80' : '' })}>
                    {conflict ? <Icon name="alert-triangle" /> : '·'} {nameOf(m.subject)} — {t(conflict ? 'desk_match_reduced_conflict' : 'desk_match_reduced_single')}
                  </Tag>
                )
              })}
            </div>
          </div>
        )
      })()}
      {/* Confidence summary (2026-07-19): per-FIELD status across every recorder — the
          headline "can I trust this?" line above the who-said-what rows. 🟢 confirmed by
          ≥2 independent sources · 🟠 sources disagree (verify) · single-source = a nudge. */}
      {maturity.length > 0 && (
        <div className="flex flex-wrap items-center gap-1">
          <span className="text-xs text-muted-foreground min-w-0">{t('desk_maturity_heading')}</span>
          {maturity.map(m => {
            const cls = m.status === 'conflict'
              ? 'border-amber-500/60 bg-amber-500/10 text-amber-600 dark:text-amber-400'
              : m.status === 'confirmed'
                ? 'border-emerald-600/50 bg-emerald-600/10 text-emerald-600 dark:text-emerald-400'
                : 'border-border bg-muted/40 text-muted-foreground'
            const mark = m.status === 'conflict' ? <Icon name="alert-triangle" /> : m.status === 'confirmed' ? '✓✓' : '·'
            const title = [t(`desk_maturity_${m.status}`), tLog('desk_maturity_sources', { n: m.sources }),
              m.freshest ? timeAgo(m.freshest) : null].filter(Boolean).join(' · ')
            return (
              <span key={m.subject} title={title}
                className={pill('none', { extra: cls })}>
                {mark} {nameOf(m.subject)} {m.status !== 'conflict' && STANCE_ICON[m.stance]}
                {m.sources > 1 && <span className="opacity-60">×{m.sources}</span>}
              </span>
            )
          })}
        </div>
      )}
      {/* The ad's OWN stated conditions (source_kind='scrape') — read-only, no editor button:
          an agent can't edit the source ad's signal, they record their own agent stance. */}
      {sourceStances.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-muted-foreground min-w-0">{t('desk_terms_from_owner_ad')}</span>
          {sourceStances.map(p => (
            <span key={p.subject} className={pill('none', { extra: STANCE_STYLE[p.stance] })}>
              {STANCE_ICON[p.stance]} {nameOf(p.subject)}
            </span>
          ))}
        </div>
      )}
      {/* New structured restriction_rule records have their own isolated reader. Keeping it
          outside the legacy terms/policies batch makes this optional summary incapable of
          taking down the property modal if an old rule has an unexpected shape. */}
      <RestrictionRulesSummary listingIds={allIds} onOpen={canRecord ? onOpen : null} />
      {/* Rows = the posting-derived groups, PLUS anyone who recorded something here
          without having a posting of their own (0221). That second case is real: a
          buyer-side agent who called the owner, or an agent whose only listing aged out
          of this family, records against the source ad and previously had nowhere to
          appear — their work fell to the anonymous row, or vanished when there was no
          viewer row to fold into. `ids` is empty for those rows; every consumer below
          already tolerates that, and the editor target falls back to the listing their
          record physically sits on. */}
      {[...(() => {
        const rows = new Map([...groups.entries()])
        for (const name of [...recorderStances.keys(), ...recorderTerms.keys()])
          if (!rows.has(name)) rows.set(name, [])
        return rows.entries()
      })()].map(([agent, ids]) => {
        const withData = ids.filter(id => data?.byListing?.[id])
        const stances = []   // merged across the agent's postings, first record per subject wins
        const seen = new Set()
        // This row's ATTRIBUTED stances lead (recorded_by → this name, plan A) — they're the
        // agent's explicit record, wherever the row physically lives. Then legacy UNATTRIBUTED
        // agent stances on their own listings (old author-based routing). Attributed rows are
        // excluded from the legacy pass — they render on their RECORDER's row, else a stance
        // Lana typed against the source ad would double-show here via the fold. Scrape stances
        // belong to the "პირობები წყაროდან" row above, never to an agent — 2026-07-15.
        for (const p of (recorderStances.get(agent) || [])) if (!seen.has(p.subject)) { seen.add(p.subject); stances.push(p) }
        for (const id of withData) for (const p of data.byListing[id].policies)
          if ((p.source_kind ?? 'agent') === 'agent' && !isAttributed(p) && !seen.has(p.subject)) { seen.add(p.subject); stances.push(p) }
        // Same precedence as the stances directly above: this row's ATTRIBUTED terms
        // lead, wherever the row physically lives; otherwise fall back to the legacy
        // listing-based pick, with attributed rows EXCLUDED so a terms row Mariam typed
        // against the source ad can't also surface on the anonymous row via the fold.
        const ownTerms = recorderTerms.get(agent) || null
        const terms = ownTerms?.terms
          || withData.map(id => data.byListing[id].terms).find(tr => tr && !isAttributedTerms(tr))
        const nums = []
        if (terms?.deposit_amount != null) nums.push(tLog('desk_terms_summary_deposit_amount', { v: Number(terms.deposit_amount).toLocaleString(), c: terms.deposit_currency || '' }))
        else if (terms?.deposit_months != null) nums.push(tLog('desk_terms_summary_deposit', { n: terms.deposit_months }))
        if (terms?.min_lease_months != null) nums.push(tLog('desk_terms_summary_minlease', { n: terms.min_lease_months }))
        if (terms?.max_occupants != null) nums.push(tLog('desk_terms_summary_maxocc', { n: terms.max_occupants }))
        // sale-shaped bits (0080) — shown whenever recorded, no intent branch needed
        if (cashOnlyOf(terms) === true) nums.push(t('desk_sale_cash_only_yes'))
        if (terms?.occupancy) nums.push(t(`desk_sale_opt_${terms.occupancy}`))
        if (terms?.encumbrance === 'bank_mortgage') nums.push(t('desk_sale_opt_bank_mortgage'))
        if (terms?.bank_appraisal_value != null) nums.push(tLog('desk_sale_summary_appraisal', { v: Number(terms.bank_appraisal_value).toLocaleString(), c: terms.bank_appraisal_currency || '' }))
        else if (terms?.bank_appraisal_status === 'appraised') nums.push(t('desk_sale_appraisal_opt_appraised'))
        else if (terms?.bank_appraisal_status === 'none') nums.push(t('desk_sale_appraisal_opt_none'))
        // Editor target: this agent's own posting when they have one, else the listing
        // their record physically sits on (0221) — without that fallback a recorder-only
        // row would open the editor on `undefined`. Their own posting is preferred so a
        // NEW recording lands on their listing rather than the owner's ad.
        const target = withData[0] || ids[0]
          || ownTerms?.listingId
          || (recorderStances.get(agent) || []).find(p => p.listing_id)?.listing_id
        const hasData = stances.length > 0 || nums.length > 0
        // The fallback row exists ONLY to keep already-recorded AGENT stances visible when
        // there's no viewer row to fold them into (shota/Pinar case) — an EMPTY "recorded
        // by an agent ＋" placeholder is noise, not an affordance: nobody's job is to record
        // terms against an ad they don't own (owner ruling 2026-07-13).
        if (agent === OWNER_TERMS_ROW && !hasData) return null
        // WRITE SCOPE (owner ruling 2026-07-29: "why should an agent see + and press it on
        // another agent's rows?"): each agent records their OWN conversation with the
        // owner — the editor door exists only on the viewer's own row (and the
        // OWNER_TERMS_ROW fallback bucket, which is where a viewer with no posting
        // records). Another agent's row is a FACT to read: chips stay, doors don't.
        const rowEditable = canRecord && (agent === viewerName || agent === OWNER_TERMS_ROW)
        // A row that carries nothing and can't be written by this viewer is pure noise —
        // before this, every colleague's name rendered with a pressable 🏠＋.
        if (!rowEditable && !hasData) return null
        return (
          <div key={agent} className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs text-muted-foreground min-w-0">{agent === OWNER_TERMS_ROW ? t('desk_terms_agent_recorded') : agent === viewerName ? t('desk_lineage_your_reposts_section') : agent}</span>
            {/* ⚠️ PRESENCE RULE (owner ruling 2026-07-25, CARD_FOOTER_SPEC.md §1 — the same
                fix CoopAgentRow got, and this row is the second instance of the same bug):
                a dashed affordance exists to represent ABSENCE. Once this agent has recorded
                something, the recorded chips ARE the button; the 🏠 pill beside them was a
                second door to the identical editor and read as "there's still something to
                add" on a row that was already complete. Owner-flagged on both rows.
                Read-only viewers keep plain spans — no door to offer them.

                ⚠️ THE CONDITION IS "no CHIPS", not "no data" (2026-08-06). The rule's own
                justification is that the chips are the button — so it must ask whether
                chips exist, and it asked `!hasData` instead. A row whose only content is
                the numeric summary (deposit / min-lease, no stance recorded yet) therefore
                lost the ＋ and gained nothing visible: its sole affordance became a hover
                underline on muted grey text, which does not exist at all on touch. Owner-
                reported — an agent with `მინ 12თვე` on his own row and no way in.
                A row with chips is unchanged. */}
            {rowEditable && stances.length === 0 && (
              <button title={t('desk_terms_facet_hint')} onClick={() => onOpen(target)}
                className={pill('add', { door: true })}>
                <Icon name="house" />＋
              </button>
            )}
            {stances.map(p => {
              const Tag = rowEditable ? 'button' : 'span'
              return (
                <Tag key={p.subject}
                  {...(rowEditable ? { type: 'button', title: t('desk_terms_facet_hint'), onClick: () => onOpen(target) } : {})}
                  className={pill('none', { door: rowEditable, extra: STANCE_STYLE[p.stance] })}>
                  {STANCE_ICON[p.stance]} {nameOf(p.subject)}
                </Tag>
              )
            })}
            {/* The numeric summary (deposit / min-lease / sale bits) is the row's only value
                when an agent recorded terms but no stance chips — so it must carry the door
                too, or such a row would have none at all once the pill went away. */}
            {nums.length > 0 && (rowEditable
              ? <button type="button" title={t('desk_terms_facet_hint')} onClick={() => onOpen(target)}
                  className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline">{nums.join(' · ')}</button>
              : <span className="text-xs text-muted-foreground">{nums.join(' · ')}</span>)}
          </div>
        )
      })}
      {/* ⚠️ REMOVED 2026-07-28 (owner report: "somehow I was able to record Mariam twice" —
          she was recorded ONCE; the UI drew her row twice). This block rendered recorders
          with no posting of their own — the Lana case — but the MAIN row loop above already
          does exactly that: it seeds `rows` from `groups` and then adds every
          recorderStances/recorderTerms name that isn't there yet (0221), with `ids` empty.
          So every recorder-without-a-posting was emitted by BOTH blocks. The main loop is
          the better of the two — it also carries their terms numbers and the editor target
          — so this one goes rather than the guard being tightened. Verified against the DB:
          one `pol_iranians` row for Mariam on this property, two rows on screen. */}
    </div>
  )
}

// Opens as a nested modal (2026-07-11) — same click-to-open pattern as the meeting
// (📅＋) and cooperation (🤝＋) chips, not an inline accordion. Portaled to body via
// <Modal>, so it stacks on top of the lineage/property modal that launched it.
// ⚠️ `propertyId` (2026-07-21) is here for ONE field — "available from". Every other field
// in this editor is per-LISTING (listing_terms, this agent's own record, amended through the
// 0055 gateway); availability is per-PROPERTY, set through the rep-gated
// /property/:id/availability endpoint. Two different stores behind one Save button, so it
// needs its own load and its own write and is NOT part of the `ops` diff.
// ⚠️ Storage changed under this field in 0220 (the UI did not): it is now an OBSERVATION in
// `property_availability`, one row per (property, source, recorder), resolved at read time
// through evidence-certainty — so a second agent's date no longer overwrites the first's,
// the two are a `conflicting` resolve until someone confirms. Saving records YOUR statement;
// clearing retracts YOUR statement, not everyone's. The value still arrives on the dormancy
// response as `available_from`, which is why nothing here had to change.
// ⚠️ NEVER store a duration ("in two weeks") in this field's place — that is what 0220
// retired. A relative phrase silently becomes a different fact every day it sits unread;
// the portal's own "vacates in N weeks" dropdown is DERIVED from this date at posting time.
// The move-in bill, as a sentence. Pure render over the LIVE form state, so it updates as
// the agent types the deposit or flips a prepay select — the point is to let them see the
// number they are about to quote a tenant before they save it.
// Parts with no price still LIST (the requirement is the fact; the money is the projection),
// and a mixed-currency bill shows its parts with no sum rather than adding ₾ to $.
function MoveInPaymentLine({ terms, rent = null, rentCurrency = null }) {
  const tri = v => (v === 'true' || v === true ? true : v === 'false' || v === false ? false : null)
  const m = moveInPayment({
    prepayFirst: tri(terms.prepay_first_month), prepayLast: tri(terms.prepay_last_month),
    depositAmount: terms.deposit_amount, depositCurrency: terms.deposit_currency,
    // 1083 - a deposit stated as a share of a month (“ნახევარი თვე” = 0.5). Without this
    // the bill silently omitted the deposit on every listing that named one in months, and
    // under-stating what a tenant must produce is worse than showing no figure.
    depositMonths: terms.deposit_months,
    rent, rentCurrency,
  })
  // ALWAYS visible (owner 2026-07-27, matching the gallery's ever-present "წინასწარ:"
  // line) — but a blank record reads "—", never a fabricated 0: the gallery total is a
  // calculator over live inputs, this line is a statement about what was RECORDED.
  const LABEL = { first_month: 'desk_terms_prepay_first', last_month: 'desk_terms_prepay_last', deposit: 'desk_terms_deposit_short' }
  return (
    <div className="rounded-lg border border-border bg-muted/40 px-3 py-2">
      <p className="text-xs font-medium text-muted-foreground mb-0.5">{t('desk_movein_heading')}</p>
      {(!m.asked || !m.parts.length) && <p className="text-xs text-muted-foreground">—</p>}
      <p className="text-xs">
        {m.parts.map((p, i) => (
          <span key={p.kind}>
            {i > 0 ? ' + ' : ''}{t(LABEL[p.kind])}
            {p.amount != null && p.currency ? <span className="text-muted-foreground"> ({Number(p.amount).toLocaleString('en-US')} {p.currency})</span> : null}
          </span>
        ))}
        {m.total != null && (
          <span className="font-semibold"> = {Math.round(m.total).toLocaleString('en-US')} {m.currency}</span>
        )}
      </p>
      {/* Two different silences, said differently — neither is allowed to look like a total. */}
      {m.mixed && <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">{t('desk_movein_mixed_currency')}</p>}
      {!m.complete && !m.mixed && <p className="text-xs text-muted-foreground mt-0.5">{t('desk_movein_incomplete')}</p>}
    </div>
  )
}

// `mode` replaced the old `saleMode` boolean when girao arrived (0219): there are three
// deal shapes now, and a boolean could only ever describe two. Callers pass the shape the
// TERMS have, which is not always the shape of the ad they were opened from — see
// termsModeFor() in LineageModal.
function RentalTermsModal({ listingId, propertyId = null, mode: modeProp = 'rent', onClose, onRecorded, agentId = null, rent = null, rentCurrency = null, inline = false }) {
  // The caller picks the OPENING shape (termsModeFor); the agent can switch once the
  // row is loaded and we can see what was actually recorded. Local state, not derived:
  // switching must not require the parent to re-decide anything.
  const [mode, setMode] = useState(modeProp)
  // review 2026-09-23: follow the host when ITS intent changes (the form's rent→sale switch).
  useEffect(() => { setMode(modeProp) }, [modeProp])
  const saleMode = mode === 'sale'
  const giraoMode = mode === 'girao'
  const [row, setRow] = useState(null)       // last-loaded server row, for diffing
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [autoSaveState, setAutoSaveState] = useState('idle')
  const failedAutoSaveRef = useRef(null)
  const latestFormRef = useRef(null)
  const [err, setErr] = useState(null)
  // Renamed from `t`/`setT` (was shadowing the imported i18n t() function, silently breaking
  // any translation call inside this component).
  const [terms, setTerms] = useState({})     // form state: tri ''|'true'|'false', numbers '', texts ''
  // Structured tenant-acceptance stances (listing_policy, 0018) — the supply-side
  // mirror of the client form's household_codes. subject → stance | undefined.
  const [policies, setPolicies] = useState({})
  const [policiesSaved, setPoliciesSaved] = useState({})   // last-loaded server state, for diffing
  // review 2026-09-23 (bug 4): the FULL policy vocabulary is loaded once; which chips show is
  // derived from the CURRENT mode, so switching rent→sale no longer keeps the rental stances.
  const [policyVocabAll, setPolicyVocabAll] = useState([])  // tenant_terms policy-mode features
  const policyVocab = useMemo(() => (saleMode
    ? policyVocabAll.filter(f => isNationalityFeature(f) || (f.subcategory === 'payment' && f.code !== 'pol_bank_transfer_ok'))
    : policyVocabAll.filter(f => f.code !== 'pol_seller_installment')), [policyVocabAll, saleMode])
  // property.available_from — separate store from `terms` (see the header note), so it keeps
  // its own last-loaded copy to diff against instead of riding fromRow(row).
  const [availFrom, setAvailFrom] = useState('')
  const [availSaved, setAvailSaved] = useState('')
  // Which stance chip has its menu open (one at a time) — the cycle needed no such state;
  // the menu does. Closed on pick, so a choice never leaves a popup hanging over the grid.
  const [openPolicy, setOpenPolicy] = useState(null)
  // B237 — "asked the owner: there are no restrictions". Its own value, never inferred from
  // an empty stance set: unanswered and answered-clear are different facts, and treating
  // absence as the answer is the bug this closes.
  const [noRestr, setNoRestr] = useState(false)
  const [noRestrSaved, setNoRestrSaved] = useState(false)
  // Fallback property id resolved by the terms GET itself (2026-07-21, user-caught: the
  // availability field silently vanished when a caller didn't thread propertyId — e.g. the
  // workspace's saved.propertyId on entries saved before it was stamped). The prop wins
  // when present; the server's answer covers everyone else.
  const [resolvedPid, setResolvedPid] = useState(null)
  const pid = propertyId || resolvedPid

  const fromRow = r => {
    const tri = v => (v == null ? '' : String(v))
    const num = v => (v == null ? '' : v)
    return {
      pets_allowed: tri(r?.pets_allowed), smoking_allowed: tri(r?.smoking_allowed),
      children_allowed: tri(r?.children_allowed), furnished: tri(r?.furnished),
      registered_lease: tri(r?.registered_lease),
      deposit_amount: num(r?.deposit_amount), deposit_currency: r?.deposit_currency || '',
      prepay_first_month: tri(r?.prepay_first_month), prepay_last_month: tri(r?.prepay_last_month),
      prepayments: Array.isArray(r?.prepayments) ? r.prepayments : [],
      // 1083 — read back, or the chip opens blank on a listing that HAS a deposit and the
      // next save clears what was recorded. tri() for the same reason as the two above:
      // "nobody asked" has to survive the round trip as '' and never save as false.
      deposit_required: tri(r?.deposit_required), deposit_basis: r?.deposit_basis || '',
      deposit_months: num(r?.deposit_months),
      min_lease_months: num(r?.min_lease_months),
      max_occupants: num(r?.max_occupants), pet_deposit_extra: num(r?.pet_deposit_extra),
      // 0416 — the pol_pet_deposit chip's successor. tri(), so "nobody asked" survives the
      // round trip as '' and never saves as false.
      pet_deposit_required: tri(r?.pet_deposit_required),
      promised_improvements: r?.promised_improvements || '',
      is_final_condition: r?.is_final_condition === true,
      tenant_pref_notes: r?.tenant_pref?.notes || '',
      // sale conditions (0080)
      // B301 step 2: the tri-state is a VIEW over financing_accepted (see cashOnlyOf).
      cash_only: tri(cashOnlyOf(r)), full_price_declaration: tri(r?.full_price_declaration),
      occupancy: r?.occupancy || '',
      furniture_included: r?.furniture_included || '', parking_in_price: r?.parking_in_price || '',
      encumbrance: r?.encumbrance || '',
      bank_appraisal_value: num(r?.bank_appraisal_value), bank_appraisal_currency: r?.bank_appraisal_currency || '',
      // 1192: a figure recorded before the status existed means "appraised".
      bank_appraisal_status: r?.bank_appraisal_status || (r?.bank_appraisal_value != null ? 'appraised' : ''),
      // girao (0219)
      girao_amount: num(r?.girao_amount), girao_currency: r?.girao_currency || '',
      girao_period_years: num(r?.girao_period_years), girao_owner_resides: tri(r?.girao_owner_resides),
    }
  }

  useEffect(() => {
    let gone = false
    setLoading(true); setErr(null)
    Promise.all([
      getListingTerms(listingId),
      // best-effort extras: stances + vocab failing must not block the terms form
      listListingPolicies(listingId).catch(() => ({ policies: [] })),
      getFeatures().catch(() => []),
      // best-effort like the two above: no propertyId (or a failed fetch) just means the
      // availability field starts blank — it must never block the terms form from opening.
      propertyId ? getDormancy([propertyId]).catch(() => new Map()) : Promise.resolve(new Map()),
    ]).then(([d, pol, feats, dorm]) => {
      if (gone) return
      setResolvedPid(d.property_id || null)
      const av = dorm.get(propertyId)?.available_from || ''
      setAvailFrom(av); setAvailSaved(av)
      // No propertyId prop but the terms GET resolved one (older lib builds don't send it) —
      // fetch availability in a follow-up; the prefetch above could only run off the prop.
      if (!propertyId && d.property_id) {
        getDormancy([d.property_id]).then(m => {
          if (gone) return
          const av2 = m.get(d.property_id)?.available_from || ''
          setAvailFrom(av2); setAvailSaved(av2)
        }).catch(() => {})
      }
      setRow(d.terms); setTerms(fromRow(d.terms))
      // Seeding order (weakest → strongest, later overwrites): scraped fallback →
      // legacy unattributed agent row (zero-uuid recorder) → THIS agent's own row.
      // Other agents' ATTRIBUTED rows are deliberately NOT seeded (plan A, 2026-07-15):
      // this editor writes to the CURRENT agent's row only — seeding another agent's
      // stance would silently copy it into this agent's record on save. Their records
      // stay visible in the by-agent panel, not here.
      const ZERO_REC = '00000000-0000-0000-0000-000000000000'
      const bySubject = {}
      for (const p of pol.policies || []) if (p.source_kind !== 'agent' && !(p.subject in bySubject)) bySubject[p.subject] = p.stance
      for (const p of pol.policies || []) if (p.source_kind === 'agent' && (p.recorded_by_agent_id ?? ZERO_REC) === ZERO_REC) bySubject[p.subject] = p.stance
      for (const p of pol.policies || []) if (p.source_kind === 'agent' && agentId && p.recorded_by_agent_id === agentId) bySubject[p.subject] = p.stance
      setPolicies(bySubject); setPoliciesSaved(bySubject)
      // B237. Deliberately NOT derived from "bySubject is empty" — that is exactly the
      // conflation this feature removes. It is its own recorded answer, loaded as its own
      // value, with its own saved-copy for diffing. An older server that doesn't send
      // `clears` yields false = "nobody answered", which is the correct read of silence
      // from a build that cannot express the third state.
      const cleared = Array.isArray(pol.clears) && pol.clears.length > 0
      setNoRestr(cleared); setNoRestrSaved(cleared)
      // mode fallback for a stale server build whose /features doesn't return it:
      // policy-mode tenant_terms codes are all pol_* by convention (0019)
      const vocabPol = feats.filter(f => f.category === 'tenant_terms' && (f.mode ? f.mode === 'policy' : f.code.startsWith('pol_')))
      // SALE: nationality/citizenship restrictions + the sale-relevant PAYMENT stances
      // (0216: seller installment, crypto/foreign wire — first-call facts per the Q11
      // market pass) — a seller never states pet/smoker/student conditions; those are
      // rental concepts. pol_bank_transfer_ok stays rent-side (the sale-side "official"
      // question is full_price_declaration, already its own field — one fact, one home).
      // RENT: everything EXCEPT seller installment, which only exists at sale.
      setPolicyVocabAll(vocabPol)   // filtered per mode by the policyVocab memo (review 2026-09-23)
      setLoading(false)
      setAutoSaveState('saved')
    }).catch(e => { if (!gone) { setErr(e.message); setLoading(false) } })
    return () => { gone = true }
  }, [listingId, propertyId])

  async function handleRecord(opts = {}) {
    const automatic = !!opts.automatic
    const submittedSignature = autoSaveSignature
    if (automatic) setAutoSaveState('saving')
    setSaving(true); setErr(null)
    try {
      const tri = v => (v === '' ? null : v === 'true')
      const num = v => (v === '' || v == null || !isFinite(+v) ? null : +v)
      const cur = fromRow(row)
      const ops = []
      const push = (field, val) => ops.push(val == null ? { op: 'clear', field } : { op: 'set', field, value: val })
      for (const [k] of TERMS_TRI) if (terms[k] !== cur[k]) push(k, tri(terms[k]))
      for (const [k] of TERMS_NUM) if (String(terms[k]) !== String(cur[k])) push(k, num(terms[k]))
      // sale fields diff regardless of mode — inputs only render in their mode, so an
      // untouched field never produces an op; this keeps ONE save path for both shapes
      // B301 step 2 (core 0400/0486): the cash-only CONTROL now writes
      // `financing_accepted`, never the deprecated boolean — 0400's own mapping, which
      // is also what its backfill used, so a row written here and a row backfilled then
      // are indistinguishable. The control stays a TRI-STATE deliberately: exposing all
      // four enumeration values is a vocabulary decision (0400 split installment_seller
      // from installment_developer because they mean different things to a buyer) and
      // needs the owner's Georgian, not a guess here.
      for (const [k] of SALE_TRI) {
        if (terms[k] === cur[k]) continue
        if (k === 'cash_only') {
          const v = tri(terms[k])
          push('financing_accepted', v == null ? null : v ? ['full'] : ['full', 'mortgage'])
        } else push(k, tri(terms[k]))
      }
      for (const [k] of RENT_TRI) if (terms[k] !== cur[k]) push(k, tri(terms[k]))
      // 1211: the prepayment list. Core derives first/last/months from it; emptying the list
      // clears those too, or core would rebuild the list from them on the next save.
      if (JSON.stringify(terms.prepayments || []) !== JSON.stringify(cur.prepayments || [])) {
        const list = (terms.prepayments || []).map(r => (r.full === false ? { month: String(r.month), full: false, part: Number(r.part) } : { month: String(r.month), full: true }))
        if (list.length) push('prepayments', list)
        else { push('prepayments', null); push('prepay_first_month', null); push('prepay_last_month', null); push('prepayment_months', null) }
      }
      for (const [k] of SALE_SELECTS) if (terms[k] !== cur[k]) push(k, terms[k] || null)
      // 1192: "no appraisal" carries no figure (the table refuses both), so choosing it clears one.
      if (terms.bank_appraisal_status !== cur.bank_appraisal_status) push('bank_appraisal_status', terms.bank_appraisal_status || null)
      if (terms.bank_appraisal_status === 'none') {
        if (cur.bank_appraisal_value !== '' && cur.bank_appraisal_value != null) push('bank_appraisal_value', null)
      } else if (String(terms.bank_appraisal_value) !== String(cur.bank_appraisal_value)) push('bank_appraisal_value', num(terms.bank_appraisal_value))
      if (terms.bank_appraisal_currency !== cur.bank_appraisal_currency) push('bank_appraisal_currency', terms.bank_appraisal_currency || null)
      // girao (0219). Amount+currency move as a UNIT for the same reason the deposit
      // does: a sum with no currency is not a fact, it's a number. period/owner_resides
      // diff independently — either can be known while the other isn't.
      if (String(terms.girao_period_years) !== String(cur.girao_period_years)) push('girao_period_years', num(terms.girao_period_years))
      if (terms.girao_owner_resides !== cur.girao_owner_resides) push('girao_owner_resides', tri(terms.girao_owner_resides))
      if (String(terms.girao_amount) !== String(cur.girao_amount)
          || terms.girao_currency !== cur.girao_currency) {
        const gAmt = num(terms.girao_amount)
        push('girao_amount', gAmt)
        push('girao_currency', gAmt == null ? null : (terms.girao_currency || 'USD'))
      }
      // Deposit amount and currency move as a UNIT — 0212 has a CHECK rejecting one
      // without the other, so sending an amount alone would fail the whole amend.
      // Clearing the amount clears the currency with it.
      if (String(terms.deposit_amount) !== String(cur.deposit_amount)
          || terms.deposit_currency !== cur.deposit_currency) {
        const amt = num(terms.deposit_amount)
        push('deposit_amount', amt)
        push('deposit_currency', amt == null ? null : (terms.deposit_currency || 'USD'))
      }
      if (terms.promised_improvements !== cur.promised_improvements) push('promised_improvements', terms.promised_improvements.trim() || null)
      if (terms.is_final_condition !== cur.is_final_condition) push('is_final_condition', terms.is_final_condition ? true : null)
      if (terms.tenant_pref_notes !== cur.tenant_pref_notes) push('tenant_pref.notes', terms.tenant_pref_notes.trim() || null)
      // stance chips diff against last-loaded server state — each change is its
      // own idempotent upsert/delete (agent source_kind only; scrapes untouched)
      const policyChanges = Object.keys({ ...policies, ...policiesSaved })
        .filter(s => (policies[s] || null) !== (policiesSaved[s] || null))
      const noRestrChanged = noRestr !== noRestrSaved
      // available_from is NOT an `ops` entry — different store, different endpoint (see the
      // component header). Counted in `n` so the toast reflects it. Identity: an agent id
      // when we have one, else the viewer's own scheduler session token — the server
      // re-verifies it as a real admin (owner ruling 2026-07-21: admin can set this;
      // non-admin agentless sessions get a 400/403, surfaced as the save error).
      const availChanged = pid && availFrom !== availSaved
      if (!ops.length && !policyChanges.length && !availChanged && !noRestrChanged) {
        if (automatic) setAutoSaveState('saved')
        setSaving(false)
        return
      }
      // agentId threaded (0221) so the row records WHO — the same argument the policy
      // call below has always carried. Without it the numbers stayed anonymous while
      // the stances typed in the same breath showed a name.
      if (ops.length) await amendListingTerms(listingId, ops, agentId)
      for (const subject of policyChanges) await setListingPolicy(listingId, subject, policies[subject] || null, undefined, agentId)
      // B237. Sent only when CHANGED, and the false case sends an explicit retract rather
      // than simply not sending — unticking has to withdraw the claim, or the stale answer
      // keeps speaking for the owner after the agent has decided it is wrong.
      if (noRestrChanged) { await setRestrictionsClear(listingId, noRestr); setNoRestrSaved(noRestr) }
      if (availChanged) {
        let adminToken = null
        if (!agentId) {
          const { data: { session } } = await supabase.auth.getSession()
          adminToken = session?.access_token || null
        }
        await setPropertyAvailability(pid, agentId, availFrom || null, adminToken)
        setAvailSaved(availFrom)
      }
      const d = await getListingTerms(listingId)
      setRow(d.terms)
      // Do not replace characters typed while the request was in flight. The new server
      // row becomes the baseline; the newer browser value remains dirty and is saved next.
      if (latestFormRef.current === submittedSignature) setTerms(fromRow(d.terms))
      setPoliciesSaved({ ...policies })
      const n = ops.length + policyChanges.length + (availChanged ? 1 : 0) + (noRestrChanged ? 1 : 0)
      onRecorded?.(`Rental terms updated (${n} change${n > 1 ? 's' : ''})`)
      if (automatic) { failedAutoSaveRef.current = null; setAutoSaveState('saved') }
    } catch (e) {
      setErr(e.message)
      if (automatic) { failedAutoSaveRef.current = submittedSignature; setAutoSaveState('error') }
    }
    finally { setSaving(false) }
  }

  const dirty = JSON.stringify(terms) !== JSON.stringify(fromRow(row))
    || JSON.stringify(policies) !== JSON.stringify(policiesSaved)
    || availFrom !== availSaved
    || noRestr !== noRestrSaved
  const autoSaveSignature = JSON.stringify({ terms, policies, availFrom, noRestr })
  latestFormRef.current = autoSaveSignature

  // Every edit to an existing terms record persists after a short pause. The failed
  // signature guard prevents an unavailable server from producing an endless retry loop;
  // another edit retries naturally, and the visible Retry action retries on demand.
  useEffect(() => {
    if (loading || !dirty || saving || failedAutoSaveRef.current === autoSaveSignature) return
    setAutoSaveState('pending')
    const timer = setTimeout(() => handleRecord({ automatic: true }), 700)
    return () => clearTimeout(timer)
  }, [loading, dirty, saving, autoSaveSignature])
  // SAFETY NET (2026-07-27). Any shape OTHER than the one on screen that already holds
  // recorded values. This exists because the opening shape is a guess — an educated one
  // (cooperation intent, then ad intent), but a guess. When it is wrong, the agent's own
  // recorded numbers would otherwise be invisible with no hint that they exist, which is
  // exactly the failure that started this work: rent terms recorded against a girao ad,
  // opening in the sale editor, reading as "nothing here".
  // Computed from the SERVER row, never from form state, so an in-progress edit in this
  // panel can't make a sibling shape appear to have data.
  const otherShapes = row
    ? Object.keys(SHAPE_FIELDS).filter(k => k !== mode && SHAPE_FIELDS[k].some(f => row[f] != null))
    : []
  // inline (2026-07-29): the combined deal editor in LineageModal hosts this form as a
  // TAB next to the cooperation form (agents fill both in one sitting) — body only, the
  // host owns the chrome. Default (no host) keeps the standalone Modal exactly as before.
  const termsBody = (
    <>
      {loading ? <p className="text-sm text-muted-foreground">{t('desk_loading')}</p> : (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">{t('desk_terms_facet_hint')}</p>
          {/* "There are conditions recorded here in another shape" — a fact plus a door,
              not a warning. Nothing is auto-switched: the agent decides which shape this
              deal really is, and switching only changes what this panel RENDERS (every
              shape's values live in the same row and none are cleared by looking away). */}
          {otherShapes.length > 0 && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/5 px-2 py-1.5 flex flex-wrap items-center gap-1.5">
              <span className="text-xs text-amber-700 dark:text-amber-300">{t('desk_terms_other_shape')}</span>
              {otherShapes.map(k => (
                <button key={k} type="button" onClick={() => setMode(k)}
                  className={pill('warn', { door: true })}>
                  {t(k === 'sale' ? 'desk_listing_intent_sale' : k === 'girao' ? 'desk_listing_intent_girao' : 'desk_listing_intent_rent')}
                </button>
              ))}
            </div>
          )}
          {/* Structured tenant-acceptance stances lead — the matchable mirror of the client
              form's household tags (same tenant_terms codes). pets/smoking/children used to
              be separate tri-state dropdowns here; removed 2026-07-11 as pure duplication —
              pol_animals/pol_smoker/pol_children are already in this same chip grid, and the
              4-value stance (allowed/preferred/not/only) is richer than yes/no/unknown was.
              Tap cycles: ✓ დაიშვება → ★ სასურველია → ✗ არ დაიშვება → ❗ მხოლოდ → off. */}
          {/* STRUCTURED RULES (core 0398–0403) — the successor to the chip grid below.
              A rule is a polarity over a predicate, so "ინდუსი გოგოები კი, ბიჭები არა" is
              one recorded fact rather than a code nobody could mint.

              ⚠ BOTH SURFACES SHIP, and that is transitional, not indecision — but the
              reason CHANGED on 2026-08-05 and the old one is no longer true. 0413/0415/0416
              moved every person-recorded stance the grammar can hold into restriction_rule
              (1,508 → 2), and a legacy stance now converts the moment an agent touches it
              (upgrade-on-touch), so nothing is stranded any more. What still keeps the grid
              is the LAST TWO axis-less subjects — pol_residency and pol_remote_worker, which
              are neither a predicate nor money and have nowhere to go until B319 is ruled on.

              ⚠ "Once rules carry the volume" can no longer be answered by counting
              restriction_rule: it reads 1,502 because a migration wrote it. Adoption is
              restriction_rule_authored_v (0419), which read 0. Retire on ADOPTION. */}
          <div>
            <p className="text-xs text-muted-foreground mb-1">{t('desk_terms_policies_label')}</p>
            {/* agentId is what makes upgrade-on-touch honest: the /policy clear resolves its
                recorder from the body, so without it the panel could only withdraw the
                zero-uuid sentinel rows and an agent's own attributed chip would silently
                reappear on reload. */}
            <RestrictionRules listingId={listingId} saleMode={saleMode} agentId={agentId} />
          </div>

          {policyVocab.length > 0 && (
            /* THE LEGACY GRID FOLDS (owner, 2026-08-05: "can't we move those values to the
               new system?"). The values ARE in the new system — restriction_rule_v projects
               every stance into the rules list above at read time, and agent-recorded ones
               are now editable there (upgrade-on-touch). What cannot move yet is the grid
               itself: scrape/ai stances re-write on every observation and colleagues'
               attributed chips are theirs alone, so this stays their only editor — folded,
               labelled with its count, open only when the agent asks. Retire it whole when
               rules carry the volume (B307), not before. */
            <div>
              {/* B237 — the answer that used to be unsayable. Stays OUTSIDE the fold below:
                  it answers the whole restrictions question ("asked, and there are none"),
                  which an agent must be able to close in one tap after a call without
                  opening the legacy grid. It does not clear or disable the chips: "no
                  restrictions apart from the one I recorded" is a real and common answer,
                  and the read-time expansion (0313/0315) already keeps a recorded stance
                  winning over the blanket clear. */}
              <label className="mb-2 flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={noRestr}
                  onChange={e => setNoRestr(e.target.checked)} />
                <span className={noRestr ? 'text-emerald-600 font-medium' : 'text-muted-foreground'}>
                  <Icon name="check" /> {t('desk_terms_no_restrictions')}
                </span>
              </label>
            <details className="group">
              <summary className="cursor-pointer list-none text-xs text-muted-foreground mb-1 select-none">
                <span className="inline-block transition-transform group-open:rotate-90">›</span>{' '}
                {t(saleMode ? 'desk_sale_policies_label' : 'desk_terms_policies_label')}
                {Object.keys(policies).length > 0 && <span className="opacity-60"> · {Object.keys(policies).length}</span>}
              </summary>
              {/* Same stance chips for both intents — rentals: who the owner ACCEPTS as a
                  tenant; sales: who the seller will SELL to (nationality restrictions do
                  happen at sale too, just rarer than cash-only). One vocabulary, one match. */}
              {/* girao uses the RENT label and the rent vocabulary (see the setPolicyVocab
                  branch): whoever puts up the gira sum moves in, so "who will the owner
                  accept living here" is the same question it is for a tenant — and it is
                  sharper still when the owner is staying in the flat too. */}
              <p className="text-xs text-muted-foreground mb-1">{t('desk_terms_policies_legend')}</p>
              {/* MENU, not a cycle (owner ruling 2026-07-27) — see StanceChip's header in
                  MatchSuggestions.jsx. The cycle made "❗ only" four taps deep and hid the
                  vocabulary behind the interaction; this is the same chip with the same
                  colours, opened. One chip's menu at a time (openPolicy). */}
              <div className="flex flex-wrap gap-1.5">
                {policyVocab.map(f => (
                  <StanceChip key={f.code}
                    label={f.name_ka || f.name_en || f.code}
                    value={policies[f.code] || null}
                    options={stanceOptions()}
                    prompt={t('desk_policy_answer_prompt')}
                    open={openPolicy === f.code}
                    onToggle={() => setOpenPolicy(o => (o === f.code ? null : f.code))}
                    onPick={st => {
                      setPolicies(p => {
                        const out = { ...p }
                        if (st) out[f.code] = st; else delete out[f.code]
                        return out
                      })
                      setOpenPolicy(null)
                    }} />
                ))}
              </div>
            </details>
            </div>
          )}
          {giraoMode ? (
            <>
              {/* GIRAO CONDITIONS (0219). Four facts, in the order an agent asks them on
                  the phone: how much, for how long, and — the one that decides whether
                  the deal is liveable at all — is the owner staying in the flat.
                  Deliberately NOT the rental panel: there is no monthly rent here, so
                  first/last month, min-lease and deposit would all be nonsense fields. */}
              <div className="grid grid-cols-3 gap-3">
                <label className="text-xs text-muted-foreground">{t('desk_girao_amount')}
                  <input className={input} type="number" min="0" value={terms.girao_amount ?? ''}
                    onChange={e => setTerms(x => ({ ...x, girao_amount: e.target.value }))} />
                </label>
                <label className="text-xs text-muted-foreground">{t('desk_criteria_currency')}
                  <select className={input} value={terms.girao_currency ?? ''} onChange={e => setTerms(x => ({ ...x, girao_currency: e.target.value }))}>
                    <option value="">—</option><option>USD</option><option>GEL</option>
                  </select>
                </label>
                <label className="text-xs text-muted-foreground">{t('desk_girao_period')}
                  <input className={input} type="number" min="1" max="50" value={terms.girao_period_years ?? ''}
                    onChange={e => setTerms(x => ({ ...x, girao_period_years: e.target.value }))} />
                </label>
              </div>
              {/* Tri-state, and the blank stays blank: an unanswered "does the owner live
                  here too" must read as UNKNOWN, never as "no". Both platforms make this a
                  required field on their own forms, so an unknown here means we haven't
                  looked yet — which is exactly what it should say. */}
              <label className="text-xs text-muted-foreground">{t('desk_girao_owner_resides')}
                <select className={input} value={terms.girao_owner_resides ?? ''}
                  onChange={e => setTerms(x => ({ ...x, girao_owner_resides: e.target.value }))}>
                  <option value="">{t('desk_tristate_not_stated')}</option>
                  <option value="true">{t('desk_girao_owner_resides_yes')}</option>
                  <option value="false">{t('desk_girao_owner_resides_no')}</option>
                </select>
              </label>
              <p className="text-xs text-muted-foreground">{t('desk_girao_hint')}</p>
            </>
          ) : saleMode ? (
            <>
              {/* SALE CONDITIONS (0080): the 8-field panel. Tri-states stay '' = not stated. */}
              <div className="grid grid-cols-2 gap-3">
                {SALE_TRI.map(([k, labelKey]) => (
                  <label key={k} className="text-xs text-muted-foreground">{t(labelKey)}
                    <select className={input} value={terms[k] ?? ''} onChange={e => setTerms(x => ({ ...x, [k]: e.target.value }))}>
                      <option value="">{t('desk_tristate_not_stated')}</option>
                      <option value="true">{t(`${labelKey}_yes`)}</option>
                      <option value="false">{t(`${labelKey}_no`)}</option>
                    </select>
                  </label>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-3">
                {SALE_SELECTS.map(([k, labelKey, opts]) => (
                  <label key={k} className="text-xs text-muted-foreground">{t(labelKey)}
                    <select className={input} value={terms[k] ?? ''} onChange={e => setTerms(x => ({ ...x, [k]: e.target.value }))}>
                      <option value="">{t('desk_tristate_not_stated')}</option>
                      {/* 'none' collides across fields: furniture none = empty/built-ins,
                          parking none = no parking at all — disambiguated label key. */}
                      {opts.map(o => <option key={o} value={o}>{t(`desk_sale_opt_${k === 'parking_in_price' && o === 'none' ? 'no_parking' : o}`)}</option>)}
                    </select>
                  </label>
                ))}
              </div>
              {/* Prior bank appraisal — predicts exactly what a mortgage buyer can finance. */}
              {/* 1192 (2026-09-16): three answers agents actually got on calls — a figure, "appraised
                  but the owner doesn't know the amount", and "no appraisal". The last two used to have
                  nowhere to go but a comment. The figure is asked only once the answer is "appraised". */}
              <div className="grid grid-cols-3 gap-3" data-sale-appraisal>
                <label className="text-xs text-muted-foreground">{t('desk_sale_bank_appraisal')}
                  <select className={input} value={terms.bank_appraisal_status ?? ''}
                    onChange={e => setTerms(x => ({ ...x, bank_appraisal_status: e.target.value }))}>
                    <option value="">{t('desk_tristate_not_stated')}</option>
                    <option value="none">{t('desk_sale_appraisal_opt_none')}</option>
                    <option value="appraised">{t('desk_sale_appraisal_opt_appraised')}</option>
                  </select>
                </label>
                {terms.bank_appraisal_status === 'appraised' && (<>
                  <label className="text-xs text-muted-foreground">{t('desk_sale_appraisal_amount')}
                    <input className={input} type="number" min="0" value={terms.bank_appraisal_value ?? ''}
                      onChange={e => setTerms(x => ({ ...x, bank_appraisal_value: e.target.value }))} />
                  </label>
                  <label className="text-xs text-muted-foreground">{t('desk_criteria_currency')}
                    <select className={input} value={terms.bank_appraisal_currency ?? ''} onChange={e => setTerms(x => ({ ...x, bank_appraisal_currency: e.target.value }))}>
                      <option value="">—</option><option>USD</option><option>GEL</option>
                    </select>
                  </label>
                </>)}
              </div>
            </>
          ) : (
            /* Numeric rental deal terms — genuine numbers, kept as inputs (not chips). */
            <>
              {/* ── Move-in advance, in the EXTENSION deal panel's grammar (owner ruling
                  2026-07-27: "this part is bogus" — the same three facts rendered as
                  bureaucratic tri-state dropdowns here vs compact ticks there was two
                  grammars for one thing, the exact drift this week's work exists to kill).
                  One row: first-month chip · last-month chip · deposit amount+currency —
                  visually the gallery's [პირველი თვის][ბოლო თვის][დეპოზიტი ___] line.
                  The chips are StanceChip (the one mechanism for stance-bearing chips),
                  NOT checkboxes: this modal opens for a dozen unrelated reasons, so
                  unknown must stay the default and stay sayable — an unchecked box would
                  fabricate "not required" on every listing an agent glanced at. Two
                  options + clear covers exactly required / not required / never asked. */}
              <PrepayMonths list={terms.prepayments} set={list => setTerms(x => ({ ...x, prepayments: list }))} />
              <div className="flex flex-wrap items-end gap-2">
                {/* Simple TOGGLES, not stance menus (owner ruling 2026-07-27, second pass:
                    "select deselect, no need to dropdown") — the gallery's exact grammar.
                    On = the owner demands it (recorded true); off = nothing recorded
                    (clear, stays unknown). Explicit "asked and NOT required" is unsayable
                    here by that ruling — the simplification is deliberate, and the bill
                    line's "not everything asked" note keeps the blank honest. */}
                {RENT_TRI.map(([k, labelKey]) => {
                  // ⚠️ A RECORDED FIGURE TICKS THE DEPOSIT CHIP (owner ruling 2026-09-12:
                  // "when value is stored it mean they ask for deposit"). Without this the
                  // form contradicted itself on 116 rows recorded before `deposit_required`
                  // existed: the amount box was open and filled while the chip above it read
                  // unticked, which says "nobody asked" about a figure somebody wrote down.
                  // 1085 wrote the implication into those rows, so this is agreement with
                  // the data rather than a second opinion about it — and it keeps holding
                  // for a value typed into THIS form before the tick is pressed.
                  const on = terms[k] === 'true' || terms[k] === true
                    || (k === 'deposit_required'
                        && !!(terms.deposit_amount || terms.deposit_months || terms.deposit_basis))
                  return (
                    <button key={k} type="button"
                      onClick={() => setTerms(x => ({ ...x, [k]: on ? '' : 'true',
                        // ⚠️ TURNING THE DEPOSIT OFF CLEARS ITS FIGURE, and it has to: the
                        // chip is lit by a stored value, so clearing only the tick would
                        // leave `on` derived back to true and the button would look broken —
                        // a control that visibly does nothing. Saying "there is no deposit"
                        // while a 300$ deposit stays recorded is also not a state anybody
                        // means. Turning it ON clears nothing; only OFF does, and only for
                        // this one chip.
                        ...(k === 'deposit_required' && on
                          ? { deposit_basis: '', deposit_amount: '', deposit_months: '', deposit_currency: '' }
                          : {}) }))}
                      className={pill('none', { door: true, extra: on ? STANCE_STYLE.only : 'border-border text-muted-foreground hover:bg-muted' })}>
                      {on ? `${STANCE_ICON.allowed} ` : ''}{t(`${labelKey}_short`)}
                    </button>
                  )
                })}
                {/* ── DEPOSIT (1083, owner ruling 2026-09-12: "deposit required and
                    percentage from month like we have on cooperations fixed amount or
                    percentage") ────────────────────────────────────────────────────────
                    MEASURED before rebuilding: of the 489 adverts naming a deposit, only
                    115 give a money sum — which was all this box could hold. 87 state a
                    NUMBER OF MONTHS, 7 state HALF a month, and 278 say there IS one and
                    give no figure. Three of the four shapes had nowhere to go.
                    So the control is the same shape as the cooperation commission now: a
                    basis, then the value that basis implies.
                    ⚠️ „დეპოზიტი საჭიროა" — the 278-advert case, "there is a deposit, amount
                    not discussed" — is NOT drawn here. It is a RENT_TRI entry, so it renders
                    as a chip beside first-month and last-month, which is where it belongs:
                    those three are the move-in facts and read as one set. RENT_TRI is a
                    RENDER list (unlike TERMS_NUM, which is diff-only since 2026-07-27), so a
                    second control here drew the same label twice — owner-caught 2026-09-12.
                    Its tri-state grammar is what keeps an untouched form from fabricating
                    "no deposit" on every listing an agent merely opened. */}
                {/* Revealed by the chip (owner 2026-09-12: "only when its selected show
                    deposit box below") — an empty basis select and an empty amount box on
                    every rental form was three controls asking a question nobody had said
                    yes to yet.
                    ⚠️ OR-ed with the VALUES, not just the tick. 116 rows carry a
                    deposit_amount recorded before `deposit_required` existed (1083), and
                    their tick is null — gating on the tick alone would hide a recorded
                    deposit and make it uneditable. A stored value is its own reason to show
                    the box, exactly as a stored basis is. */}
                {(terms.deposit_required === 'true' || terms.deposit_required === true
                  || terms.deposit_basis || terms.deposit_amount || terms.deposit_months) && (
                <label className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <span>{t('desk_terms_deposit_short')}</span>
                  <select className="rounded-lg border border-input px-2 py-2 text-sm bg-background"
                    value={terms.deposit_basis ?? ''}
                    onChange={e => setTerms(x => ({ ...x, deposit_basis: e.target.value }))}>
                    <option value="">—</option>
                    <option value="amount">{t('desk_terms_deposit_basis_amount')}</option>
                    <option value="percent_of_month">{t('desk_terms_deposit_basis_percent')}</option>
                  </select>
                  {terms.deposit_basis === 'percent_of_month' ? (
                    <>
                      {/* 🔑 THE AGENT TYPES A PERCENT; THE COLUMN STORES MONTHS. 50 → 0.5,
                          because `deposit_months` is multiplied by the rent downstream and
                          a 50 left in it would bill fifty months of rent. The conversion
                          lives here, in one place, and the equivalent is shown back so the
                          agent can see what they just said. */}
                      <input className={`${input} w-20`} type="number" min="0" step="5"
                        value={terms.deposit_months === '' || terms.deposit_months == null
                          ? '' : String(Math.round(Number(terms.deposit_months) * 1000) / 10)}
                        onChange={e => setTerms(x => ({ ...x,
                          deposit_months: e.target.value === '' ? '' : String(Number(e.target.value) / 100) }))} />
                      <span>%</span>
                      {Number(terms.deposit_months) > 0 && (
                        <span className="text-muted-foreground">
                          = {t('desk_terms_summary_deposit').replace('{n}', String(Number(terms.deposit_months)))}
                        </span>
                      )}
                    </>
                  ) : (
                    <>
                      <input className={`${input} w-28`} type="number" min="0" value={terms.deposit_amount ?? ''}
                        onChange={e => setTerms(x => ({ ...x, deposit_amount: e.target.value }))} />
                      <select className="rounded-lg border border-input px-2 py-2 text-sm bg-background" value={terms.deposit_currency ?? ''}
                        onChange={e => setTerms(x => ({ ...x, deposit_currency: e.target.value }))}>
                        <option value="">—</option><option>USD</option><option>GEL</option>
                      </select>
                    </>
                  )}
                </label>
                )}
                {/* THE PET FEE MOVED (owner ruling 2026-09-07: "are pets allowed by a deposit or
                    not at all?"). It is one question with three answers, and the deposit is a
                    CONDITION on the permission — so it is captured beside the pets answer in
                    the restrictions panel above, and deliberately NOT here.
                    🔴 It lived here for one day and that was long enough to see why: with the
                    permission in one panel and the price in another, 7 of the 23 listings
                    charging a pet fee also carried a rule refusing pets outright. Two panels
                    asking one question is how a flat ends up priced for an animal it does not
                    accept. pet_deposit_extra stays in TERMS_NUM for the save diff so a legacy
                    value still round-trips; nothing here EDITS it any more. */}
              </div>
              {/* The bill as one sentence — always visible, like the gallery's "წინასწარ:"
                  line, but honest about blanks: an unrecorded bill reads "—", never 0.
                  Derived live, never stored (lib/tenancyTerms.js). */}
              <MoveInPaymentLine terms={terms} rent={rent} rentCurrency={rentCurrency} />
              {/* Owner ruling 2026-07-27 (second pass): min lease = quick-select (the market
                  speaks in 1/3/6/12 — GPT Q1: "ერთი წლით მაინც" is the common phrasing);
                  pet-deposit input REMOVED — one deposit box is enough. ⚠️ REVERSED by the
                  owner 2026-09-07: the box is back, ABOVE, and it is now the whole answer.
                  What July removed was an always-visible field sitting empty on every
                  listing; what replaced it is a field that only means anything once someone
                  has said there is a fee. Kept rather than deleted because the July reasoning
                  is still why it is ONE box borrowing the deposit currency, not two. */}
              <div className="grid grid-cols-3 gap-3">
                <label className="text-xs text-muted-foreground">{t('desk_terms_min_lease')}
                  <select className={input} value={terms.min_lease_months ?? ''}
                    onChange={e => setTerms(x => ({ ...x, min_lease_months: e.target.value }))}>
                    <option value="">—</option>
                    {/* Owner ruling 2026-08-22: "use below 6 month as short term — lets add
                        there an option below 6 month and we check that one there means short
                        term." SHORT-TERM IS NOT ITS OWN FIELD; it is a READING of this one —
                        under six is a short let, six is not. The option stores 0, meaning no
                        committed minimum at all, which is exactly what a daily or weekly let is.
                        ⚠️ The ruling also dissolved a real blocker rather than working around
                        it: listing_terms.short_term_ok exists but amend_listing_terms cannot
                        write it (B321's disease), and the alternative was editing an
                        8,464-character live gateway function. THIS field the gateway already
                        accepts. ⚠️ 0 must stay in the known-values list below, or a saved
                        "under 6" would reappear as a legacy nonstandard value. */}
                    <option value="0">{t('desk_terms_min_lease_under6')}</option>
                    {/* a legacy nonstandard value (e.g. 24) still shows rather than silently
                        snapping to blank — hiding an active term is the worse lie */}
                    {[1, 3, 6, 12].concat(
                      terms.min_lease_months && ![0, 1, 3, 6, 12].includes(Number(terms.min_lease_months))
                        ? [Number(terms.min_lease_months)] : []
                    ).map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </label>
                <label className="text-xs text-muted-foreground">{t('desk_terms_max_occupants')}
                  <input className={input} type="number" min="0" value={terms.max_occupants ?? ''}
                    onChange={e => setTerms(x => ({ ...x, max_occupants: e.target.value }))} />
                </label>
              </div>
            </>
          )}
          {/* AVAILABLE FROM — the one place move-in timing is asked, for sale and rent alike.
              Outside the sale/rent branch on purpose: "when can possession begin" is a
              condition the owner states either way, and since 0220 it is THE model for it —
              the `handover` duration select that used to sit above (sale) and beside it
              (rent, for one morning) is gone, because a duration silently becomes a
              different fact every day it goes unread. A DATE is the fact; "handover after
              signing" and the portals' "vacates in N weeks" are renderings derived from it.
              Recorded as an observation with provenance, so a second agent's date no longer
              overwrites the first's — the two resolve as `conflicting` until someone
              confirms. Needs only a property id (prop or terms-GET fallback): agents pass
              the rep gate server-side, agentless viewers ride the verified-admin path. */}
          {pid && (
            <label className="text-xs text-muted-foreground block">{t('desk_avail_facet_title')}
              <DateInput className={`flex items-center gap-0.5 ${input}`} value={availFrom}
                onChange={v => setAvailFrom(v)} />
              <span className="block text-xs text-muted-foreground mt-1">{t('desk_avail_shared_note')}</span>
            </label>
          )}
          <label className="text-xs text-muted-foreground block">{t('desk_terms_promised_improvements')}
            <input className={input} value={terms.promised_improvements ?? ''} onChange={e => setTerms(x => ({ ...x, promised_improvements: e.target.value }))} />
          </label>
          {/* The pair of promised_improvements (0010): "as shown, nothing outstanding".
              The gallery captures it, so the Desk has to be able to see and change it —
              otherwise an owner's answer is editable on one surface only. */}
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <input type="checkbox" checked={terms.is_final_condition === true}
              onChange={e => setTerms(x => ({ ...x, is_final_condition: e.target.checked }))} />
            {t('desk_terms_is_final_condition')}
          </label>
          {/* free-text tenant_pref removed 2026-07-11 — unmatchable; the stance chips above
              ARE the owner's tenant preferences now. Old saved notes still display read-only
              so recorded owner words are never silently lost. */}
          {terms.tenant_pref_notes && (
            <p className="text-xs text-muted-foreground">{t('desk_terms_tenant_prefs')}: <span className="italic">{terms.tenant_pref_notes}</span></p>
          )}
          {err && <p className="text-sm text-destructive"><Icon name="alert-triangle" /> {err}</p>}
          <div className="flex justify-end gap-2">
            <button className={btn('ghost')} onClick={onClose}>{t('desk_client_cancel')}</button>
            {autoSaveState === 'error' ? (
              <button className={btn('primary')} disabled={saving}
                onClick={() => { failedAutoSaveRef.current = null; handleRecord({ automatic: true }) }}>
                {t('desk_coop_autosave_retry')}
              </button>
            ) : (
              <span className="inline-flex items-center px-3 text-sm text-muted-foreground" aria-live="polite">
                {autoSaveState === 'saving' || autoSaveState === 'pending'
                  ? t('desk_coop_autosave_saving')
                  : t('desk_coop_autosave_saved')}
              </span>
            )}
          </div>
        </div>
      )}
    </>
  )
  if (inline) return termsBody
  return (
    <Modal title={t(saleMode ? 'desk_sale_terms_title' : giraoMode ? 'desk_girao_terms_title' : 'desk_terms_facet_title')}
      subtitle={row ? tLog('desk_terms_version_badge', { n: row.version ?? 1 }) : t('desk_terms_none_recorded')}
      onClose={onClose}>
      {termsBody}
    </Modal>
  )
}

// ─── B20 timeline spine — the property entity's event feed ───────────────────
// The projection (evLabel/evTime/burst collapsing/paging) MOVED to
// ./PropertyTimeline.jsx on 2026-07-27 so the lineage modal can render the same
// stream for a whole identity cluster. This file keeps only the <Facet> chrome:
// one renderer, two containers. Nothing about the single-property behaviour
// changed — the hook takes one id exactly as before.
function PropertyTimeline({ propertyId, refreshKey, pm = null }) {
  const feed = usePropertyEvents(propertyId, refreshKey, { limit: 50 })
  return (
    <Facet title={t('desk_timeline_facet_title')} defaultOpen={false} num={pm?.num} labelKey={pm?.labelKey}
      hint={t('desk_timeline_facet_hint')}
      summary={feed.feed ? tLog('desk_timeline_events_count', { n: feed.feed.events.length, '+': feed.feed.has_more ? '+' : '' }) : '…'}>
      <TimelineBody {...feed} />
    </Facet>
  )
}

// ── My Version (agent property layer v1, core migration 0064) ────────────────
// The agent's own version of a property: own claim > pinned endorsement >
// canonical. Per fact: 🖊 own / ✓ accepted / ○ unendorsed, ⚠ when canonical
// drifted off what the agent asserted/pinned. Endorsing is ADOPTION — it never
// raises anyone's confidence; it just makes the value publishable AS this
// agent's belief later (step 3). Notes are private (author + admin only).
// Private notes — author-only (+admin), never on the shared timeline, never published.
// (Was "MY VERSION": the fact endorse/accept UI it used to carry is now redundant —
// the property card's editable form + Record already make a value yours, and drift
// warnings moved inline onto those fields. Notes are the one thing the form can't hold.)
function PrivateNotesPanel({ propertyId, agentId, pm = null }) {
  const [notes, setNotes] = useState(null)
  const [err, setErr] = useState(null)
  const [busy, setBusy] = useState(false)
  const [noteText, setNoteText] = useState('')
  const [refresh, setRefresh] = useState(0)
  useEffect(() => {
    // agentId optional: an admin has no roster row and would otherwise never load their notes.
    let gone = false
    getMyPropertyVersion(propertyId, agentId)
      .then(d => { if (!gone) { setNotes(d.notes || []); setErr(null) } })
      .catch(e => { if (!gone) setErr(e.message) })
    return () => { gone = true }
  }, [propertyId, agentId, refresh])
  if (!agentId) return null

  async function act(fn) {
    setBusy(true)
    try { await fn(); setRefresh(r => r + 1) }
    catch (e) { setErr(e.message) }
    finally { setBusy(false) }
  }

  return (
    <Facet title={t('desk_notes_facet_title')} num={pm?.num} labelKey={pm?.labelKey}
      hint={t('desk_notes_facet_hint')}
      summary={notes ? tLog('desk_notes_count', { n: notes.length, s: notes.length === 1 ? '' : 's' }) : '…'}
      defaultOpen={false}>
      {err && <p className="text-sm text-destructive"><Icon name="alert-triangle" /> {err}</p>}
      <ul className="text-xs text-muted-foreground space-y-0.5">
        {(notes || []).map(n => (
          <li key={n.id} className="flex items-start gap-2">
            <span className="flex-1 whitespace-pre-wrap">{n.body}</span>
            <button className="hover:text-destructive" title={t('desk_notes_delete')} disabled={busy}
              onClick={() => act(() => deletePropertyNote(n.id, agentId))}>✕</button>
          </li>
        ))}
      </ul>
      <div className="flex gap-1 mt-1">
        <input value={noteText} onChange={e => setNoteText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && noteText.trim()) { act(async () => { await addPropertyNote(propertyId, agentId, noteText.trim()); setNoteText('') }) } }}
          placeholder={t('desk_notes_add_ph')}
          className="flex-1 rounded border border-input px-2 py-1 text-xs bg-background" />
        <button className={btn('ghost')} disabled={busy || !noteText.trim()}
          onClick={() => act(async () => { await addPropertyNote(propertyId, agentId, noteText.trim()); setNoteText('') })}>{t('desk_notes_add_button')}</button>
      </div>
    </Facet>
  )
}

// ── Assumed values (core 1228) ───────────────────────────────────────────────
// Owner 2026-09-16: "whatever you assume about numbers or currency must be verified by data ...
// better to be marked as most likely meant, assumed". Each row shows what the source SAID, what we
// take it to mean and why; an agent confirms, or rejects and the stated value comes back.
function assumedValueText(v) {
  if (!v) return ''
  const n = v.price ?? v.amount
  return `${n == null ? '' : Number(n).toLocaleString()} ${v.currency || ''}`.trim()
}

function AssumptionsFacet({ propertyId, pm = null }) {
  const [rows, setRows] = useState(null)
  const [err, setErr] = useState(null)
  const [busy, setBusy] = useState(false)
  const [tick, setTick] = useState(0)
  useEffect(() => {
    let gone = false
    getPropertyAssumptions(propertyId)
      .then(d => { if (!gone) { setRows(d.assumptions || []); setErr(null) } })
      .catch(e => { if (!gone) setErr(e.message) })
    return () => { gone = true }
  }, [propertyId, tick])
  if (!rows || !rows.length) return err ? <p className="text-sm text-destructive"><Icon name="alert-triangle" /> {err}</p> : null

  async function decide(id, decision) {
    setBusy(true)
    try { await decideAssumption(id, decision); setTick(x => x + 1) }
    catch (e) { setErr(e.message) }
    finally { setBusy(false) }
  }
  const open = rows.filter(r => r.status === 'assumed').length
  return (
    <Facet title={t('desk_assumed_facet_title')} hint={t('desk_assumed_facet_hint')} num={pm?.num} labelKey={pm?.labelKey} summaryTone={open > 0 ? 'warn' : null}
      summary={tLog('desk_assumed_summary', { n: open })} defaultOpen={open > 0}>
      {err && <p className="text-sm text-destructive"><Icon name="alert-triangle" /> {err}</p>}
      <ul className="space-y-1.5" data-assumptions>
        {rows.map(r => (
          <li key={r.id} className="rounded border border-border px-2 py-1.5 text-xs">
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className="font-medium">{t(`desk_assumed_field_${r.field}`)}</span>
              <span>{t('desk_assumed_most_likely')} <b>{assumedValueText(r.assumed_value)}</b></span>
              <span className="text-muted-foreground">{t('desk_assumed_source_said')} {assumedValueText(r.stated_value)}</span>
            </div>
            <p className="text-muted-foreground mt-0.5">{r.reason}</p>
            {r.quote && <p className="text-muted-foreground mt-0.5 italic break-words">„{r.quote}"</p>}
            <div className="flex items-center gap-1.5 mt-1">
              {r.status === 'assumed' ? (<>
                <button type="button" className={pill('ok', { door: true, sm: true })} disabled={busy}
                  onClick={() => decide(r.id, 'confirmed')}><Icon name="check" /> {t('desk_assumed_confirm')}</button>
                <button type="button" className={pill('warn', { door: true, sm: true })} disabled={busy}
                  onClick={() => decide(r.id, 'rejected')}><Icon name="x" /> {t('desk_assumed_reject')}</button>
              </>) : (
                <span className="text-muted-foreground">{t(`desk_assumed_status_${r.status}`)}</span>
              )}
            </div>
          </li>
        ))}
      </ul>
    </Facet>
  )
}

// ── Promises (core 1227) ─────────────────────────────────────────────────────
// Owner 2026-09-16: "this is so common we need to have sections on property future promise
// included in current price not included and list". A promise is NOT a feature: "the balcony
// WILL BE tiled" recorded as present would say something exists that does not. So it is its own
// list — what, who delivers it (flat/building/complex/area), when, how far along, and whether the
// current price includes it. `unstated` is the honest default: silence about cost is not "included".
const PROMISE_SCOPES = ['flat', 'building', 'complex', 'area']
const PROMISE_STATES = ['promised', 'in_progress', 'done', 'not_kept']
const PROMISE_PRICE = [
  { v: 'included', icon: 'check', tone: 'ok' },
  { v: 'extra',    icon: 'plus',  tone: 'warn' },
  { v: 'unstated', icon: 'help-circle', tone: 'neutral' },
]
const PROMISE_CURRENCIES = ['USD', 'GEL', 'EUR']
const EMPTY_PROMISE = { what: '', scope: 'flat', due_text: '', in_price: 'unstated', extra_amount: '', extra_currency: 'USD' }

function PromisesFacet({ propertyId, pm = null }) {
  const [rows, setRows] = useState(null)
  const [err, setErr] = useState(null)
  const [busy, setBusy] = useState(false)
  const [draft, setDraft] = useState(EMPTY_PROMISE)
  useEffect(() => {
    let gone = false
    getPropertyPromises(propertyId)
      .then(d => { if (!gone) { setRows(d.promises || []); setErr(null) } })
      .catch(e => { if (!gone) setErr(e.message) })
    return () => { gone = true }
  }, [propertyId])

  async function send(ops) {
    setBusy(true)
    try { const d = await amendPropertyPromises(propertyId, ops); setRows(d.promises || []); setErr(null); return true }
    catch (e) { setErr(e.message); return false }
    finally { setBusy(false) }
  }
  const set = (id, field, value) => send([{ op: 'set', id, field, value }])
  async function add() {
    const v = { what: draft.what.trim(), scope: draft.scope, due_text: draft.due_text.trim() || null, in_price: draft.in_price }
    if (draft.in_price === 'extra' && draft.extra_amount !== '') { v.extra_amount = Number(draft.extra_amount); v.extra_currency = draft.extra_currency }
    if (await send([{ op: 'add', value: v }])) setDraft(EMPTY_PROMISE)
  }

  const list = rows || []
  const nIncl = list.filter(r => r.in_price === 'included').length
  const nExtra = list.filter(r => r.in_price === 'extra').length
  const field = 'rounded border border-input px-1 py-0.5 bg-background'
  return (
    <Facet title={t('desk_promises_facet_title')} hint={t('desk_promises_facet_hint')} defaultOpen={false} num={pm?.num} labelKey={pm?.labelKey}
      summary={rows ? tLog('desk_promises_summary', { n: list.length, incl: nIncl, extra: nExtra }) : '…'}>
      {err && <p className="text-sm text-destructive"><Icon name="alert-triangle" /> {err}</p>}
      <ul className="space-y-1.5" data-promises>
        {list.map(r => (
          <li key={r.id} className="rounded border border-border px-2 py-1.5 text-xs">
            <div className="flex items-start gap-2">
              <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground shrink-0">{t(`desk_promise_scope_${r.scope}`)}</span>
              <span className="flex-1 min-w-0 whitespace-pre-wrap break-words" title={r.quote || undefined}>
                {r.what}
                {r.due_text && <span className="text-muted-foreground"> · {r.due_text}</span>}
                {r.source_kind === 'ai' && <span className="text-muted-foreground"> · AI</span>}
              </span>
              <button type="button" className="hover:text-destructive text-muted-foreground" disabled={busy}
                title={t('desk_promise_remove')} onClick={() => send([{ op: 'retract', id: r.id }])}>✕</button>
            </div>
            <div className="flex flex-wrap items-center gap-1.5 mt-1">
              {PROMISE_PRICE.map(o => (
                <button key={o.v} type="button" disabled={busy}
                  className={pill(r.in_price === o.v ? o.tone : 'neutral', { door: true, sm: true })}
                  onClick={() => r.in_price !== o.v && set(r.id, 'in_price', o.v)}>
                  <Icon name={o.icon} /> {t(`desk_promise_price_${o.v}`)}
                </button>
              ))}
              {r.in_price === 'extra' && (
                <span className="inline-flex items-center gap-1">
                  <input type="number" min="0" className={`w-20 ${field}`}
                    defaultValue={r.extra_amount ?? ''} placeholder={t('desk_promise_extra_amount')}
                    onBlur={e => { const v = e.target.value; if (String(r.extra_amount ?? '') !== v) set(r.id, 'extra_amount', v === '' ? null : Number(v)) }} />
                  <select className={field} value={r.extra_currency || 'USD'}
                    onChange={e => set(r.id, 'extra_currency', e.target.value)}>
                    {PROMISE_CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </span>
              )}
              <select className={`ml-auto ${field}`} value={r.state} disabled={busy}
                onChange={e => set(r.id, 'state', e.target.value)}>
                {PROMISE_STATES.map(s => <option key={s} value={s}>{t(`desk_promise_state_${s}`)}</option>)}
              </select>
            </div>
          </li>
        ))}
      </ul>
      <div className="mt-2 grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-1.5 text-xs">
        <input value={draft.what} onChange={e => setDraft(d => ({ ...d, what: e.target.value }))}
          placeholder={t('desk_promise_what_ph')} className="rounded border border-input px-2 py-1 bg-background" />
        <select value={draft.scope} onChange={e => setDraft(d => ({ ...d, scope: e.target.value }))} className={field}>
          {PROMISE_SCOPES.map(s => <option key={s} value={s}>{t(`desk_promise_scope_${s}`)}</option>)}
        </select>
        <input value={draft.due_text} onChange={e => setDraft(d => ({ ...d, due_text: e.target.value }))}
          placeholder={t('desk_promise_due_ph')} className="rounded border border-input px-2 py-1 bg-background sm:w-36" />
      </div>
      <div className="flex flex-wrap items-center gap-1.5 mt-1.5 text-xs">
        {PROMISE_PRICE.map(o => (
          <button key={o.v} type="button"
            className={pill(draft.in_price === o.v ? o.tone : 'neutral', { door: true, sm: true })}
            onClick={() => setDraft(d => ({ ...d, in_price: o.v }))}>
            <Icon name={o.icon} /> {t(`desk_promise_price_${o.v}`)}
          </button>
        ))}
        {draft.in_price === 'extra' && (<>
          <input type="number" min="0" value={draft.extra_amount} placeholder={t('desk_promise_extra_amount')}
            onChange={e => setDraft(d => ({ ...d, extra_amount: e.target.value }))} className={`w-20 ${field}`} />
          <select value={draft.extra_currency} onChange={e => setDraft(d => ({ ...d, extra_currency: e.target.value }))} className={field}>
            {PROMISE_CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </>)}
        <button type="button" className={`${btn('ghost')} ml-auto`} disabled={busy || !draft.what.trim()} onClick={add}>
          {t('desk_promise_add')}
        </button>
      </div>
    </Facet>
  )
}

// ── POOL (step 3, core migration 0065) ────────────────────────────────────────
// One card per physical property; agents' publications are OFFERS on it. The
// card shows the MARKET VERSION (consensus of offers; ties shown disputed,
// never silently picked). Offers = table, cheapest first (named policy);
// "your side" = price × commission% × 50/50 split — informs, never steers.
// View switch inside the SAME modal: market ⇄ a differing agent's version,
// differing fields subtly marked. Agents with identical versions get no entry.

// Pool feed (PoolCardModal/PoolAutoPublishSettings/PoolFeed + poolVal/poolFactLine)
// — moved to ./desk/PoolFeed.jsx (plan §3 decomposition, verbatim).

// Leads feed (LeadContactDensity, cooling, LeadsFeed) — moved to
// ./desk/LeadsFeed.jsx (plan §3 decomposition, verbatim).

// Part C2 (2026-07-14): availability date — rep-only ("owner says free from…").
// A dormant property auto-reactivates 7 days before this date with a fresh
// 60-day clock (the wake moment counts as activity server-side). Clearing the
// date is allowed. Server enforces the representation gate; this UI only
// renders where the rep's workbench already renders.
// B201 (2026-07-27): the date is a RESOLVED value over observations, so it can be
// resolved from sources that disagree — and until now the edit field showed only the
// winner. That hid the disagreement at the one moment it is cheap to settle: the agent
// is already looking at the field, and often already on the phone with the owner.
// Source kind is resolved through t() rather than labels.js's sourceKindLabel purely to
// avoid an import this file is mid-refactor on; the enum is closed by the 0220 CHECK
// constraint and every member has a key, with a raw-value fallback for safety.
const availSourceLabel = k => {
  if (!k) return ''
  const s = t(`desk_source_kind_${k}`)
  return s === `desk_source_kind_${k}` ? String(k).replace(/_/g, ' ') : s
}
function AvailabilityFacet({ propertyId, agentId, pm = null }) {
  const [val, setVal] = useState('')
  const [loaded, setLoaded] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const [saved, setSaved] = useState(false)
  const [certainty, setCertainty] = useState(null)
  const [claims, setClaims] = useState(null)
  useEffect(() => {
    if (!propertyId) return
    let gone = false
    getDormancy([propertyId])
      .then(m => {
        if (gone) return
        const d = m.get(propertyId)
        setVal(d?.available_from || ''); setLoaded(true)
        setCertainty(d?.availability_certainty || null); setClaims(d?.availability_claims || null)
      })
      .catch(() => { if (!gone) setLoaded(true) })
    return () => { gone = true }
  }, [propertyId])
  // agentId no longer required (2026-07-21 owner ruling): an agentless viewer saves via
  // their scheduler session token, which the server re-verifies as a real admin.
  if (!propertyId) return null
  async function save(next) {
    setBusy(true); setErr(null); setSaved(false)
    try {
      let adminToken = null
      if (!agentId) {
        const { data: { session } } = await supabase.auth.getSession()
        adminToken = session?.access_token || null
      }
      await setPropertyAvailability(propertyId, agentId, next || null, adminToken)
      setSaved(true)
      // The save wrote one more observation; the conflict it may have created (or
      // settled) is only knowable after re-resolving, so re-read rather than guess.
      getDormancy([propertyId]).then(m => {
        const d = m.get(propertyId)
        setCertainty(d?.availability_certainty || null); setClaims(d?.availability_claims || null)
      }).catch(() => {})
    }
    catch (e) { setErr(e.message) } finally { setBusy(false) }
  }
  const disputed = certainty === 'conflicting'
  return (
    <Facet title={t('desk_avail_facet_title')} hint={t('desk_avail_facet_hint')} num={pm?.num} labelKey={pm?.labelKey} summaryTone={disputed ? 'warn' : null}
      summary={loaded ? (val ? (disputed ? `${val} · ${t('desk_avail_conflicting')}` : val) : '—') : '…'}
      defaultOpen={false}>
      <div className="flex items-center gap-2 text-xs">
        <DateInput value={val} disabled={busy || !loaded}
          className="flex items-center gap-0.5 rounded border border-input px-2 py-1 bg-background"
          onChange={v => { setVal(v); if (v) save(v) }} />
        {val && <button className={btn('ghost')} disabled={busy}
          onClick={() => { setVal(''); save(null) }}>✕</button>}
        {saved && <span className="text-green-600 dark:text-green-400"><Icon name="check" /></span>}
        {err && <span className="text-destructive"><Icon name="alert-triangle" /> {err}</span>}
      </div>
      {disputed && (
        <div className="mt-1.5 rounded border border-amber-500/40 bg-amber-500/5 px-2 py-1.5">
          <p className="text-xs font-medium text-amber-700 dark:text-amber-400">
            <Icon name="alert-triangle" /> {t('desk_avail_conflicting')}
          </p>
          <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
            {(claims || []).map(c => (
              <li key={`${c.available_from}|${c.source_kind}`} className="flex gap-1.5">
                <span className="tabular-nums font-medium">{c.available_from}</span>
                <span>· {availSourceLabel(c.source_kind)}</span>
              </li>
            ))}
          </ul>
          <p className="mt-1 text-xs text-muted-foreground">{t('desk_avail_conflict_note')}</p>
        </div>
      )}
      <p className="text-xs text-muted-foreground mt-1">{t('desk_avail_wake_note')}</p>
    </Facet>
  )
}

// ── Public page (B187, 0135) — the /p/:id opt-in toggle ────────────────────────
// The public site's writer: publish_property_public via the same representation-
// gated door as the pool. Commission is deliberately absent (0135: the public
// offer is just price+intent). The link is shown whenever ANYONE has an active
// public publication — the page renders for the property, not per agent.
function PublicPageFacet({ propertyId, agentId }) {
  const [st, setSt] = useState(null)     // public-status payload
  const [price, setPrice] = useState('')
  const [ccy, setCcy] = useState('USD')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  useEffect(() => {
    if (!propertyId) return
    let gone = false
    getPublicStatus(propertyId, agentId).then(d => {
      if (gone) return
      setSt(d)
      setPrice(d.suggested_price != null ? String(d.suggested_price) : '')
      setCcy(d.suggested_currency || 'USD')
    }).catch(e => { if (!gone) setErr(e.message) })
    return () => { gone = true }
  }, [propertyId, agentId])
  if (!propertyId || !agentId) return null
  async function toggle() {
    setBusy(true); setErr(null)
    try {
      if (st?.published_by_me) {
        await unpublishPropertyPublic(propertyId, agentId)
      } else {
        await publishPropertyPublic(propertyId, agentId,
          { price: Number(price), currency: ccy, intent: st?.suggested_intent || null })
      }
      setSt(await getPublicStatus(propertyId, agentId))
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }
  const mine = !!st?.published_by_me
  return (
    <Facet title={t('desk_public_facet_title')} hint={t('desk_public_facet_hint')}
      summary={st ? (mine ? t('desk_public_on') : (st.published_by_anyone ? t('desk_public_by_other') : t('desk_public_off'))) : '…'}
      defaultOpen={false}>
      {!mine && (
        <div className="flex items-center gap-2 text-xs mb-1.5">
          <input type="number" min="1" value={price} disabled={busy}
            placeholder={t('desk_public_price_ph')}
            className="w-28 rounded border border-input px-2 py-1 bg-background"
            onChange={e => setPrice(e.target.value)} />
          <select value={ccy} disabled={busy} onChange={e => setCcy(e.target.value)}
            className="rounded border border-input px-1.5 py-1 bg-background">
            {['USD', 'GEL', 'EUR'].map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      )}
      {mine && st?.offer && (
        <p className="text-xs text-muted-foreground mb-1.5">
          <Money amount={st.offer.price} currency={st.offer.currency} />
          {st.offer.intent ? ` · ${st.offer.intent}` : ''} · {timeAgo(st.offer.published_at)}
        </p>
      )}
      <div className="flex items-center gap-2">
        <button className={btn(mine ? 'ghost' : 'primary')} disabled={busy || (!mine && !(Number(price) > 0))}
          onClick={toggle}>
          {busy ? '…' : mine ? t('desk_public_unpublish') : t('desk_public_publish')}
        </button>
        {(mine || st?.published_by_anyone) && st?.url && (
          <a className="text-xs underline text-muted-foreground hover:text-foreground"
            href={st.url} target="_blank" rel="noreferrer">{t('desk_public_view')} ↗</a>
        )}
      </div>
      {err && <p className="text-xs text-destructive mt-1"><Icon name="alert-triangle" /> {err}</p>}
      <p className="text-xs text-muted-foreground mt-1">{t('desk_public_note')}</p>
    </Facet>
  )
}

// Read-only "available from" row for the rental-terms / sale-conditions card (2026-07-21).
// Renders NOTHING when no date is set — an empty availability is not a condition the owner
// stated, and a permanent "—" row in a card that is otherwise all real statements is noise.
// `refreshKey` is the card's termsRefresh counter, so closing the editor after setting a
// date re-reads it here instead of showing a stale blank until the modal is reopened.
function AvailabilityLine({ propertyId, refreshKey }) {
  const [val, setVal] = useState('')
  const [disputed, setDisputed] = useState(false)
  useEffect(() => {
    if (!propertyId) return
    let gone = false
    getDormancy([propertyId])
      .then(m => {
        if (gone) return
        const d = m.get(propertyId)
        setVal(d?.available_from || ''); setDisputed(d?.availability_certainty === 'conflicting')
      })
      .catch(() => { if (!gone) { setVal(''); setDisputed(false) } })
    return () => { gone = true }
  }, [propertyId, refreshKey])
  if (!val) return null
  // A disputed date rendered bare is worse than no date — it reads as settled, and the
  // agent quotes it to a client. The marker is the whole point of resolving certainty.
  return (
    <p className="mt-3 pt-3 border-t border-border/40 text-sm">
      <span className="text-xs font-semibold text-muted-foreground">{t('desk_avail_facet_title')}</span>
      {' '}<span className="font-medium tabular-nums">{val}</span>
      {disputed && <span className="ml-1.5 text-xs text-amber-700 dark:text-amber-400">
        <Icon name="alert-triangle" /> {t('desk_avail_conflicting')}</span>}
    </p>
  )
}

// ── Inventory (archive) controls — unified property card ─────────────────────
// "Am I working this property?" Archive = flip the represents_property edge to
// dismissed (0064 set_property_inventory 'remove') — soft, reversible, touches
// nothing else. Restore = flip it back. The property row, claims, publications
// and history are all untouched; this is purely the agent's own worklist state.
function InventoryControls({ propertyId, agentId }) {
  const [inInv, setInInv] = useState(null)   // null = loading
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  useEffect(() => {
    let gone = false
    getMyPropertyVersion(propertyId, agentId)
      .then(d => { if (!gone) setInInv(!!d.in_inventory) })
      .catch(() => { if (!gone) setInInv(false) })
    return () => { gone = true }
  }, [propertyId, agentId])

  async function flip() {
    setBusy(true); setErr(null)
    try {
      await setPropertyInventory(propertyId, agentId, inInv ? 'remove' : 'add')
      setInInv(v => !v)
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  if (inInv === null) return null
  return (
    <div className="flex items-center gap-2 pt-1">
      {err && <span className="text-xs text-destructive"><Icon name="alert-triangle" /> {err}</span>}
      <button className={`${btn('ghost')} ${inInv ? 'text-muted-foreground' : 'text-emerald-600 dark:text-emerald-400'}`}
        disabled={busy} onClick={flip}
        title={inInv ? t('desk_archive_active_tooltip') : t('desk_archive_restore_tooltip')}>
        {inInv ? t('desk_archive_button') : t('desk_archive_restore_button')}
      </button>
    </div>
  )
}

// ── Possible duplicates (property identity graph v1, core migration 0063) ────
// "Is this the same physical apartment as that one?" — the human review surface.
// A verdict is EVIDENCE (reversible, judge-stamped), never a merge: nothing is
// fused or deleted; the server resolver maintains same_as/different_from edges
// and the cluster below is the only collapse — explained by evidence sentences,
// never scores (core ARCHITECTURE_INVARIANTS: forbidden compressions).
function PropertyDuplicates({ propertyId, agentId, agentName, privileged = false, pm = null }) {
  const [data, setData] = useState(null)   // { property, candidates, cluster, verdicts }
  const [err, setErr] = useState(null)
  const [busy, setBusy] = useState(null)   // candidate id mid-verdict
  const [refresh, setRefresh] = useState(0)
  useEffect(() => {
    let gone = false
    getPropertyDuplicates(propertyId)
      .then(d => { if (!gone) { setData(d); setErr(null) } })
      .catch(e => { if (!gone) setErr(e.message) })
    return () => { gone = true }
  }, [propertyId, refresh])

  async function judge(cand, verdict) {
    // Dual identity (mirrors DedupSection / PartyDuplicates): admins have no
    // agents-table row (the Tako/Mariam hijack), so fall back to their own
    // scheduler session token, server-reverified. Fixes the same admin gap the
    // party card had (2026-07-19).
    let adminToken = null
    if (!agentId) {
      if (!privileged) { setErr(t('desk_dup_no_identity')); return }
      const { data: { session: fresh } } = await supabase.auth.getSession()
      adminToken = fresh?.access_token
      if (!adminToken) { setErr(t('desk_dup_no_identity')); return }
    }
    setBusy(cand.id)
    try {
      await recordPropertyVerdict({
        propertyA: propertyId, propertyB: cand.other.id, verdict,
        agent: agentId ? { id: agentId, name: agentName || null } : undefined,
        adminToken,
      })
      setRefresh(r => r + 1)
    } catch (e) { setErr(e.message) } finally { setBusy(null) }
  }

  const cands = data?.candidates || []
  const members = data?.cluster?.members || []
  const pending = cands.filter(c => c.status === 'pending')
  // nothing flagged and no cluster → no card at all (don't add empty chrome)
  if (data && !err && !cands.length && members.length <= 1) return null

  const propLine = p => [p.address_text, p.district_code, p.area_sqm ? tLog('desk_entry_area_abbrev', { area: p.area_sqm }) : null,
    p.rooms ? tLog('desk_entry_rooms_abbrev', { rooms: p.rooms }) : null].filter(Boolean).join(' · ') || p.id

  return (
    <Facet title={t('desk_dup_facet_title')} num={pm?.num} labelKey={pm?.labelKey} summaryTone={data && pending.length ? 'warn' : null}
      hint={t('desk_dup_facet_hint')}
      summary={data ? (pending.length ? tLog('desk_dup_to_review', { n: pending.length }) : (members.length > 1 ? tLog('desk_dup_cluster_of', { n: members.length }) : '—')) : '…'}
      defaultOpen={pending.length > 0}>
      {err && <p className="text-sm text-destructive"><Icon name="alert-triangle" /> {err}</p>}
      {!data && !err && <p className="text-xs text-muted-foreground">{t('desk_loading')}</p>}

      {cands.length > 0 && (
        <ul className="space-y-2">
          {cands.map(c => (
            <li key={c.id} className="border rounded p-2 text-xs">
              <div className="flex items-start gap-2">
                {c.other?.photo && <img src={c.other.photo} alt="" className="w-14 h-14 object-cover rounded shrink-0" />}
                <div className="min-w-0">
                  <div className="font-medium truncate">{propLine(c.other || {})}</div>
                  <ul className="text-muted-foreground mt-0.5">
                    {(c.evidence || []).map((s, i) => <li key={i}>• {s}</li>)}
                  </ul>
                  {c.latest_verdict && (
                    <div className="mt-0.5 text-muted-foreground">
                      {tLog('desk_dup_last_verdict', { verdict: t(`desk_dup_verdict_${c.latest_verdict.verdict}`) || c.latest_verdict.verdict, note: c.latest_verdict.note ? ` — ${c.latest_verdict.note}` : '' })}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex gap-1 mt-1.5">
                <button className={btn('ghost')} disabled={busy === c.id} onClick={() => judge(c, 'same')}><IconText>{t('desk_dup_verdict_same')}</IconText></button>
                <button className={btn('ghost')} disabled={busy === c.id} onClick={() => judge(c, 'different')}><IconText>{t('desk_dup_verdict_different')}</IconText></button>
                <button className={btn('ghost')} disabled={busy === c.id} onClick={() => judge(c, 'unsure')}>{t('desk_dup_verdict_unsure')}</button>
                {busy === c.id && <span className="text-muted-foreground self-center"><Icon name="hourglass" /></span>}
              </div>
            </li>
          ))}
        </ul>
      )}

      {members.length > 1 && (
        <div className="mt-2 text-xs">
          <div className="font-medium">{tLog('desk_dup_shown_together', { n: members.length })}</div>
          <ul className="text-muted-foreground">
            {members.map(m => <li key={m.id}>• {propLine(m)}{m.id === propertyId ? ` ${t('desk_dup_this_one')}` : ''}</li>)}
          </ul>
          {data?.cluster?.contested && (
            <p className="text-destructive mt-1"><IconText>{t('desk_dup_contested')}</IconText></p>
          )}
        </div>
      )}
    </Facet>
  )
}

// `existing` = { propertyId, listingId? } — UNIFIED PROPERTY CARD mode (2026-07-08):
// the same workspace opens for an ALREADY-KNOWN property (scraped, reshared, or from
// My Properties) — same design whether it entered by hand or by scrape, because at the
// data layer it's the same thing. In this mode: the form prefills from the listing's
// merged view, Record anchors to existing.propertyId (never re-resolves identity —
// address-only resolution would mint a duplicate row, the B25 failure mode), the
// entry id is STABLE per (agent, property) so every edit session lands on ONE manual
// listing instead of accreting one per session, and a POSTED ON / lineage panel shows
// the scrape provenance when there is one. Provenance is evidence, not identity — the
// card looks the same either way, it just has more or less provenance to show.
// The container the embedded form renders in. Module-level on purpose: a component defined inside the
// render gets a new identity every render, which remounts the whole form on each keystroke.
function EmbeddedShell({ children }) { return <div data-embedded-property-form>{children}</div> }
// A jump target for PropertyModalNav. Module-level on purpose: a component declared inside the modal's
// render would be a new type every render and remount every input inside it.
function PmSection({ id, children }) { return <div id={`pm-${id}`} className="scroll-mt-28" data-pm-section={id}>{children}</div> }
// Which section a "did not land" warning is about, so the footer can jump there.
const WARNING_SECTION = { cadastralConflict: 'property', listingStateSkipped: 'terms', needsPolicyStance: 'conditions',
  policyWriteFailed: 'conditions', giraoTermsFailed: 'conditions', petTermsFailed: 'conditions', subtypeUnknown: 'property' }

function ManualPropertyModal({ districts: districtsProp, draft, onClose, onSaved, onDraftCreated, agentId, agentName, existing = null, onAddMeeting, onCreateContract, privileged = false,
  // EMBEDDED (owner 2026-09-16, "questions filled like it would be adding new listing"): the valuation
  // tab renders THIS form — not a copy of its fields — so a fact has one control, one prefill and one
  // save wherever it is asked. Embedded shows only the property facts, the owner and the save; the
  // photos, terms, agreement, meeting and history facets live in their own tabs and modals.
  // `highlight` names the fields the valuation analysis still has an open question about.
  embedded = false, highlight = null }) {
  // The deal editor has no district list to hand in; the form fetches its own rather than render an
  // address picker with nothing to pick from.
  const [fetchedDistricts, setFetchedDistricts] = useState(null)
  useEffect(() => { if (!districtsProp) getDistricts().then(setFetchedDistricts).catch(() => {}) }, [])  // mount only
  const districts = districtsProp || fetchedDistricts || []
  const draftInput = draft?.proposed?.input || null
  // stable per logical Save: retries reuse it, so the server upsert can't duplicate.
  // existing mode: deterministic per (agent, property) — see the header comment.
  const entryIdRef = useRef(existing ? `edit-${agentId || 'anon'}-${existing.propertyId}` : crypto.randomUUID())
  const [lineage, setLineage] = useState(null)   // getListingLineage result (existing mode w/ listing)
  // driftFacts: attr → { canonical, canonical_source } for facts where a newer/
  // higher-trust source disagrees with what THIS agent recorded (agent_property_view
  // .drift). Surfaced as an inline ⚠ on the matching form field — replaces the old
  // MY VERSION panel's drift list (2026-07-08: fact endorsing folded into the form).
  const [driftFacts, setDriftFacts] = useState({})
  // ⓘ definitions (0162): this modal historically had no features fetch (its ladders are
  // hardcoded [code,label] pairs) — fetched now ONLY to resolve definitions; labels stay
  // hardcoded so nothing about chip rendering depends on the fetch landing.
  const [defVocab, setDefVocab] = useState([])
  const [defKey, setDefKey] = useState(null)
  useEffect(() => { getFeatures().then(setDefVocab).catch(() => {}) }, [])
  const [proposalId, setProposalId] = useState(draft?.id || null)
  // ⚠️ Every "✨ Auto-fill" press saves a NEW pending draft server-side (evidence first), and Save consumes only the LAST
  // one — the earlier presses, a draft closed with "discard", and an opened draft a later paste replaced all stayed in the
  // list as "AI-created · pending" cards (owner 2026-11-03: "fix the add property window too"). These two refs are what
  // this window may tidy: drafts IT made, and the one it was opened on. Dismissing keeps the row (status 'dismissed').
  const parsedHereRef = useRef(new Set())
  const openedDraftRef = useRef(draft?.id || null)
  const [prop, setProp] = useState({
    address: draftInput?.property?.address || '', district_code: draftInput?.property?.district_code || '',
    cadastral_code: draftInput?.property?.cadastral_code || '', kind: draftInput?.property?.kind || 'apartment',
    // AddressBlock's own three fields (2026-08-19). `address` remains the composed record
    // that gets SAVED; these are how it is built and corrected. A draft written before this
    // change has none of them, and that is fine — AddressBlock seeds its street box from
    // `value.street` and shows the composed address regardless.
    street: draftInput?.property?.street || '',
    street_number: draftInput?.property?.street_number || '',
    entrance_apt: draftInput?.property?.entrance_apt || '',
    // property_coords: the REAL property pin (ingest resolves lat/lng after the draft is written,
    // so an executed draft's proposed.input usually has none) — attached by /api/core/proposals.
    lat: draftInput?.property?.lat ?? draft?.property_coords?.lat ?? null,
    lng: draftInput?.property?.lng ?? draft?.property_coords?.lng ?? null,
  })
  // City scope for the district picker. Seeded from the district the draft already
  // carries, so reopening a Batumi property does not open the tree on Tbilisi and
  // hide its own answer; otherwise Tbilisi, which is 64 of the 236 rows and the
  // overwhelming majority of supply. Always overridable — "All of Georgia" is one
  // pick away, so this is a default, never a restriction.
  const [districtCity, setDistrictCity] = useState(() => {
    const seed = draftInput?.property?.district_code || ''
    if (seed && !/^(TBS_|PERI_)/.test(seed)) {
      const row = districts.find(d => d.code === seed)
      if (row?.city) return row.city
      return null            // known code, unknown city → show everything rather than hide it
    }
    return DEFAULT_CITY
  })
  const [offer, setOffer] = useState({
    intent: draftInput?.listing?.intent || 'sale',
    price: draftInput?.listing?.price ?? '', currency: draftInput?.listing?.currency || 'USD',
  })
  // Numeric/text facts '' = not observed; balcony/terrace are TRI-state ('' | 'true' | 'false')
  // because a checkbox can't say "unknown" and omission must mean not-observed downstream
  // (prepareListingObservation contract: omitted ≠ false). All of these fields are already
  // accepted server-side (core_prepare.js FACT map) — no server change needed.
  const [facts, setFacts] = useState({
    rooms: draftInput?.facts?.rooms ?? '', area_sqm: draftInput?.facts?.area_sqm ?? '',
    // the unit's apart-hotel terms (core 2856) — '' = not stated, like every fact here
    ...Object.fromEntries(APARTHOTEL_FACT_KEYS.map(k => [k, draftInput?.facts?.[k] ?? ''])),
    floor: draftInput?.facts?.floor ?? '', floors_total: draftInput?.facts?.floors_total ?? '',
    bedrooms: draftInput?.facts?.bedrooms ?? '', bathrooms: draftInput?.facts?.bathrooms ?? '',
    kitchen_area: draftInput?.facts?.kitchen_area ?? '', ceiling_height: draftInput?.facts?.ceiling_height ?? '',
    // A ceiling that VARIES (2026-09-16; sloped 2.6/2.9 m, 3 m rising to 4 m on a დაშენება).
    // ⚠️ Owner correction the same day: ceiling_height is NOT the minimum. It keeps its historical
    // meaning — the reported height, as ~6,900 portal and agent values were recorded — and a varying
    // ceiling is three facts of its own. '' variation = not stated: an ordinary "2.8 m" proves
    // neither that the ceiling varies nor that it does not.
    ceiling_variation: draftInput?.facts?.ceiling_variation ?? '',
    ceiling_height_min: draftInput?.facts?.ceiling_height_min ?? '',
    ceiling_height_max: draftInput?.facts?.ceiling_height_max ?? '',
    condition: draftInput?.facts?.condition ?? '',
    project: draftInput?.facts?.project ?? '',
    // year_built (2026-09-16): the building's year. Already accepted and retractable server-side
    // (core_prepare FACT_KEYS, core_manual RETRACTABLE) — only the form never asked for it.
    year_built: draftInput?.facts?.year_built ?? '',
    balcony_count: draftInput?.facts?.balcony_count ?? '', balcony_area_sqm: draftInput?.facts?.balcony_area_sqm ?? '',
    has_terrace: draftInput?.facts?.has_terrace == null ? '' : String(draftInput.facts.has_terrace),
    // 0083 Tier C: commercial power feed + raw zoning label (land/commercial)
    power_capacity_kw: draftInput?.facts?.power_capacity_kw ?? '',
    functional_zone: draftInput?.facts?.functional_zone ?? '',
    // The rest of core_prepare's FACT_KEYS (2026-09-16, "all the fields known to our system"): the
    // server has accepted and retracted these since 2026-07-24 and the form never asked for them.
    land_area_sqm: draftInput?.facts?.land_area_sqm ?? '',
    living_room_area_sqm: draftInput?.facts?.living_room_area_sqm ?? '',
    loggia_area_sqm: draftInput?.facts?.loggia_area_sqm ?? '',
    storeroom_area_sqm: draftInput?.facts?.storeroom_area_sqm ?? '',
    porch_area_sqm: draftInput?.facts?.porch_area_sqm ?? '',
    building_material: draftInput?.facts?.building_material ?? '',
    // 0167: 1 = the space is deeded, 0 = explicitly not; '' = not stated. Numeric on purpose (see core_prepare).
    parking_deeded: draftInput?.facts?.parking_deeded == null ? '' : String(draftInput.facts.parking_deeded),
    // 2026-09-16: the declared metro station, and the unit the owner stated the plot area in (the
    // number itself stays square metres in land_area_sqm).
    metro_station: draftInput?.facts?.metro_station ?? '',
    land_area_unit: draftInput?.facts?.land_area_unit ?? '',
    // The fine type under the kind (garage, office, cottage, agricultural…) — the `subtype` claim.
    subtype: draftInput?.facts?.subtype ?? '',
    // A ROOM's relation to the flat or house it sits in (owner 2026-09-24, decision C; core 2394) — claims
    // room_unit_kind / room_unit_rooms / room_unit_area_sqm / room_unit_occupants (core_prepare FACT_KEYS).
    room_unit_kind: draftInput?.facts?.room_unit_kind ?? '',
    room_unit_rooms: draftInput?.facts?.room_unit_rooms ?? '',
    room_unit_area_sqm: draftInput?.facts?.room_unit_area_sqm ?? '',
    room_unit_occupants: draftInput?.facts?.room_unit_occupants ?? '',
  })
  const [owner, setOwner] = useState({ name: draftInput?.owner?.name || '', phone: draftInput?.owner?.phone || '', email: draftInput?.owner?.email || '' })
  // ── Lift, parking, heating (owner 2026-09-16: "questions filled like adding a new listing") ──
  // Yes / no / not stated, NOT a checkbox: an unticked box cannot say "unknown", and omission
  // must stay "not observed" downstream. Saved as `chip_states` ({code: true|false}); only the
  // answers the agent CHANGED from the prefill leave the form, the same rule as facts. Heating is
  // several independent yeses (central AND floor heating is ordinary), so deselecting a type is
  // not sent as "absent" — that would assert a fact the agent did not state.
  const [featAns, setFeatAns] = useState({ elevator: '', parking: '', heat: [] })
  const featBaselineRef = useRef({ elevator: '', parking: '', heat: [] })
  // ── Every other registry feature (2026-09-16, src/lib/featureCapture.js) ── {code: 'true'|'false'},
  // prefilled from the card's feature rows; only answers that differ from the prefill are saved.
  const [featStates, setFeatStates] = useState({})
  const featStatesBaseRef = useRef({})
  const [aiFeatureCodes, setAiFeatureCodes] = useState(() => new Set())
  const [featDisagree, setFeatDisagree] = useState(() => new Set())   // codes whose recorded answers disagree   // what the last paste answered, marked in the picker
  const [kindCaps, setKindCaps] = useState([])
  useEffect(() => { getKindFeatureCapability().then(setKindCaps).catch(() => {}) }, [])
  // 8-level apartment class (0078) — matched against demand's condition_codes. Not
  // pre-filled on edit-existing (features aren't loaded into this modal yet); an empty
  // selection is simply omitted, never cleared server-side (append-only features).
  // The condition block (2026-09-24, owner "let's match those"): three answers from the shared definition —
  // building (new / old / under construction), condition (frames + renovation ladder), apartment class. It replaced ONE
  // slot shared by 14 buttons, where a class wiped a frame and "old building, renovated" could not be said at all.
  const condFromCodes = codes => {
    const out = { building: '', condition: '', apartment_class: '', never_lived_in: '' }
    for (const c of codes || []) { const k = conditionRowOf(String(c)); if (k && !out[k]) out[k] = String(c) }
    return out
  }
  const [cond, setCond] = useState(() => condFromCodes(draftInput?.tags))
  // Prefill baselines (2026-09-16): what the card already said, so a cleared answer can be TAKEN BACK
  // (features_retract) and an untouched prefilled rung is not re-sent as this agent's answer.
  const condBaseRef = useRef({ building: '', condition: '', apartment_class: '', never_lived_in: '' })
  const typeBaseRef = useRef([])
  const entranceBaseRef = useRef('')
  // review 2026-09-23: the price/currency the card prefilled (null = no prefill → everything typed is theirs).
  // An untouched prefilled price is not re-sent as this agent's own on every Record.
  const offerBaseRef = useRef(null)
  const [saveWarnings, setSaveWarnings] = useState([])   // what the last save reported did NOT land
  const [landText, setLandText] = useState(null)         // the plot area as typed, in the chosen unit
  // Tier A per-type tags (supply side of TYPE_CHIP_CODES) — multi-toggle, additive
  // observations like everything else in this workspace (starts empty on edit).
  const [typeTags, setTypeTags] = useState([])
  const toggleTypeTag = code => setTypeTags(ts => ts.includes(code) ? ts.filter(c => c !== code) : [...ts, code])
  const [sourceKind, setSourceKind] = useState(draftInput?.source_kind || (draft ? 'ai' : 'owner'))
  // Several texts about ONE property (owner 2026-11-03: "add text 1, 2 or any amount — some WhatsApp, some anything else,
  // their own description — then a button lets AI parse and fill the form"). One press reads them all as one draft.
  const [pasteTexts, setPasteTexts] = useState([''])
  const filledTexts = pasteTexts.map(s => s.trim()).filter(Boolean)
  // one text goes as it is; several are numbered so the parser reads them as separate sources about the same property
  const pasteText = filledTexts.length > 1 ? filledTexts.map((s, i) => `[text ${i + 1}]\n${s}`).join('\n\n') : (filledTexts[0] || '')
  const setPasteText = v => setPasteTexts([v])
  const [parsedText, setParsedText] = useState('')   // the combined text the current draft was read from
  const [parsing, setParsing] = useState(false)
  // The fields a second parse disagrees with, and the agent's answer for each. Empty is the
  // normal case: a first paste into a blank form produces no conflicts at all.
  const [parseConflicts, setParseConflicts] = useState([])
  const [parseAnswers, setParseAnswers] = useState({})
  const [saving, setSaving] = useState(false)
  const [archiving, setArchiving] = useState(false)
  const [err, setErr] = useState(null)

  // ── Workspace state: the modal STAYS OPEN after recording (2026-07-06 redesign) ──
  // Each Save = one observation of the same source listing (same entry_id / proposal_id
  // → same source_listing_id server-side, so re-records upsert, never duplicate).
  // The agent accumulates partial truth: record, learn more, record again.
  // An EXECUTED draft (clicked from the ledger after it was saved) reopens straight in the
  // saved-workspace state — that's how the agent gets back in to add photos/observations.
  // Safe because re-Record upserts on the same proposal_id (never duplicates) and the Record
  // button stays disabled until something actually changes (saved && !dirty).
  const [saved, setSaved] = useState(() =>
    existing ? { propertyId: existing.propertyId, listingId: existing.listingId || null }
    : draft?.status === 'executed' && draft?.result?.listing_id
      ? { propertyId: draft.result.property_id, listingId: draft.result.listing_id }
      : null)                                       // { propertyId, listingId } after first record
  const [lastRec, setLastRec] = useState(null)      // snapshot at last record → dirty detection
  const initialSnapRef = useRef(null)               // the form as first rendered — "unsaved work" for a new entry
  const baselineRef = useRef(null)    // facts as prefilled (card ⊕ my version) — only diffs from it are saved
  const [filledNote, setFilledNote] = useState(0)   // how many gaps the last fill took from the card
  const ownKeysRef = useRef([])       // attributes the agent has hand-written (their version)
  const cardFactsRef = useRef(null)   // the opened card's own values, for "fill my gaps from this card"
  const [tlRefresh, setTlRefresh] = useState(0)     // bumped after every record → PropertyTimeline refetches (B20)
  // ── Paste update batches (core 1241): on a property that exists, a paste becomes a reviewable batch ──
  const [batches, setBatches] = useState([])
  // core 1264: false = a hand-made property this agent neither made nor was shared — they work on their own
  // private version. null = not known yet (treated as working on it: the database enforces the rule).
  const [worksOnProperty, setWorksOnProperty] = useState(null)
  const [batchBusy, setBatchBusy] = useState(false)
  const rebaseAfterBatchRef = useRef(false)          // accepted values were written — re-baseline the dirty check
  const [coops, setCoops] = useState([])            // cooperations on this property (Agency Agreement facet)
  const [pcontacts, setPcontacts] = useState(null)  // ranked property contacts — who a cooperation opens with
  const [coopModal, setCoopModal] = useState(null)  // { propertyId, propertyLabel?, contactPartyId?, contactName?, cooperationId? }
  const [showTerms, setShowTerms] = useState(false) // rental-restrictions modal open?

  // ── Photos & documents: uploaded rows live server-side (listing_image via lib/core_media.js);
  // kind='photo' is the publishable gallery, kind='document' is agent-only (plans, PDFs,
  // cadastral-code photos — never published; core migration 0062). Files picked BEFORE the
  // first Record wait in pendingImgs (no listingId to attach to yet) and flush right after
  // Save succeeds. Deliberately NOT in snapshot(): uploads commit immediately, they are not
  // part of the dirty/Record cycle.
  const [images, setImages] = useState([])          // [{ id, url, kind }] committed, array order = position
  const [pendingImgs, setPendingImgs] = useState([]) // [{ file, preview, kind }] waiting for a listingId
  const [imgBusy, setImgBusy] = useState(false)
  const [heicBusy, setHeicBusy] = useState(0)   // iPhone photos being turned into JPEGs (see handlePickImages)
  const [imgErr, setImgErr] = useState(null)
  // Upload size, asked the way a mail client asks about an attachment. Persisted per agent
  // because it is a habit, not a per-listing decision — someone shooting on a good phone for
  // premium listings sets it once. Documents are never affected (see imageDownscale.js).
  const [photoSize, setPhotoSize] = useState(() => {
    try { return photoSizeSpec(localStorage.getItem(PHOTO_SIZE_LS)).key } catch { return DEFAULT_PHOTO_SIZE }
  })
  const [sizeEst, setSizeEst] = useState(null)      // { bytes, original } for the queued photos
  const dragIdx = useRef(null)                       // photo tile being dragged (index within photos)
  // The portal's own full-size copies. On ss/myhome/home.ge these carry the portal's
  // watermark and are deliberately kept out of every gallery, card strip and client share
  // (0332) — but an agent still wants to SEE the original sometimes, so the photos section
  // offers a view toggle. Empty for korter/bog, whose full rendition is already the clean
  // one; the toggle then does not render at all rather than switching to nothing.
  const [originals, setOriginals] = useState([])
  // ── A CAPTURE IS IMMUTABLE; MY VERSION IS MINE (owner 2026-09-28) ──────────────────────────────
  // "Every scrape is immutable … every agent may have their own version", and "my version is ss photos
  // plus what I added". Opened on a scraped ad, `saved.listingId` IS that ad until the first Record — and
  // photos used to upload straight onto it (13 on one ss.ge ad), where they never reached the agent's own
  // version. So while the workspace still points at a capture: its photos render READ-ONLY, new picks
  // QUEUE, and Record writes the agent's version (manual:edit-…) and uploads the queue THERE, linking the
  // version to this ad (based_on_listing_id) so the version shows the ad's photos + the agent's.
  // `openedSource` comes from the lineage fetch; until it lands the ad counts as a capture (the safe way:
  // a queued photo still uploads on Record, a photo written onto a scrape cannot be taken back).
  // The server enforces the same rule (core_media.js "A CAPTURE IS IMMUTABLE") — this is the UX half.
  const [openedSource, setOpenedSource] = useState(null)
  // ⚠️ A LISTING THE SERVER CALLS A CAPTURE IS ONE (owner 2026-10-18: "agents must have their own versions and whenever
  // they press edit they see it"). The window decided "capture or mine" only from the advert it was OPENED on, so a
  // workspace pointing at a capture any other way (an executed draft's listing, …) uploaded the agent's photos onto it:
  // the server refused each one (409 scrape_immutable) and Retry sent them to the same ad again. A refusal now marks that
  // listing as the capture it is: the photos stay queued, the agent's VERSION is recorded at once, and they upload there.
  const [refusedCapture, setRefusedCapture] = useState(null)   // a listing id the server refused as a capture
  const [versionOwed, setVersionOwed] = useState(false)        // queued photos wait for a Record that makes the version
  const [basePhotos, setBasePhotos] = useState([])   // the capture's photos, once the version exists
  // ── ONE VERSION PER AGENT PER FLAT (owner 2026-09-28) ─────────────────────────────────────────
  // "They saved and left, came back and opened another from that cluster; they see different values,
  // and if they edit they may end up with two versions — that's messy." When the agent already has a
  // version anywhere on the flat (my-version's `version`, server agent_version.js), THIS window is that
  // version: values from the capture it is based on + the agent's facts, its photos, its base — never
  // the advert that happened to be clicked. `baseListingId` is the capture the version is (or will be)
  // based on; Save sends it, and the server sets it only once.
  const [myVersionInfo, setMyVersionInfo] = useState(null)   // { listing_id, property_id, based_on_listing_id } | null
  const [baseListingId, setBaseListingId] = useState(existing?.listingId || null)
  const versionOpenedRef = useRef(false)   // the version's photos are loaded — the clicked ad's must not overwrite them
  // 'clean' = what everyone else sees and what reordering/removal acts on.
  // 'original' = READ-ONLY. Originals are evidence rows (they carry media_hash/phash for
  // dedup); editing them here would walk the watermark back into the surfaces 0332 cleared.
  const [imgView, setImgView] = useState('clean')

  const snapshot = () => JSON.stringify({ prop, offer, facts, owner, sourceKind, featAns, featStates, cond, typeTags })
  const batchPropertyId = existing?.propertyId || saved?.propertyId || null
  const reloadBatches = () => (batchPropertyId ? getPasteBatches(batchPropertyId, { agentId }).then(setBatches).catch(() => {}) : Promise.resolve())
  useEffect(() => { reloadBatches() }, [batchPropertyId])   // eslint-disable-line react-hooks/exhaustive-deps
  // The area the map shows for the chosen district, else the chosen city (core 1265). "All of Georgia"
  // (no city) asks nothing and leaves the map where it is.
  const [mapFocus, setMapFocus] = useState(null)
  useEffect(() => {
    let gone = false
    const districtCode = prop.district_code || null
    if (!districtCode && !districtCity) return
    geoExtent({ districtCode, city: districtCode ? null : districtCity }).then(async ext => {
      // A district with no outline and too few pins falls back to its city.
      const e = ext || (districtCode && districtCity ? await geoExtent({ city: districtCity }) : null)
      if (!gone && e) setMapFocus(e)
    })
    return () => { gone = true }
  }, [prop.district_code, districtCity])
  // After a batch wrote values into the form state, that state IS the record — not an unsaved edit.
  useEffect(() => {
    const keys = rebaseAfterBatchRef.current
    if (!keys) return
    rebaseAfterBatchRef.current = false
    // Re-baseline ONLY what the batch wrote: other unsaved edits (address, class, a failed save's retry marker) stay dirty.
    if (lastRec == null || lastRec === '__retry__') return
    let base
    try { base = JSON.parse(lastRec) } catch { return }
    const cur = JSON.parse(snapshot())
    for (const k of ['facts', 'offer', 'owner', 'featStates']) for (const f of keys[k] || []) base[k] = { ...(base[k] || {}), [f]: cur[k]?.[f] }
    for (const f of keys.featAns || []) base.featAns = { ...(base.featAns || {}), [f]: cur.featAns?.[f] }
    setLastRec(JSON.stringify(base))
  }, [facts, offer, owner, featStates, featAns])   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { initialSnapRef.current = snapshot() }, [])   // mount only
  const OUR_LISTING_SOURCES = ['manual', 'sentavus']
  const onCapture = (!!existing?.listingId && saved?.listingId === existing.listingId && !OUR_LISTING_SOURCES.includes(openedSource))
    || (!!refusedCapture && saved?.listingId === refusedCapture)
  // Photos queued on a capture ARE the change: without this an agent who only added photos had no way to
  // Record them (the form snapshot does not include photos), so they would sit queued forever.
  const dirty = lastRec == null || snapshot() !== lastRec || (onCapture && pendingImgs.length > 0)

  // review 2026-09-23 (bug 2): an existing property whose facts have not loaded — no listing to
  // prefill from, or the lineage fetch not back / failed — shows the form's DEFAULTS (apartment,
  // sale). Recording then would file those defaults as facts under the property's id, so Record
  // stays blocked until the prefill has landed.
  const prefillMissing = !!existing?.propertyId && (!existing?.listingId || !lineage)
  const canSave = !prefillMissing && (prop.address.trim().length > 0 || prop.cadastral_code.trim().length > 0)

  // Reopened in saved state (executed draft): baseline the dirty-detector on the initial form
  // (so Record shows '✓ Recorded' until something changes) and load what the workspace shows —
  // existing photos, cooperations, contacts — exactly like handleSave does after a Record.
  useEffect(() => {
    if (!saved?.listingId) return
    setLastRec(snapshot())
    listListingImages(saved.listingId, { includeOriginals: true }).then(r => {
      if (versionOpenedRef.current) return   // the agent's version was opened meanwhile — its photos win
      setImages(r.images.map(i => ({ id: i.id, url: i.url, kind: i.kind || 'photo' })))
      setOriginals((r.originals || []).map(i => ({ id: i.id, url: i.url })))
    }).catch(() => {})
    if (saved.propertyId) {
      listCooperations({ propertyId: saved.propertyId }).then(setCoops).catch(() => {})
      getPropertyContacts(saved.propertyId).then(setPcontacts).catch(() => {})
    }
  }, [])  // mount only — saved starts non-null only for executed drafts / existing mode

  // Unified-card mode: prefill the form from the listing's merged view (the same
  // overlay the lineage modal showed) and keep the lineage around for the POSTED ON
  // panel. Re-baseline the dirty-detector AFTER prefill so Record stays disabled
  // until the agent actually changes something.
  // 🔑 MY VERSION IS AN OVERLAY, AND THE EDITOR MUST SHOW IT (owner 2026-09-03): the agent's
  // hand-written facts win, the card they opened fills the gaps, and opening a DIFFERENT card
  // changes only the gaps. Before this the form prefilled from the card alone — so opening the
  // korter sibling of a flat whose floor you had already corrected showed korter's floor, and
  // saving from there would have overwritten your correction with it.
  // Three refs are captured here and read at save: the baseline the form was prefilled with
  // (only what DIFFERS from it may leave as a claim — see factsDiff), the keys the agent owns,
  // and the card's own values (for "fill my gaps from this card").
  useEffect(() => {
    if (!existing?.listingId) return
    let gone = false
    // ⚠️ NOT gated on agentId — this is the fetch that PREFILLS the editor with the agent's own
    // recorded values. Gated, an admin opened the editor with their own recorded facts missing
    // from the form, and clearing a field to retract it was impossible because it was already
    // blank. Measured 2026-09-04 against a recorded ceiling height.
    const mineP = existing?.propertyId
      ? getMyPropertyVersion(existing.propertyId, agentId).catch(() => null)   // soft: no version is a valid answer
      : Promise.resolve(null)
    mineP.then(mine => {
      const v = mine?.version || null
      const baseId = v?.based_on_listing_id || existing.listingId
      return getListingLineage(baseId, { agentId: privileged ? null : agentId }).then(d => [d, mine, v, baseId])
    }).then(([d, mine, v, baseId]) => {
      if (gone) return
      setLineage(d)
      setOpenedSource(d.listing?.source || null)
      setBaseListingId(baseId)
      if (v?.listing_id) {
        // Open THE VERSION: Save and photo uploads go to it, the capture's photos show read-only beside it.
        versionOpenedRef.current = true
        setMyVersionInfo(v)
        setSaved({ propertyId: v.property_id, listingId: v.listing_id })
        setOriginals([])
        Promise.all([
          listListingImages(v.listing_id).catch(() => ({ images: [] })),
          baseId && baseId !== v.listing_id ? listListingImages(baseId).catch(() => ({ images: [] })) : Promise.resolve({ images: [] }),
        ]).then(([own, base]) => {
          if (gone) return
          setImages((own.images || []).map(i => ({ id: i.id, url: i.url, kind: i.kind || 'photo' })))
          setBasePhotos((base.images || []).filter(i => (i.kind || 'photo') === 'photo').map(i => ({ id: i.id, url: i.url, kind: 'photo' })))
        })
      }
      const m = d.merged || {}
      const numStr = v => (v == null ? '' : String(v))
      const addr = m.address ? splitCityFromAddress(m.address).rest || m.address : ''
      // review 2026-09-23: seed AddressBlock's street/number from the stored address, or a number
      // edit composed the address from an empty street and saved it as the bare number ("14").
      const sn = addr ? splitStreetNumber(addr) : null
      setProp(p => ({
        ...p,
        address: addr || p.address,
        ...(sn && !p.street ? { street: sn.street, street_number: p.street_number || sn.number } : {}),
        district_code: m.district_code || p.district_code,
        kind: m.kind || p.kind,
        lat: m.lat ?? p.lat, lng: m.lng ?? p.lng,
        // the private entrance/apartment detail, so clearing it can actually remove it
        entrance_apt: d.listing?.entrance_apt || p.entrance_apt,
      }))
      entranceBaseRef.current = d.listing?.entrance_apt || ''
      setOffer(o => {
        const next = { ...o, intent: m.intent || o.intent, price: numStr(m.price), currency: m.currency || o.currency }
        offerBaseRef.current = { price: next.price, currency: next.currency }   // review 2026-09-23: bug 3
        return next
      })
      const cardFacts = {
        rooms: numStr(m.rooms), bedrooms: numStr(m.bedrooms), bathrooms: numStr(m.bathrooms),
        area_sqm: numStr(m.area_sqm), floor: numStr(m.floor), floors_total: numStr(m.floors_total),
        condition: m.condition || '',
        year_built: numStr(m.year_built),
        metro_station: m.metro_station || '', land_area_unit: m.land_area_unit || '',
        land_area_sqm: numStr(m.land_area_sqm), living_room_area_sqm: numStr(m.living_room_area_sqm),
        loggia_area_sqm: numStr(m.loggia_area_sqm), storeroom_area_sqm: numStr(m.storeroom_area_sqm),
        porch_area_sqm: numStr(m.porch_area_sqm), building_material: m.building_material || '',
        parking_deeded: numStr(m.parking_deeded),
      }
      // Lift / parking / heating from the card's merged feature rows. ⚠️ Those rows are the
      // agent-READ answers (Idmon, Lynceus, agents) — a bare portal checkbox is not among them,
      // which is the safe direction: the form never shows "no lift" on the strength of a box a
      // portal left unticked (a scrape-only negative is unknown, never absence).
      {
        if (typeof mine?.works_on_property === 'boolean') setWorksOnProperty(mine.works_on_property)
        // core 1264: the agent's PRIVATE answers (a hand-made property they do not work on) never reach the
        // card's shared rows; in their own form their answer replaces the shared ones for that feature.
        const priv = Array.isArray(mine?.private_features) ? mine.private_features : []
        const privKeys = new Set(priv.map(x => x.feature_key))
        const rows = [...(Array.isArray(m.features) ? m.features : []).filter(x => !privKeys.has(x.feature_key)),
          ...priv.map(x => ({ feature_key: x.feature_key, present: x.present, source_kind: x.source_kind }))]
        const tri = key => {
          const mine = rows.filter(x => x.feature_key === key)
          if (mine.some(x => x.present === true)) return 'true'
          if (mine.some(x => x.present === false && !['scrape', 'scrape_reshare'].includes(x.source_kind))) return 'false'
          return ''
        }
        const heat = [...new Set(rows.filter(x => /^heat_/.test(x.feature_key) && x.present === true).map(x => x.feature_key))]
          .filter(c => HEATING_CODES.includes(c))
        const next = { elevator: tri('elevator'), parking: tri('parking'), heat }
        featBaselineRef.current = next
        setFeatAns(next)
        const states = featureAnswersFromRows(rows)
        featStatesBaseRef.current = states
        setFeatStates(states)
        setFeatDisagree(disagreeingFeatureCodes(rows))
        // The state/class rung and the per-type chips were never prefilled, so an agent could not see
        // or correct an existing answer. Any source counts for SHOWING; only a change is saved.
        const presentCodes = new Set(rows.filter(x => x.present === true).map(x => x.feature_key))
        // ladder order decides when two sources disagree (the same rule the single slot used)
        const rec = condFromCodes([...CONDITION_CODES].filter(c => presentCodes.has(c)))
        condBaseRef.current = rec
        setCond(rec)
        const typeCodes = new Set(Object.values(PROP_TYPE_TAGS).flat().map(([c]) => c))
        const tt = [...presentCodes].filter(c => typeCodes.has(c))
        typeBaseRef.current = tt
        setTypeTags(tt)
      }
      // The card payload carries no subtype, metro, plot unit, material, sub-areas or deeded parking, so an existing
      // property showed those blank. "My version" returns every recorded fact (own, endorsed or canonical).
      for (const k of ['subtype', 'metro_station', 'land_area_unit', 'land_area_sqm', 'building_material', 'living_room_area_sqm',
        'loggia_area_sqm', 'storeroom_area_sqm', 'porch_area_sqm', 'parking_deeded',
        'room_unit_kind', 'room_unit_rooms', 'room_unit_area_sqm', 'room_unit_occupants', ...APARTHOTEL_FACT_KEYS]) {
        const v = mine?.facts?.[k]?.value
        if ((cardFacts[k] == null || cardFacts[k] === '') && v != null && v !== '') cardFacts[k] = String(v)
      }
      const own = Object.entries(mine?.facts || {}).filter(([, f]) => f && f.state === 'own')
      const mineFacts = Object.fromEntries(own.map(([k, f]) => [k, f.value == null ? '' : String(f.value)]))
      cardFactsRef.current = cardFacts
      ownKeysRef.current = own.map(([k]) => k)
      setFacts(f => {
        const next = { ...f, ...cardFacts }
        for (const k of Object.keys(mineFacts)) if (k in next) next[k] = mineFacts[k]
        baselineRef.current = { ...next }
        return next
      })
    }).catch(() => {})
    return () => { gone = true }
  }, [])  // mount only

  // …and once the prefill has actually rendered into state, baseline the dirty
  // detector on it (snapshot() here sees the post-prefill values). Runs once.
  const prefillBaselined = useRef(false)
  useEffect(() => {
    if (!existing?.listingId || !lineage || prefillBaselined.current) return
    prefillBaselined.current = true
    setLastRec(snapshot())
  }, [lineage])  // eslint-disable-line react-hooks/exhaustive-deps

  // Drift: which recorded facts a newer source now disagrees with. Refetched after
  // every Record (tlRefresh) so a fresh re-scrape that moved a value lights up ⚠.
  useEffect(() => {
    if (!saved?.propertyId) { setDriftFacts({}); return }   // agentId optional — see the prefill above
    let gone = false
    getMyPropertyVersion(saved.propertyId, agentId)
      .then(d => {
        if (gone) return
        const m = {}
        for (const [attr, f] of Object.entries(d.facts || {})) if (f.drift) m[attr] = { canonical: f.canonical, source: f.canonical_source }
        setDriftFacts(m)
        if (typeof d.works_on_property === 'boolean') setWorksOnProperty(d.works_on_property)
      })
      .catch(() => { if (!gone) setDriftFacts({}) })
    return () => { gone = true }
  }, [saved?.propertyId, agentId, tlRefresh])

  // "$800 · Owner message · accepted by Nino · 17 Sep · History" — the latest accepted decision for the field.
  const provenance = provenanceByField(batches)
  const provLine = field => {
    const p = provenance[field]
    if (!p) return null
    const ms = p.decided_at ? Date.parse(p.decided_at) : NaN
    const date = Number.isNaN(ms) ? '' : new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
    return (
      <span className="block text-[11px] font-normal text-muted-foreground" data-provenance={field}>
        {batchSourceLabel(p.source_kind)}{p.decided_by_name ? ` · ${tLog('desk_prov_accepted_by', { name: p.decided_by_name })}` : ''}{date ? ` · ${date}` : ''}
        {/* The history list lives in the paste section, which the embedded view does not render. */}
        {!embedded && ' · '}{!embedded && <button type="button" className="underline" onClick={() => document.getElementById('pm-paste-history')?.scrollIntoView({ block: 'start' })}>{t('desk_prov_history')}</button>}
      </span>
    )
  }
  const driftMark = attr => {
    const d = driftFacts[attr]
    if (!d) return null
    const v = d.canonical == null ? '—' : typeof d.canonical === 'object' ? JSON.stringify(d.canonical) : String(d.canonical)
    return <span className="ml-1.5 inline-flex items-center gap-0.5 rounded-full border border-amber-500/50 bg-amber-500/10 px-1.5 text-[11px] leading-5 text-amber-700 dark:text-amber-300 align-middle" title={`A newer source says ${v}${d.source ? ` (${d.source})` : ''} — verify`} data-drift={attr}><Icon name="alert-triangle" /> {v}</span>
  }

  // ⚠️ ONE ROW PER PARSED FIELD. `bucket` names which piece of form state owns it, `read`
  // pulls it out of the draft, `label` is what the agent is asked about. Registering a field
  // here is the whole job; there is deliberately no second list to keep in step — a field
  // half-wired into the apply path while missing from the conflict check is exactly the bug
  // class this replaces.
  // ⚠️ `has_terrace` is stringified because the form holds it as a string ('true'/'false').
  // The comparison is by meaning, but what lands in state has to match what the input expects.
  const PARSE_FIELDS = [
    { key: 'address',           bucket: 'prop',  label: () => attrLabel('address'),       read: d => d.property?.address },
    { key: 'district_code',     bucket: 'prop',  label: () => attrLabel('district_code'),      read: d => d.property?.district_code },
    { key: 'cadastral_code',    bucket: 'prop',  label: () => attrLabel('cadastral_code'),     read: d => d.property?.cadastral_code },
    { key: 'kind',              bucket: 'prop',  label: () => attrLabel('kind'),    read: d => d.property?.kind },
    { key: 'lat',               bucket: 'prop',  label: () => attrLabel('lat'),           read: d => d.property?.lat },
    { key: 'lng',               bucket: 'prop',  label: () => attrLabel('lng'),           read: d => d.property?.lng },
    { key: 'intent',            bucket: 'offer', label: () => attrLabel('intent'),        read: d => d.listing?.intent },
    { key: 'price',             bucket: 'offer', label: () => attrLabel('price'),         read: d => d.listing?.price },
    { key: 'currency',          bucket: 'offer', label: () => attrLabel('currency'),      read: d => d.listing?.currency },
    { key: 'rooms',             bucket: 'facts', label: () => attrLabel('rooms'),         read: d => d.facts?.rooms },
    { key: 'area_sqm',          bucket: 'facts', label: () => attrLabel('area_sqm'),          read: d => d.facts?.area_sqm },
    { key: 'floor',             bucket: 'facts', label: () => attrLabel('floor'),         read: d => d.facts?.floor },
    { key: 'floors_total',      bucket: 'facts', label: () => attrLabel('floors_total'),  read: d => d.facts?.floors_total },
    { key: 'bedrooms',          bucket: 'facts', label: () => attrLabel('bedrooms'),      read: d => d.facts?.bedrooms },
    { key: 'bathrooms',         bucket: 'facts', label: () => attrLabel('bathrooms'),     read: d => d.facts?.bathrooms },
    { key: 'kitchen_area',      bucket: 'facts', label: () => attrLabel('kitchen_area'),       read: d => d.facts?.kitchen_area },
    { key: 'ceiling_height',    bucket: 'facts', label: () => attrLabel('ceiling_height'),       read: d => d.facts?.ceiling_height },
    { key: 'condition',         bucket: 'facts', label: () => attrLabel('condition'),     read: d => d.facts?.condition },
    { key: 'balcony_count',     bucket: 'facts', label: () => attrLabel('balcony_count'), read: d => d.facts?.balcony_count },
    { key: 'balcony_area_sqm',  bucket: 'facts', label: () => attrLabel('balcony_area_sqm'),  read: d => d.facts?.balcony_area_sqm },
    { key: 'power_capacity_kw', bucket: 'facts', label: () => attrLabel('power_capacity_kw'),         read: d => d.facts?.power_capacity_kw },
    { key: 'functional_zone',   bucket: 'facts', label: () => attrLabel('functional_zone'),          read: d => d.facts?.functional_zone },
    { key: 'has_terrace',       bucket: 'facts', label: () => attrLabel('has_terrace'),       read: d => (d.facts?.has_terrace == null ? null : String(d.facts.has_terrace)) },
    // ── Everything else the parser already extracts (2026-09-16). Before this the prompt asked for
    // these and the form threw the answers away: nothing read them, so they never reached the save.
    // ⚠️ The prompt names four areas WITHOUT the _sqm suffix the server's FACT_KEYS use
    // (living_room_area vs living_room_area_sqm) — read both, the prompt's spelling first.
    { key: 'land_area_sqm',        bucket: 'facts', label: () => attrLabel('land_area_sqm'),        read: d => d.facts?.land_area_sqm },
    { key: 'living_room_area_sqm', bucket: 'facts', label: () => attrLabel('living_room_area_sqm'), read: d => d.facts?.living_room_area ?? d.facts?.living_room_area_sqm },
    { key: 'loggia_area_sqm',      bucket: 'facts', label: () => attrLabel('loggia_area_sqm'),      read: d => d.facts?.loggia_area ?? d.facts?.loggia_area_sqm },
    { key: 'storeroom_area_sqm',   bucket: 'facts', label: () => attrLabel('storeroom_area_sqm'),   read: d => d.facts?.storeroom_area ?? d.facts?.storeroom_area_sqm },
    { key: 'porch_area_sqm',       bucket: 'facts', label: () => attrLabel('porch_area_sqm'),       read: d => d.facts?.porch_area ?? d.facts?.porch_area_sqm },
    { key: 'year_built',           bucket: 'facts', label: () => attrLabel('year_built'),           read: d => d.facts?.year_built },
    { key: 'project',              bucket: 'facts', label: () => attrLabel('project'),              read: d => d.facts?.project },
    { key: 'building_material',    bucket: 'facts', label: () => attrLabel('building_material'),    read: d => d.facts?.building_material },
    { key: 'parking_deeded',       bucket: 'facts', label: () => attrLabel('parking_deeded'),       read: d => (d.facts?.parking_deeded == null ? null : String(d.facts.parking_deeded)) },
    // building_status has no field of its own on this form: "under construction" is a rung of the
    // state ladder, "new"/"old" are the building features cls_new_built / cls_old_built. The parser
    // writes it as free text in either language ('new'/'ახალაშენებული', 'under_construction'/'მშენებარე').
    // ⚠️ Rows here may use only attrLabel: render-parse-conflicts.mjs runs this table on its own.
    // One answer since 2026-09-24: the condition block's building row (new / old / under construction).
    { key: 'building_code', bucket: 'cls', label: () => attrLabel('building_status'),
      read: d => {
        const v = String(d.facts?.building_status || '')
        if (/under|construct|მშენებარ/i.test(v)) return 'cls_under_constr'
        if (/^\s*(new|ახალ)/i.test(v)) return 'cls_new_built'
        if (/^\s*(old|ძველ)/i.test(v)) return 'cls_old_built'
        return null
      } },
    { key: 'name',              bucket: 'owner', label: () => attrLabel('owner_name'),    read: d => d.owner?.name },
    { key: 'phone',             bucket: 'owner', label: () => attrLabel('owner_phone'),   read: d => d.owner?.phone },
    { key: 'email',             bucket: 'owner', label: () => attrLabel('owner_email'),   read: d => d.owner?.email },
    { key: 'metro_station',     bucket: 'facts', label: () => attrLabel('metro_station'), read: d => d.facts?.metro_station },
  ]

  // Route a flat {key: value} patch back into the state buckets it came from.
  function applyParsePatch(patch) {
    const byBucket = { prop: {}, offer: {}, facts: {}, owner: {}, cls: {}, feat: {} }
    let any = false
    for (const fd of PARSE_FIELDS) {
      if (!(fd.key in patch)) continue
      byBucket[fd.bucket][fd.key] = patch[fd.key]
      any = true
    }
    for (const k of Object.keys(patch)) if (k.startsWith('feat:')) any = true
    if (!any) return
    if (Object.keys(byBucket.prop).length)  setProp(v => ({ ...v, ...byBucket.prop }))
    if (Object.keys(byBucket.offer).length) setOffer(v => ({ ...v, ...byBucket.offer }))
    if (Object.keys(byBucket.facts).length) setFacts(v => ({ ...v, ...byBucket.facts }))
    if (Object.keys(byBucket.owner).length) setOwner(v => ({ ...v, ...byBucket.owner }))
    if ('building_code' in byBucket.cls) setCond(c => ({ ...c, building: byBucket.cls.building_code,
      condition: conditionAfterBuilding(deskDef, c.condition, byBucket.cls.building_code, offer.intent, condBaseRef.current.condition) }))
    // Feature rows come from the parse response, not PARSE_FIELDS — route any 'feat:' key directly.
    for (const [k, val] of Object.entries(patch)) if (k.startsWith('feat:')) { byBucket.feat[k] = val; any = true }
    // Lift, parking and heating are answered through featAns, not featStates — found in a live form test
    // (2026-09-17): a paste reading "ცენტრალური გათბობა, ლიფტი" left both pills on "not stated".
    const ans = {}, heatOn = [], heatOff = [], states = {}
    for (const [k, val] of Object.entries(byBucket.feat)) {
      const code = k.slice(5)
      if (code === 'elevator' || code === 'parking') ans[code] = val
      else if (HEATING_CODES.includes(code)) (val === 'true' ? heatOn : heatOff).push(code)
      else states[code] = val
    }
    if (Object.keys(states).length) setFeatStates(v => ({ ...v, ...states }))
    if (Object.keys(ans).length || heatOn.length || heatOff.length)
      setFeatAns(a => ({ ...a, ...ans, heat: [...new Set([...(a.heat || []).filter(c => !heatOff.includes(c)), ...heatOn])] }))
  }
  // paste → AI draft, reviewed right here in place (the modal becomes the review)
  //
  // ⚠️ A SECOND PARSE MAY NOT SILENTLY WIN (owner: "when user parses text and the parses new
  // one it shouldn't overwrite conflicting values silently. user must be asked do you want to
  // replace this with this? yes or no. if there are many ask on each one").
  //
  // What this used to be — worth naming, because the shape recurs — was one long spread of
  // `d.x || current` / `d.x ?? current`. Every field the newest parse mentioned won: over the
  // previous parse, and over anything the agent had typed by hand. Nothing was lost in the
  // database (every observation is kept), but the FORM they were about to save changed
  // underneath them, which is the same defect from where the agent sits.
  async function adminTokenIfNeeded() {
    if (agentId) return null
    try { const { data: { session } } = await supabase.auth.getSession(); return session?.access_token || null } catch { return null }
  }
  const batchSource = () => (sourceKind === 'ai' ? 'owner' : sourceKind)

  // Accepted values → the form, and the baselines with them, so the form shows the record without a reload.
  function applyAcceptedToForm(fields) {
    const patch = formPatchFromAccepted(fields)
    const touched = { facts: Object.keys(patch.facts), offer: Object.keys(patch.offer), owner: Object.keys(patch.owner), featStates: [], featAns: [] }
    for (const code of Object.keys(patch.featStates)) {
      if (code === 'elevator' || code === 'parking') touched.featAns.push(code)
      else if (HEATING_CODES.includes(code)) { if (!touched.featAns.includes('heat')) touched.featAns.push('heat') }
      else touched.featStates.push(code)
    }
    // Nothing accepted (keep / skip / every write failed) → nothing to re-baseline; leaving the flag set would
    // clear the dirty check on the agent's NEXT edit.
    if (Object.values(touched).some(a => a.length)) rebaseAfterBatchRef.current = touched
    if (Object.keys(patch.facts).length) {
      setFacts(f => ({ ...f, ...patch.facts }))
      baselineRef.current = { ...(baselineRef.current || {}), ...patch.facts }
      ownKeysRef.current = [...new Set([...ownKeysRef.current, ...Object.keys(patch.facts)])]
    }
    if (Object.keys(patch.offer).length) setOffer(o => ({ ...o, ...patch.offer }))
    if (Object.keys(patch.owner).length) setOwner(w => ({ ...w, ...patch.owner }))
    const ans = {}, heatOn = [], heatOff = [], states = {}
    for (const [code, v] of Object.entries(patch.featStates)) {
      if (code === 'elevator' || code === 'parking') ans[code] = v
      else if (HEATING_CODES.includes(code)) (v === 'true' ? heatOn : heatOff).push(code)
      else states[code] = v
    }
    if (Object.keys(ans).length || heatOn.length || heatOff.length) {
      const merge = a => ({ ...a, ...ans, heat: [...new Set([...(a.heat || []).filter(c => !heatOff.includes(c)), ...heatOn])] })
      setFeatAns(merge)
      featBaselineRef.current = merge(featBaselineRef.current)
    }
    if (Object.keys(states).length) {
      setFeatStates(x => ({ ...x, ...states }))
      featStatesBaseRef.current = { ...featStatesBaseRef.current, ...states }
    }
  }

  async function decideBatch(proposalId, decisions, acknowledgeStale = []) {
    const batch = batches.find(b => b.proposal_id === proposalId)
    setBatchBusy(true); setErr(null)
    try {
      const r = await decidePasteBatch(proposalId, { decisions, sourceKind: batch?.source_kind || batchSource(), entryId: entryIdRef.current,
        intent: offer.intent, kind: prop.kind, acknowledgeStale, agentId, adminToken: await adminTokenIfNeeded() })
      const chosen = new Set(decisions.map(d => d.field))
      applyAcceptedToForm((r.batch?.fields || []).filter(f => chosen.has(f.field)))
      await reloadBatches()
      setTlRefresh(n => n + 1)
      return { failures: r.failures || [] }
    } catch (e) {
      if (e.status === 409 && e.body?.stale) return { stale: e.body.stale }
      const details = Array.isArray(e.body?.errors) ? e.body.errors.map(x => (typeof x === 'string' ? x : x?.message || x?.field || '')).filter(Boolean) : []
      setErr(details.length ? `${e.message}: ${details.join('; ')}` : e.message)
      return null
    } finally { setBatchBusy(false) }
  }

  async function undoBatch(proposalId, opts) {
    setBatchBusy(true); setErr(null)
    try {
      const r = await undoPasteBatch(proposalId, { ...opts, agentId, adminToken: await adminTokenIfNeeded() })
      // Undone facts return to what they replaced: read the record back rather than guess.
      const undone = r.undone || []
      if (undone.length && batchPropertyId) {
        const mine = await getMyPropertyVersion(batchPropertyId, agentId).catch(() => null)
        const factPatch = {}, featPatch = {}, ans = {}, heatOff = []
        const after = r.features_after || {}
        for (const field of undone) {
          if (field.startsWith('feature:')) {
            const code = field.slice(8)
            // What the flat's record says after the undo (another source may still answer); blank only when nothing does.
            const v = after[code] === true ? 'true' : after[code] === false ? 'false' : ''
            if (code === 'elevator' || code === 'parking') ans[code] = v
            else if (HEATING_CODES.includes(code)) { if (v !== 'true') heatOff.push(code) }
            else featPatch[code] = v
          } else if (!['price', 'currency', 'intent'].includes(field) && !field.startsWith('owner_')) {
            const v = mine?.facts?.[field]?.value
            factPatch[field] = v == null ? '' : String(v)
          }
        }
        const touched = { facts: Object.keys(factPatch), offer: [], owner: [], featStates: Object.keys(featPatch),
          featAns: [...Object.keys(ans), ...(heatOff.length ? ['heat'] : [])] }
        if (Object.values(touched).some(a => a.length)) rebaseAfterBatchRef.current = touched
        if (Object.keys(factPatch).length) { setFacts(f => ({ ...f, ...factPatch })); baselineRef.current = { ...(baselineRef.current || {}), ...factPatch } }
        if (Object.keys(featPatch).length) { setFeatStates(x => ({ ...x, ...featPatch })); featStatesBaseRef.current = { ...featStatesBaseRef.current, ...featPatch } }
        if (touched.featAns.length) {
          const merge = a => ({ ...a, ...ans, heat: (a.heat || []).filter(c => !heatOff.includes(c)) })
          setFeatAns(merge)
          featBaselineRef.current = merge(featBaselineRef.current)
        }
      }
      await reloadBatches()
      setTlRefresh(n => n + 1)
      return { not_undoable: r.not_undoable || [] }
    } catch (e) {
      if (e.status === 409 && e.body?.needs_confirmation) return { needs_confirmation: e.body.needs_confirmation }
      setErr(e.message)
      return null
    } finally { setBatchBusy(false) }
  }

  const batchFieldLabel = field => {
    if (field.startsWith('feature:')) { const f = (defVocab || []).find(x => x.code === field.slice(8)); return f ? featureLabel(f) : field.slice(8) }
    if (field === 'owner_name') return t('desk_owner_contact_name')
    if (field === 'owner_phone') return t('desk_owner_contact_phone')
    if (field === 'owner_email') return t('desk_owner_contact_email')
    if (field === 'price') return t('desk_property_price_negotiable')
    if (field === 'currency') return t('desk_property_currency_label')
    if (field === 'intent') return t('desk_property_intent_label')
    return attrLabel(field)
  }
  const batchSourceLabel = kind => OBSERVED_FROM_OPTIONS.find(o => o.value === kind)?.label || kind || ''

  async function handlePasteParse() {
    if (!pasteText.trim()) return
    setParsing(true); setErr(null)
    try {
      // want_features: this form alone asks the parser for features (owner 2026-09-16, form pastes only).
      const r = await parseListingText({ raw_text: pasteText, channel: 'scheduler', agent_id: agentId, want_features: true })
      if (batchPropertyId) {
        // A property exists: a paste NEVER fills the form directly, even when the parser returned no proposal.
        if (!r.proposal_id) { setErr(t('desk_batch_no_proposal')); return }
        // Owner ruling 2026-09-17: extraction finishing is not acceptance. Nothing enters the form — the review does.
        await reviewPasteBatch({ proposalId: r.proposal_id, propertyId: batchPropertyId,
          form: formSnapshotForBatch({ facts, offer, owner, featStates, featAns }), sourceKind: batchSource(),
          agentId, adminToken: await adminTokenIfNeeded() })
        await reloadBatches()
        setPasteText('')
        return
      }
      const d = r.draft || {}
      const prev = proposalId
      setProposalId(r.proposal_id)
      // the new draft REPLACES this window's previous auto-fill draft (it read the same texts plus any added since);
      // a draft the window was opened on is left for Save to retire, with its WhatsApp / owner provenance intact
      if (r.proposal_id) parsedHereRef.current.add(r.proposal_id)
      if (prev && prev !== r.proposal_id && parsedHereRef.current.has(prev)) {
        parsedHereRef.current.delete(prev)
        dismissProposal(prev).catch(e => console.warn('[auto-fill] could not retire the previous draft', prev, e?.message))
      }
      const cur = { prop, offer, facts, owner, cls: { building_code: cond.building },
        feat: Object.fromEntries(Object.entries(featStates).map(([k, val]) => ['feat:' + k, val])) }
      // Features the parser read (server sanitises to known codes + strict booleans). Same rule as every
      // other field: a blank fills, an agreement is silent, a contradiction of the form's answer asks.
      const vocabByCode = new Map((defVocab || []).map(f => [f.code, f]))
      const featRows = Object.entries(d.features && typeof d.features === 'object' ? d.features : {})
        .filter(([code, v]) => vocabByCode.has(code) && (v === true || v === false))
        .map(([code, v]) => ({ key: 'feat:' + code, label: featureLabel(vocabByCode.get(code)),
          // lift/parking/heating are read from featAns — the answers the agent actually sees
          current: code === 'elevator' || code === 'parking' ? (featAns[code] || '')
            : HEATING_CODES.includes(code) ? ((featAns.heat || []).includes(code) ? 'true' : '')
            : (featStates[code] || ''),
          incoming: v ? 'true' : 'false' }))
      const { apply, conflicts } = splitParse([...PARSE_FIELDS.map(fd => ({
        key: fd.key, label: fd.label(),
        current: cur[fd.bucket][fd.key],
        incoming: fd.read(d),
      })), ...featRows])
      applyParsePatch(apply)
      setAiFeatureCodes(new Set(featRows.map(x => x.key.slice(5))))
      // Apart-hotel is not in the capture vocabulary (property_type), so it rides the apartment type tag
      // instead — the same control the agent sees and can untick. A yes only; silence and no change nothing.
      if (d.features && d.features.type_aparthotel_unit === true)
        setTypeTags(tt => tt.includes('type_aparthotel_unit') ? tt : [...tt, 'type_aparthotel_unit'])
      // Only what actually disagrees reaches the agent. A blank being filled, a field the parse
      // did not mention, and a value it merely repeats are all silent — the owner's own
      // constraint: "do not generate fake conflicts because one source did not provide a value".
      setParseConflicts(conflicts)
      setParseAnswers({})
      setSourceKind('ai')
      // The proposal is already pending server-side at this point (evidence-first, same as the
      // demand intake) — reflect it in Recent entries / the drafts panel NOW, not only after Save,
      // so a paste that's parsed but not yet reviewed doesn't silently vanish from the list.
      onDraftCreated?.()
      // The texts STAY (owner 2026-11-03: "text always stays there and is part of it") — the agent adds the next one and
      // presses again; the new draft replaces this one. Remembered as read, so they no longer count as unsaved work.
      setParsedText(pasteText)
    } catch (e) { setErr(e.message) }
    finally { setParsing(false) }
  }

  // Sequential on purpose: one raw-body request per file (15mb/request server cap),
  // and position is assigned server-side from the current active count.
  // Returns the items that FAILED (review 2026-09-16): one bad file used to stop the loop, and the
  // caller had already emptied the queue, so that file and every one after it were simply lost.
  async function uploadFiles(listingId, items) {   // items: [{ file, kind }]
    setImgBusy(true); setImgErr(null)
    const failed = []
    try {
      for (const item of items) {
        const { file, kind } = item
        try {
        // Photos are shrunk in the browser first (see imageDownscale.js: a phone shot is 4-12mb
        // and no surface renders past 2560px). Documents are sent byte-for-byte — re-encoding
        // evidence destroys the small print someone will need to read.
        // ⚠️ ORDER IS THE WHOLE POINT. downscalePhoto re-encodes anything over 1 MB through a
        // canvas, and a canvas carries no metadata — so the owner's "read it into the DB" ruling
        // was being defeated in the browser, by us, before the server ever saw the file. Read
        // first, then shrink. Never blocks an upload: a photo we cannot read is still a photo.
        // a converted iPhone HEIC brings the metadata read from the HEIC itself (handlePickImages) — the JPEG has none
        const meta = kind === 'document' ? null : (item.meta !== undefined ? item.meta : await readPhotoMeta(file))
        const send = kind === 'document' ? file : (await downscalePhoto(file, photoSize)).file
        const r = await uploadListingImage(listingId, send, { kind, meta })
        setImages(prev => prev.some(i => i.id === r.id) ? prev : [...prev, { id: r.id, url: r.url, kind: r.kind || kind }])
        } catch (e) {
          failed.push(item)
          // a capture (see refusedCapture): not an error to show — the photos go to the agent's version instead
          if (/scrape_immutable/.test(e?.message || '')) {
            setRefusedCapture(listingId)
            setBaseListingId(b => b || listingId)
            setVersionOwed(true)
          } else setImgErr(e.message)
        }
      }
    } finally { setImgBusy(false) }
    return failed
  }

  // What the queued photos will actually weigh at the chosen size — measured by encoding them,
  // never a guessed ratio (see estimateTotal). Only meaningful BEFORE the first Record, when
  // files are still waiting in pendingImgs; after that a pick uploads immediately and there is
  // nothing left to estimate. Aborted on every change so switching sizes quickly cannot leave
  // an older, slower pass writing a stale number over a newer one.
  useEffect(() => {
    const queued = pendingImgs.filter(p => p.kind !== 'document').map(p => p.file)
    if (!queued.length) { setSizeEst(null); return }
    const ac = new AbortController()
    const id = setTimeout(() => {
      estimateTotal(queued, photoSize, ac.signal)
        .then(r => { if (!ac.signal.aborted) setSizeEst(r) })
        .catch(() => {})   // AbortError, or an undecodable file — the label just stays absent
    }, 150)
    return () => { ac.abort(); clearTimeout(id) }
  }, [pendingImgs, photoSize])

  function pickPhotoSize(key) {
    setPhotoSize(key)
    try { localStorage.setItem(PHOTO_SIZE_LS, key) } catch { /* private mode — the choice just won't persist */ }
  }

  async function handlePickImages(fileList, kind = 'photo') {
    // photos are images only; documents also take PDFs/sheets/etc (server validates mime).
    // ⚠️ isHeic: a .heic can arrive with an EMPTY type, and `type.startsWith('image/')` alone dropped it silently.
    const picked = [...fileList].filter(f => kind === 'document' || f.type.startsWith('image/') || isHeic(f))
    if (!picked.length) return
    // iPhone HEIC → JPEG here, before anything else sees the file (2026-10-21: Chrome showed broken tiles and
    // could not shrink a byte). Date/GPS are read from the HEIC first — the JPEG carries none. Documents are
    // never re-encoded (evidence), so only photos are converted.
    const heics = kind === 'document' ? 0 : picked.filter(isHeic).length
    if (heics) setHeicBusy(n => n + heics)
    let items
    try {
      items = await Promise.all(picked.map(async f => {
        if (kind === 'document' || !isHeic(f)) return { file: f, kind }
        const meta = await readPhotoMeta(f)
        return { file: await heicToJpeg(f), kind, meta }
      }))
    } finally { if (heics) setHeicBusy(n => n - heics) }
    const preview = f => f.type.startsWith('image/') && !isHeic(f) ? URL.createObjectURL(f) : null
    if (saved?.listingId && !onCapture) {
      // Failed files stay queued with a preview, like the first-save path, instead of being dropped.
      uploadFiles(saved.listingId, items).then(failed => {
        if (failed.length) setPendingImgs(p => [...p, ...failed.map(it => ({ ...it, preview: preview(it.file) }))])
      })
    }
    else {
      setPendingImgs(p => [...p, ...items.map(it => ({ ...it, preview: preview(it.file) }))])
      // on a capture the picks ARE the change: the version is recorded now, so the agent sees their photos on it at once
      if (onCapture) setVersionOwed(true)
    }
  }

  // Retry the queued files that failed to upload (review 2026-09-17: they were kept, with no way to send them).
  async function retryUploads() {
    const lid = saved?.listingId
    if (!lid || !pendingImgs.length) return
    // on a capture a retry is the Record that makes the version (it uploads the queue there), never the ad again
    if (onCapture) { setVersionOwed(true); return }
    const queued = pendingImgs
    const failed = await uploadFiles(lid, queued)
    queued.forEach(p => { if (!failed.includes(p) && p.preview) URL.revokeObjectURL(p.preview) })
    setPendingImgs(failed)
  }

  async function removeImg(id) {
    try { await removeListingImage(id); setImages(p => p.filter(i => i.id !== id)) }
    catch (e) { setImgErr(e.message) }
  }

  // photo turns out to show a cadastral code → make it a document (and back)
  async function moveImg(id, kind) {
    try { await setListingImageKind(id, kind); setImages(p => p.map(i => i.id === id ? { ...i, kind } : i)) }
    catch (e) { setImgErr(e.message) }
  }

  // drag-reorder within the PHOTOS grid; documents keep their relative order after.
  // Optimistic local swap, then persist the full id order (position = index server-side).
  function dropPhotoAt(targetIdx) {
    const from = dragIdx.current; dragIdx.current = null
    if (from == null || from === targetIdx) return
    const photos = images.filter(i => (i.kind || 'photo') === 'photo')
    const docs = images.filter(i => i.kind === 'document')
    const moved = photos.splice(from, 1)[0]
    photos.splice(targetIdx, 0, moved)
    const next = [...photos, ...docs]
    setImages(next)
    if (saved?.listingId) reorderListingImages(saved.listingId, next.map(i => i.id)).catch(e => setImgErr(e.message))
  }

  // (The Nominatim address type-ahead that lived here was removed 2026-08-19 when the
  // Desk adopted AddressBlock: the street now comes from our own gazetteer, typo-tolerant,
  // through streets/resolve. Nominatim survives only as geocode/suggest's last fallback.)



  const featCodeMode = code => answerModeOf((defVocab || []).find(f => f.code === code))
  const pendingChipStates = () => {
    const merged = { ...(prop.kind === 'land' ? {} : (changedChipStates(featBaselineRef.current, featAns) || {})),
      // Only codes the form still OFFERS for the current kind: an answer typed before switching to land
      // stays in state (a misclick can flip back) but never rides along invisibly.
      ...Object.fromEntries(Object.entries(changedFeatureStates(featStatesBaseRef.current, featStates, featCodeMode) || {})
        .filter(([code]) => offeredFeatureCodes.has(code))) }
    return Object.keys(merged).length ? merged : undefined
  }
  // the Record a capture's photos are waiting for (see refusedCapture) — run once the queue is in state
  useEffect(() => {
    if (!versionOwed || saving || !pendingImgs.length) return
    setVersionOwed(false)
    if (canSave) handleSave()
    else setImgErr(t('desk_pm_photos_need_version'))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleSave/canSave are this render's; the trigger is the owed flag
  }, [versionOwed, saving, pendingImgs.length])

  async function handleSave() {
    if (!canSave || saving) return
    const chipStates = pendingChipStates()
    if (chipStates && Object.keys(chipStates).length > MAX_CHIP_ANSWERS) {
      setErr(tLog('desk_feature_capture_cap', { n: MAX_CHIP_ANSWERS })); return
    }
    // A mistyped year used to be dropped silently while the save said ✓. Now it stops the save.
    if (prop.kind !== 'land' && !yearBuiltValid(facts.year_built)) { setErr(t('desk_facts_year_invalid')); return }
    const retractCodes = featureRetractCodes(
      { featStates: featStatesBaseRef.current, featAns: featBaselineRef.current, rungs: condBaseRef.current, typeTags: typeBaseRef.current },
      { featStates, featAns, rungs: cond, typeTags }, { building: prop.kind !== 'land' })
    setSaving(true); setErr(null); setSaveWarnings([])
    try {
      const numOrOmit = v => (v === '' || v == null || !isFinite(+v) ? undefined : +v)
      // A year outside a building's plausible span is a typo, not a fact: dropped, never saved.
      const yearOrOmit = v => { const n = numOrOmit(v); return n != null && yearBuiltValid(v) ? n : undefined }
      const boolOrOmit = v => (v === '' ? undefined : v === 'true')
      // Type tags toggled under a PREVIOUS kind aren't rendered anymore but still sit
      // in state — only the current kind's own chips may save.
      const changedKeys = new Set(Object.keys(factsDiff(baselineRef.current, facts)))
      if (changedKeys.has('balcony_count')) changedKeys.add('has_balcony')   // derived from it
      const onlyChanged = full => Object.fromEntries(Object.entries(full).filter(([k]) => changedKeys.has(k)))
      // a fact field the card does not show for this kind × subtype × deal is never sent (see `shownF`)
      // the apart-hotel terms belong to an apart-hotel: unticking it (or another kind) leaves them out of the save
      const ahOnSave = prop.kind === 'apartment' && typeTags.includes('type_aparthotel_unit')
      const off = f => !shownF(f) || (APARTHOTEL_FACT_KEYS.includes(f) && !ahOnSave)
      // T24/0846 (owner: "clearing a field removes the claim"): the hand-written fields the agent
      // emptied. Retracted on the server; only a claim of THEIRS can be removed this way, so a
      // cleared card-backfill sends nothing and simply shows the card again.
      // + the ceiling's own rule (lib/ceilingFacts.js): "varies" retracts their single height, anything
      // else retracts their range — so a reopen never shows a stale half of the other model.
      const cleared = [...new Set([...clearedOwnKeys(baselineRef.current, facts, ownKeysRef.current),
                                   ...ceilingRetracts(facts, ownKeysRef.current, { isLand })])]
            const kindTagCodes = new Set((PROP_TYPE_TAGS[prop.kind] || []).map(([code]) => code))
      const liveTypeTags = typeTags.filter(c => kindTagCodes.has(c))
      let sentFacts = {}
      const r = await saveListingEntry({
        origin: proposalId ? 'ai_draft' : 'manual',
        // ai_draft keys its upsert on proposal_id itself (core_manual.js) — so a repeat
        // Record lands on the SAME source listing for both origins, never a duplicate.
        entry_id: proposalId ? undefined : entryIdRef.current,
        proposal_id: proposalId || undefined,
        // The capture this version is based on — see "A CAPTURE IS IMMUTABLE" at the state above. Sent only
        // when the opened ad is KNOWN to be a capture; the server also checks it sits on the same property.
        based_on_listing_id: baseListingId && ((openedSource && !OUR_LISTING_SOURCES.includes(openedSource)) || baseListingId === refusedCapture)
          ? baseListingId : undefined,
        source_kind: sourceKind,
        // 0064: stamps claims with this agent's party (their own version) and
        // auto-adds the property to their inventory (My Properties).
        agent_id: agentId || undefined,
        // Known-property anchor: once the entity exists (opened existing, or after the
        // first Record), every further Record targets THAT id — re-resolving identity
        // by address would mint a duplicate property row (B25 failure mode) and, before
        // this, quietly leaked an orphan property row on every repeat Record.
        property_id: existing?.propertyId || saved?.propertyId || undefined,
        property: {
          address: prop.address.trim() || undefined, district_code: prop.district_code || undefined,
          // the picked city (2026-09-17): without it a flat with a city but no district or pin was saved with no city
          city: districtCity || undefined,
          cadastral_code: prop.cadastral_code.trim() || undefined, kind: prop.kind,
          lat: prop.lat ?? undefined, lng: prop.lng ?? undefined,
          // T35 — WHICH canonical street, not just its name. Present only when the agent picked
          // a gazetteer row; AddressBlock clears it on typing, on a name read off the pin, on
          // accepting the pin's offer, and on Change, because a stale id files the correction
          // against the street chosen BEFORE. The server turns it into a property_street row
          // with basis='manual' plus a relink journal entry (0823).
          street_id: prop.street_id || undefined,
          street_number: prop.street_number || undefined,
        },
        // review 2026-09-23 (bug 3): price only when it (or its currency) differs from the prefill —
        // an omitted price keeps the stored one (upsert_listing, 0573), so an untouched card price is
        // no longer re-stamped as this agent's on every Record. ⚠️ intent/currency are still always
        // sent: core_prepare defaults an omitted intent to 'sale' and currency to 'USD', which WOULD
        // overwrite a rent listing. Clearing a price has no server path (price<=0 is refused), so an
        // emptied price is simply not sent.
        listing: { intent: offer.intent, currency: offer.currency,
          price: (!offerBaseRef.current || offer.price !== offerBaseRef.current.price
                  || offer.currency !== offerBaseRef.current.currency) ? numOrOmit(offer.price) : undefined },
        // ⚠️ TOP-LEVEL, and NEVER inside `address` — the same rule core_fill.js follows and
        // states at length. AddressBlock promises "მხოლოდ აგენტისთვის — არსად ქვეყნდება",
        // and that promise holds only because of where this value goes: listing.meta, which
        // neither the share page nor the repost payload reads. `address` IS published to
        // myhome/ss/korter on a repost, so folding a flat number into it — the natural way
        // to write an address — would post the owner's door number to three public portals.
        // Added 2026-08-19 with AddressBlock: the control offers the field, so the save has
        // to carry it. A control that collects a value nobody stores is a lie to the agent.
        ...(prop.entrance_apt?.trim() ? { entrance_apt: prop.entrance_apt.trim() }
          // cleared a detail that was saved before → '' tells the server to remove it
          : entranceBaseRef.current ? { entrance_apt: '' } : {}),
        ...(retractCodes.length && { features_retract: retractCodes }),
        ...(cleared.length && { facts_retract: cleared }),
        // Building-shaped facts are omitted for land (their inputs are hidden, but a
        // value typed before a kind switch could still be sitting in state).
        // 🔴 ONLY WHAT THE AGENT CHANGED leaves the form as a claim (owner 2026-09-03). The
        // payload below is built in full and then cut to the keys that differ from the prefill
        // baseline — otherwise opening card B and pressing Record turns B's backfill into "the
        // agent asserted this" for every field they never touched, and their version becomes
        // whatever card they last saved from. No baseline (a NEW entry) means everything typed
        // is theirs, which factsDiff already answers.
        // 2026-09-24: "hidden" now means "not shown for this kind × subtype × deal" by the shared intake definition (core
        // 2404) — the same drop rule as before, which named land only. A field is shown whenever it holds a recorded value.
        facts: (sentFacts = onlyChanged({ rooms: off('rooms') ? undefined : numOrOmit(facts.rooms), bedrooms: off('bedrooms') ? undefined : numOrOmit(facts.bedrooms),
                 bathrooms: off('bathrooms') ? undefined : numOrOmit(facts.bathrooms), area_sqm: numOrOmit(facts.area_sqm),
                 kitchen_area: off('kitchen_area') ? undefined : numOrOmit(facts.kitchen_area), ...ceilingPayload(facts, { isLand }),
                 floor: off('floor') ? undefined : numOrOmit(facts.floor), floors_total: off('floors_total') ? undefined : numOrOmit(facts.floors_total),
                 condition: isLand ? undefined : (facts.condition.trim() || undefined),
                 // facts.project → claim `project` (core_prepare FACT_KEYS) — a building
                 // series makes no sense on land, same omission rule as condition.
                 project: off('project') ? undefined : (facts.project || undefined),
                 year_built: isLand ? undefined : yearOrOmit(facts.year_built),
                 balcony_count: off('balcony_count') ? undefined : numOrOmit(facts.balcony_count),
                 balcony_area_sqm: off('balcony_area_sqm') ? undefined : numOrOmit(facts.balcony_area_sqm),
                 // has_balcony is DERIVED from count, not asked separately — a count field
                 // already says yes/no; asking twice just invites disagreement between them.
                 has_balcony: off('balcony_count') || facts.balcony_count === '' ? undefined : Number(facts.balcony_count) > 0,
                 has_terrace: off('has_terrace') ? undefined : boolOrOmit(facts.has_terrace),
                 power_capacity_kw: off('power_capacity_kw') ? undefined : numOrOmit(facts.power_capacity_kw),
                 functional_zone: off('functional_zone') ? undefined : (String(facts.functional_zone || '').trim() || undefined),
                 land_area_sqm: off('land_area_sqm') ? undefined : numOrOmit(facts.land_area_sqm),
                 living_room_area_sqm: off('living_room_area_sqm') ? undefined : numOrOmit(facts.living_room_area_sqm),
                 loggia_area_sqm: off('loggia_area_sqm') ? undefined : numOrOmit(facts.loggia_area_sqm),
                 storeroom_area_sqm: off('storeroom_area_sqm') ? undefined : numOrOmit(facts.storeroom_area_sqm),
                 porch_area_sqm: off('porch_area_sqm') ? undefined : numOrOmit(facts.porch_area_sqm),
                 building_material: off('building_material') ? undefined : (String(facts.building_material || '').trim() || undefined),
                 parking_deeded: off('parking_deeded') || facts.parking_deeded === '' ? undefined : Number(facts.parking_deeded),
                 metro_station: off('metro_station') ? undefined : (String(facts.metro_station || '').trim() || undefined),
                 room_unit_kind: off('room_unit_kind') || !['apartment', 'house'].includes(facts.room_unit_kind) ? undefined : facts.room_unit_kind,
                 room_unit_rooms: off('room_unit_rooms') ? undefined : numOrOmit(facts.room_unit_rooms),
                 room_unit_area_sqm: off('room_unit_area_sqm') ? undefined : numOrOmit(facts.room_unit_area_sqm),
                 room_unit_occupants: off('room_unit_occupants') ? undefined : numOrOmit(facts.room_unit_occupants),
                 land_area_unit: facts.land_area_unit || undefined,
                 subtype: (KIND_SUBTYPES[prop.kind] || []).includes(facts.subtype) ? facts.subtype : undefined })),
        // apartment-class + Tier A type tags ride the ingest tagKeys path → commit_feature (0078).
        // the condition block (building / condition / class) holds building-only codes — never valid on land, even if it was
        // picked before the kind switch.
        // Only a CHANGED rung and newly added type chips: both are prefilled now, and re-sending an
        // untouched prefill would stamp someone else's answer as this agent's.
        tags: (() => {
          const cls = isLand ? [] : CONDITION_ROW_KEYS.map(k => cond[k]).filter((c, i) => c && c !== condBaseRef.current[CONDITION_ROW_KEYS[i]])
          const added = liveTypeTags.filter(c => !typeBaseRef.current.includes(c))
          return cls.length || added.length ? [...cls, ...added] : undefined
        })(),
        owner: { name: owner.name.trim() || undefined, phone: owner.phone.trim() || undefined, email: owner.email.trim() || undefined },
        chip_states: chipStates,
      })
      // What the server refused stays a change, so the next save retries it (review 2026-09-17: a partial failure
      // used to be rebased as saved and silently dropped from every later save).
      const featFailed = failedFeatureCodes(r, retractCodes)
      {
        const prev = featBaselineRef.current
        const next = { ...featAns, heat: [...featAns.heat] }
        for (const k of ['elevator', 'parking']) if (featFailed.has(k)) next[k] = prev[k]
        next.heat = [...new Set([...next.heat.filter(c => !featFailed.has(c) || (prev.heat || []).includes(c)), ...(prev.heat || []).filter(c => featFailed.has(c))])]
        featBaselineRef.current = next
      }
      // The next save compares against THIS save, and a fact saved now can be cleared later.
      const { failed: factsFailed, retractFailed } = failedFactKeys(r)
      const rebased = rebaseAfterSaveWithFailures({ facts, prevBaseline: baselineRef.current, ownKeys: ownKeysRef.current,
        sent: sentFacts, retracted: r.retracted, failed: factsFailed, retractFailed })
      baselineRef.current = rebased.baseline
      ownKeysRef.current = rebased.ownKeys
      const needsRetry = rebased.retry || featFailed.size > 0
      // A cleared answer someone ELSE recorded is still on the flat — put it back on screen.
      const notYours = Array.isArray(r.features_not_yours) ? r.features_not_yours : []
      const prevStates = featStatesBaseRef.current
      const nextStates = { ...featStates }
      for (const code of notYours) if (prevStates[code] && !nextStates[code]) nextStates[code] = prevStates[code]
      if (notYours.length) setFeatStates(nextStates)
      const baseStates = { ...nextStates }
      for (const code of featFailed) { if (prevStates[code] == null) delete baseStates[code]; else baseStates[code] = prevStates[code] }
      featStatesBaseRef.current = baseStates
      if (!isLand) condBaseRef.current = Object.fromEntries(CONDITION_ROW_KEYS.map(k =>
        [k, featFailed.has(cond[k]) || featFailed.has(condBaseRef.current[k]) ? condBaseRef.current[k] : cond[k]]))
      typeBaseRef.current = typeTags.filter(c => !featFailed.has(c)).concat(typeBaseRef.current.filter(c => featFailed.has(c)))
      entranceBaseRef.current = prop.entrance_apt?.trim() || ''
      if (offerBaseRef.current) offerBaseRef.current = { price: offer.price, currency: offer.currency }   // review 2026-09-23
      setSaveWarnings(saveWarningsFrom(r))
      // Workspace stays OPEN: first record anchors the entity, later records are new
      // observations of it ("five minutes later: actually it's renovated").
      setSaved({ propertyId: r.propertyId || saved?.propertyId, listingId: r.listingId || saved?.listingId })
      // Save consumed `proposalId`; every OTHER draft this window touched is now superseded — the opened one (when a later
      // auto-fill replaced it) and any earlier auto-fill. Dismissed, not deleted: the rows and their provenance stay.
      {
        const superseded = [openedDraftRef.current, ...parsedHereRef.current].filter(id => id && id !== proposalId)
        parsedHereRef.current.clear(); openedDraftRef.current = null
        if (superseded.length) Promise.all(superseded.map(id => dismissProposal(id).catch(() => null))).then(() => onDraftCreated?.())
      }
      // Anything that did not land keeps the form dirty, so Save stays available to retry it.
      setLastRec(needsRetry ? '__retry__' : snapshot())
      setTlRefresh(n => n + 1)
      const lid = r.listingId || saved?.listingId
      // First Record on a capture: the workspace moves from the ad to MY VERSION. The ad's photos stay on
      // screen read-only (basePhotos); the editable gallery becomes the version's own photos (earlier
      // uploads included), and the queued picks below upload onto the version, never the ad.
      // (the capture is the listing the workspace pointed at — the opened advert, or one the server refused as a capture)
      if (onCapture && r.listingId && r.listingId !== saved?.listingId) {
        setBasePhotos(images.filter(i => (i.kind || 'photo') === 'photo'))
        setOriginals([])
        try {
          const cur = await listListingImages(r.listingId)
          setImages(cur.images.map(i => ({ id: i.id, url: i.url, kind: i.kind || 'photo' })))
        } catch { setImages([]) }
      }
      if (lid && !saved) {
        // first Record may land on an EXISTING listing (upsert) — show its images too
        try {
          const cur = await listListingImages(lid, { includeOriginals: true })
          setImages(cur.images.map(i => ({ id: i.id, url: i.url, kind: i.kind || 'photo' })))
          setOriginals((cur.originals || []).map(i => ({ id: i.id, url: i.url })))
        } catch { /* best-effort */ }
      }
      // ⚠️ never the capture (see refusedCapture): a save that did not hand back the agent's version keeps the photos queued
      const capId = onCapture ? saved?.listingId : null
      if (lid && pendingImgs.length && lid === capId) setImgErr(t('desk_pm_photos_need_version'))
      else if (lid && pendingImgs.length) {
        const queued = pendingImgs
        const failed = await uploadFiles(lid, queued) // per-file try/catch → imgErr, never fails the Save
        // Keep what failed in the queue (with its preview) so it can be retried; release the rest.
        queued.forEach(p => { if (!failed.includes(p) && p.preview) URL.revokeObjectURL(p.preview) })
        setPendingImgs(failed)
      }
      const pid = r.propertyId || saved?.propertyId
      if (pid) {
        listCooperations({ propertyId: pid }).then(setCoops).catch(() => {})
        getPropertyContacts(pid).then(setPcontacts).catch(() => {})
      }
      onSaved?.()
    } catch (e) { setErr(e.message) }
    finally { setSaving(false) }
  }

  // Archive this pending draft without saving it — the proposal stays in the DB (status
  // 'dismissed', migration 0045), it just leaves the "needs review" list. Only meaningful for a
  // draft that has a persisted proposal (proposalId); a plain manual entry has nothing to archive.
  async function handleDismiss() {
    if (!proposalId) return
    setArchiving(true); setErr(null)
    try {
      await dismissProposal(proposalId)
      onSaved(); onClose()   // onSaved reloads history → the draft drops from pending + the count
    } catch (e) { setErr(e.message) }
    finally { setArchiving(false) }
  }

  // Shape-aware facts (supply twin of the client form's shape gating): land has no
  // building, so building-only numerics, the condition/class ladders, and balcony/
  // terrace hide — plot area (area_sqm) is the one size that remains. Hidden fields
  // are ALSO dropped from the Save payload, so a value typed before a kind switch
  // never rides along invisibly (the state itself is kept, in case the switch was
  // a misclick and the agent flips back).
  const isLand = prop.kind === 'land'
  // ── The shared intake question list (owner 2026-09-24, decision E; core 2394/2404, surface 'desk') ──────────────
  // Which fact fields this kind × subtype × deal has, and which are required, now come from the same definition the
  // sentavus.com owner form reads. How a value is SAVED is untouched. Until the definition arrives (or if it cannot be
  // read) the card keeps its own pre-catalogue rule below, so a network failure never changes the card.
  const [deskDef, setDeskDef] = useState(null)
  useEffect(() => {
    let live = true
    getIntakeDefinition({ kind: prop.kind, subtype: facts.subtype, intent: offer.intent })
      .then(d => { if (live) setDeskDef(d && Array.isArray(d.questions) ? d : null) })
      .catch(() => { if (live) setDeskDef(null) })
    return () => { live = false }
  }, [prop.kind, facts.subtype, offer.intent])
  // the card's own rule before the catalogue (kept as the fallback, verbatim in meaning)
  const legacyShown = (f) => {
    if (['power_capacity_kw'].includes(f)) return ['commercial', 'office'].includes(prop.kind)
    if (f === 'functional_zone') return ['land', 'commercial', 'office'].includes(prop.kind)
    if (f === 'land_area_sqm') return true
    if (f.startsWith('room_unit_')) return prop.kind === 'room'
    return !isLand
  }
  // A RECORDED value keeps its field visible whatever the definition says (nothing on record is put out of reach); a
  // value typed and then stranded by a kind switch is hidden and left out of the save, as before.
  const factRules = deskFieldRules(deskDef, baselineRef.current || {}, legacyShown,
    { building: cond.building, condition: cond.condition, intent: offer.intent, kind: prop.kind, subtype: facts.subtype })
  // Metro only in Tbilisi (owner 2026-09-24): the one city with a metro. A recorded station stays on screen.
  const metroHere = placeHasMetro(prop.district_code, districts, districtCity)
  const shownF = f => f === 'metro_station'
    ? factRules.shown(f) && (metroHere || String(baselineRef.current?.metro_station || '').trim() !== '')
    : factRules.shown(f)
  const condLabel = code => [...PROP_STATE, ...APT_CLASS].find(([c]) => c === code)?.[1]
    || (defVocab || []).find(f => f.code === code)?.[getLanguage() === 'ka' ? 'name_ka' : 'name_en'] || code
  const conditionRows = deskConditionRows(deskDef, cond, { intent: offer.intent, recorded: condBaseRef.current,
    lang: getLanguage() === 'ka' ? 'ka' : getLanguage() === 'ru' ? 'ru' : 'en', fallbackLabel: condLabel })
  // Tap again to clear. A new building drops a condition that building does not have (owner-form rule).
  const pickCond = (key, code) => setCond(c => {
    const next = { ...c, [key]: c[key] === code ? '' : code }
    if (key === 'building') next.condition = conditionAfterBuilding(deskDef, c.condition, next.building, offer.intent, condBaseRef.current.condition)
    next.apartment_class = classAfterCondition(next, condBaseRef.current.apartment_class)
    return next
  })
  const reqMark = (f) => factRules.required(f) ? <span className="ml-0.5 text-destructive" aria-hidden="true">*</span> : null
  // Codes another control on this form already asks — one fact, one control.
  const captureExclude = useMemo(() => new Set(['elevator', 'parking', 'heat_unspecified', ...HEATING_CODES,
    ...CONDITION_CODES, ...(PROP_TYPE_TAGS[prop.kind] || []).map(([c]) => c)]), [prop.kind])
  const offeredFeatureCodes = useMemo(() => new Set(captureFeatureGroups(defVocab, prop.kind, kindCaps, captureExclude)
    .flatMap(([, rows]) => rows.map(f => f.code))), [defVocab, prop.kind, kindCaps, captureExclude])
  // Both ends typed and the lowest is not below the highest — held back from the save, named in the form.
  const ceilingRangeBad = isCeilingRangeBad(facts)
  // area_sqm is hidden for land (review 2026-09-17): ingestion keeps the plot area when both are sent, so two area
  // boxes on a plot competed. The plot area (with its unit) below is the one control.
  const LAND_HIDDEN_FACTS = new Set(['rooms', 'bedrooms', 'bathrooms', 'kitchen_area', 'area_sqm',
    'floor', 'floors_total', 'ceiling_height', 'ceiling_variation', 'ceiling_height_min', 'ceiling_height_max'])

  // Agency Agreement needs someone to agree WITH — the ranked contact resolver's default
  // party (owner phone recorded above usually resolves it). is_agent contacts are never it.
  const coopContact = pcontacts?.contacts?.find(c => c.party_id === pcontacts.default_party_id && !c.is_agent) || null

  // Meeting seed — the same core-pick shape the lineage modal builds (property +
  // ranked contact + agreed cooperation terms so commission arrives pre-filled),
  // assembled from this card's own state. Powers the 📅＋ chip in the MEETING section.
  const buildMeetingSeed = () => {
    // core intent → the scheduler's own deal_type vocabulary (viewings table).
    // girao/ijara pass through under their own names rather than being flattened into
    // trade or lease: a viewing on a girao is not a sale viewing, and DealsTab's
    // rented_out/sold outcome split reads this value (0219).
    const DEAL = { sale: 'trade', rent: 'lease', daily_rent: 'daily_rent', girao: 'girao', ijara: 'ijara' }
    const numOrNull = v => (v === '' || v == null || !isFinite(+v) ? null : +v)
    const activeCoop = coops.find(c => c.status !== 'rejected' && c.status !== 'ended') || null
    const dflt = pcontacts?.contacts?.find(c => c.party_id === pcontacts.default_party_id) || null
    return {
      __source: 'core', id: saved?.listingId || null, property_id: saved?.propertyId || null,
      address: prop.address.trim() || lineage?.merged?.address || null,
      rooms_count: numOrNull(facts.rooms), apt_floor: numOrNull(facts.floor),
      total_floors: numOrNull(facts.floors_total), area_sq_m: numOrNull(facts.area_sqm),
      deal_type: DEAL[offer.intent] || offer.intent, price: numOrNull(offer.price), price_unit: offer.currency,
      contact_name: dflt ? dflt.name : (owner.name.trim() || null),
      contact_phone: dflt ? dflt.phone : (owner.phone.trim() || null),
      contacts_ranked: pcontacts?.contacts?.length ? pcontacts.contacts : null,
      default_contact_party_id: pcontacts?.default_party_id || null,
      cooperation: coopMeetingSeed(activeCoop),
    }
  }

  // ── Redesign stage 1: section status for the jump navigation (PropertyModalNav) ──
  // Only fields this kind asks (review 2026-09-17: land counted nine building fields and stayed at 0/9).
  const FACT_KEYS_COUNTED = prop.kind === 'land' ? ['land_area_sqm', 'functional_zone']
    : ['rooms', 'bedrooms', 'bathrooms', 'area_sqm', 'kitchen_area', 'floor', 'floors_total', 'ceiling_height', 'year_built']
  const factsAnswered = FACT_KEYS_COUNTED.filter(k => String(facts[k] ?? '').trim() !== '').length
  const errSection = err && err === t('desk_facts_year_invalid') ? 'facts' : null
  const factsNeedYou = errSection === 'facts' || saveWarnings.some(([k]) => (WARNING_SECTION[k] || 'facts') === 'facts')
  const navSections = [
    { id: 'property', group: 'record', label: t('desk_pm_nav_property'), status: canSave ? 'done' : 'needs' },
    !embedded && { id: 'terms', group: 'record', label: t('desk_pm_nav_terms'), status: String(offer.price ?? '') !== '' ? 'done' : 'empty' },
    { id: 'facts', group: 'record', label: t('desk_pm_nav_facts'), status: factsNeedYou ? 'needs' : factsAnswered ? 'done' : 'empty',
      count: `${factsAnswered}/${FACT_KEYS_COUNTED.length}` },
    !embedded && { id: 'photos', group: 'record', label: t('desk_pm_nav_photos'), status: imgErr || (saved && pendingImgs.length) ? 'needs' : (images.length || pendingImgs.length) ? 'done' : 'empty' },   // 2026-10-21: picked-but-not-yet-recorded photos ARE filled in (they upload with the Record) — grey read as "the photos did not take"
    { id: 'owner', group: 'record', label: t('desk_pm_nav_owner'), status: owner.name.trim() || owner.phone.trim() || owner.email.trim() ? 'done' : 'empty' },
    !embedded && { id: 'observed', group: 'record', label: t('desk_pm_nav_observed'), status: 'done' },
    !embedded && { id: 'paste', group: 'in', label: t('desk_pm_nav_paste'),
      status: parseConflicts.length || batches.some(b => b.pending > 0) ? 'needs' : batches.length ? 'done' : 'empty',
      count: batches.some(b => b.pending > 0) ? String(batches.reduce((n, b) => n + b.pending, 0)) : null },
    !embedded && { id: 'agreement', group: 'work', label: t('desk_pm_nav_agreement'), status: !saved ? 'locked' : coops.length ? 'done' : 'empty' },
    !embedded && { id: 'conditions', group: 'work', label: t('desk_pm_nav_conditions'), status: saved?.listingId ? 'empty' : 'locked' },
    !embedded && saved?.propertyId && (onAddMeeting || onCreateContract) && { id: 'meeting', group: 'work', label: t('desk_pm_nav_meeting'), status: 'empty' },
    !embedded && { id: 'more', group: 'work', label: t('desk_pm_nav_more'), status: saved ? 'empty' : 'locked' },
  ].filter(Boolean)
  const jumpToSection = id => { document.getElementById(`pm-${id}`)?.scrollIntoView({ block: 'start' }) }
  // Closing with unsaved work asks first (review 2026-09-17: × silently threw typed values away). Unsaved work =
  // the form differs from its last save or prefill, queued files, a paste not yet parsed, or open conflicts.
  const hasUnsavedWork = () => {
    const ref = lastRec ?? initialSnapRef.current
    return (ref != null && snapshot() !== ref) || pendingImgs.length > 0 || (pasteText.trim() !== '' && pasteText !== parsedText) || parseConflicts.length > 0
  }
  const requestClose = () => {
    if (hasUnsavedWork() && !window.confirm(t('desk_pm_discard_confirm'))) return
    // closed without saving: the drafts THIS window made by auto-fill go too (an opened draft stays — the agent may only
    // have looked). They used to stay as "AI-created · pending" cards nobody had asked for.
    if (!saved && parsedHereRef.current.size) {
      const ids = [...parsedHereRef.current]; parsedHereRef.current.clear()
      Promise.all(ids.map(id => dismissProposal(id).catch(() => null))).then(() => onDraftCreated?.())
    }
    onClose()
  }
  const Shell = embedded ? EmbeddedShell : Modal
  return (
    <Shell wide
      headerRight={!embedded ? <PropertyModalProgress sections={navSections} /> : null}
      headerBelow={!embedded ? <PropertyModalNav variant="chips" sections={navSections} onJump={jumpToSection} /> : null}
      title={saved ? (prop.address.trim() || prop.cadastral_code.trim() || t('desk_property_fallback_title')) : proposalId ? t('desk_property_review_title') : t('desk_add_property')}
      subtitle={saved ? t('desk_property_recorded_subtitle') : undefined}
      onClose={requestClose}>
      <div className={embedded ? 'space-y-4' : 'lg:grid lg:grid-cols-[11.5rem_minmax(0,1fr)] lg:gap-6'}>
        {!embedded && <PropertyModalNav variant="side" sections={navSections} onJump={jumpToSection} />}
        <div className="space-y-4 min-w-0">
        {worksOnProperty === false && (
          <p role="note" data-private-version className="rounded-xl border border-sky-500/40 bg-sky-500/10 px-3 py-2 text-[13px] text-foreground">
            {t('desk_pm_private_version')}
          </p>
        )}
        {/* One version per agent per flat (owner 2026-09-28): say so, and say when the advert that was
            clicked is not the one the version is based on — its differing values are information only. */}
        {myVersionInfo && (
          <p role="note" data-my-version className="rounded-xl border border-primary/40 bg-primary/10 px-3 py-2 text-[13px] text-foreground">
            {t('desk_pm_my_version_open')}
            {existing?.listingId && baseListingId && existing.listingId !== baseListingId && (
              <span className="block text-[12px] text-muted-foreground mt-0.5">{t('desk_pm_my_version_other_ad')}</span>
            )}
          </p>
        )}
        <PmSection id="property">
        <Facet num="01" title={t('desk_property_facet_title')} labelKey="desk_pm_nav_property" badge={<span className="ml-2 rounded-full border border-rose-500/50 bg-rose-500/10 px-1.5 text-[10px] font-normal leading-4 text-rose-600 dark:text-rose-400">{t('desk_pm_required')}</span>}
          summary={[prop.address.trim(), prop.kind].filter(Boolean).join(' · ')}>
          {/* Property type FIRST (redesign stage 1): it decides which fields appear further down, so it is
              answered before the address rather than after it. */}
          <div className="mb-3" data-pm-kind>
            <p className="text-xs text-muted-foreground mb-1">{t('desk_property_type_label')}</p>
            <div className="flex flex-wrap gap-1.5">
              {/* Apart-hotel (owner 2026-10-02) sits beside apartment because that is where an agent looks — but it
                  is NOT a kind: it saves kind=apartment + the type tag type_aparthotel_unit (core 1940), exactly
                  like the search tree's row. So "apartment" lights only for a plain flat, and either chip switches. */}
              {PROPERTY_KIND_OPTIONS.flatMap(o => o === 'apartment' ? [o, 'aparthotel'] : [o]).map(o => {
                const ahOn = prop.kind === 'apartment' && typeTags.includes('type_aparthotel_unit')
                const on = o === 'aparthotel' ? ahOn : (prop.kind === o && !(o === 'apartment' && ahOn))
                const kind = o === 'aparthotel' ? 'apartment' : o
                return (
                <button key={o} type="button" aria-pressed={on} onClick={() => {
                  setProp(p => ({ ...p, kind }))
                  // a subtype belongs to one kind — switching kind drops one the new kind cannot have
                  setFacts(f => ((KIND_SUBTYPES[kind] || []).includes(f.subtype) ? f : { ...f, subtype: '' }))
                  setTypeTags(tt => o === 'aparthotel'
                    ? (tt.includes('type_aparthotel_unit') ? tt : [...tt, 'type_aparthotel_unit'])
                    : tt.filter(c => c !== 'type_aparthotel_unit'))
                }}
                  className={pill('none', { door: true, extra: on ? 'bg-foreground text-background border-foreground' : 'border-border text-muted-foreground hover:bg-muted' })}>
                  {o === 'aparthotel' ? t('desk_context_aparthotel') : t(PROPERTY_CATEGORY_KEYS[o])}
                </button>
                )
              })}
            </div>
            {/* Subtype (review 2026-09-17: "has a garage" is not "is a garage"). Saved as the subtype claim, the same one
                portals fill and search filters on; tap again to clear. */}
            {(KIND_SUBTYPES[prop.kind] || []).length > 0 && (
              <div className="mt-2" data-pm-subtype>
                <p className="text-xs text-muted-foreground mb-1">{t('desk_pm_subtype_label')}{driftMark('subtype')}</p>
                <div className="flex flex-wrap gap-1.5">
                  {KIND_SUBTYPES[prop.kind].map(st => (
                    <button key={st} type="button" aria-pressed={facts.subtype === st}
                      onClick={() => setFacts(f => ({ ...f, subtype: f.subtype === st ? '' : st }))}
                      className={pill('none', { door: true, extra: facts.subtype === st ? 'bg-foreground/85 text-background border-foreground' : 'border-border text-muted-foreground hover:bg-muted' })}>
                      {t(`desk_subtype_${st}`)}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {/* ONE address control (2026-08-19, owner-approved). This was a free-text line
                with a Nominatim type-ahead, a separate DistrictPicker and a separate map —
                three controls that did not know about each other, and no street or number
                field at all. AddressBlock is the control the fill form already uses, and its
                own header calls it "the ONE address control: city · district · street ·
                number · pin"; the Desk was the surface still outside it.
                What that buys here, none of which was possible before:
                  • the street comes from OUR gazetteer, typo-tolerant since 0660/0661 —
                    the free-text box asked Nominatim, which has no fuzzy matching and
                    returned nothing for a one-vowel slip (the 2026-08-19 report);
                  • a real NUMBER field, so "ჭავჭავაძის 37" stops losing the 37;
                  • the district is DERIVED from the pin and disagreements are surfaced
                    rather than silently overwritten.
                ⚠️ `address` stays the composed record ("<street> <number>") because that is
                what the save path sends and what B718's parser (0580/0582) reads to fill
                property_street.house_number. The number is not a new stored field — it is
                the same address text, finally typed in two boxes that know what they are. */}
            <div className="col-span-2">
              <AddressBlock
                value={{ address: prop.address, street: prop.street, street_number: prop.street_number,
                  entrance_apt: prop.entrance_apt,
                  district_code: prop.district_code, city: districtCity, lat: prop.lat, lng: prop.lng }}
                onChange={patch => {
                  // AddressBlock always emits a PATCH. `city` is its own scope selector, which
                  // this modal already owns as districtCity — routed there rather than into
                  // `prop`, so the two cannot disagree about which city the tree is showing.
                  const { city, ...rest } = patch
                  // ⚠️ `'city' in patch`, NOT `city !== undefined` — same root cause as the
                  // coercion below, read from the other side. AddressBlock clears the scope
                  // with `{ city: undefined }` ("All cities"), and testing the VALUE threw
                  // that away, so `districtCity` could never return to null and the promise
                  // written at its useState — "All of Georgia is one pick away, this is a
                  // default, never a restriction" — was false on this surface. A patch key
                  // that is PRESENT is an instruction, whatever its value; only an ABSENT
                  // key means "not mentioned".
                  if ('city' in patch) setDistrictCity(city ?? null)
                  // ⚠️ AddressBlock CLEARS BY SENDING `undefined`, and `prop`'s text fields are
                  // STRINGS ('' when empty) — see the useState above and the seven unguarded
                  // `prop.address.trim()` / `prop.cadastral_code.trim()` reads in this file,
                  // one of which (`canSave`) runs on EVERY render. So the "შეცვლა" button next
                  // to a picked street, which emits {street, street_number, address: undefined},
                  // put `undefined` into `prop.address` and the very next render threw
                  // "Cannot read properties of undefined (reading 'trim')" — caught by
                  // TabErrorBoundary, so the whole Desk tab was replaced by
                  // "ამ ხედის ჩატვირთვა ვერ მოხერხდა" (owner report, 2026-08-25).
                  // Coerced HERE, at the one boundary where the foreign contract meets ours,
                  // rather than guarding seven read sites — a new `.trim()` reader added later
                  // would re-open the bug, a broken invariant at the door would not.
                  // 📌 The fill form never hit this: FillApp reads `(form.address || '').trim()`.
                  // Same component, same button, one host guarded and one not — which is why
                  // the fix belongs to the host's own state shape, not to AddressBlock.
                  for (const k of ['address', 'street', 'street_number', 'entrance_apt', 'district_code']) {
                    if (k in rest && rest[k] == null) rest[k] = ''
                  }
                  if (Object.keys(rest).length) setProp(p => ({ ...p, ...rest }))
                }}
                districts={districts} lang={getLanguage()} mapBeside
                resolveStreet={(q, city) => addressStreets(q, city)}
                districtForPoint={(lat, lng) => addressDistrictForPoint(lat, lng)}
                addressForPoint={(lat, lng) => addressForPoint(lat, lng)}
                houseForAddress={(street, number, lat, lng) => addressHouse(street, number, lat, lng)}
                renderMap={({ lat, lng, onPick }) => (
                  <PointMapPicker lat={lat} lng={lng} focus={mapFocus}
                    onChange={v => (v ? onPick(v.lat, v.lng) : setProp(p => ({ ...p, lat: null, lng: null })))} />
                )}
                labels={{
                  street: t('desk_addr_street'), street_ph: t('desk_addr_street_ph'), number: t('desk_addr_number'),
                  district: t('desk_property_district'), city: t('desk_addr_city'), peri: t('desk_addr_peri'),
                  at_pin: t('desk_addr_at_pin'), use_it: t('desk_addr_use_it'),
                  map_search_ph: t('desk_addr_map_ph'), or_map: t('desk_addr_or_map'),
                  other_number: t('desk_addr_other_number'), unnamed: t('desk_addr_unnamed'),
                  change: t('desk_addr_change'), other: t('desk_addr_other'),
                  entrance_add: t('desk_addr_entrance_add'), entrance_label: t('desk_addr_entrance_label'),
                  entrance_note: t('desk_addr_entrance_note'),
                  or: t('desk_addr_or'), mark_on_map: t('desk_addr_mark_on_map'),
                  my_location: t('desk_addr_my_location'), not_listed_hint: t('desk_addr_not_listed'),
                  pin_says: t('desk_addr_pin_says'), pin_keep: t('desk_addr_pin_keep'),
                  pin_decide: t('desk_addr_pin_decide'), searching: t('desk_addr_searching'),
                  no_street: t('desk_addr_no_street'),
                  auto: t('desk_addr_auto'), auto_pin: t('desk_addr_auto_pin'),
                  conflict: t('desk_addr_conflict'), keep_mine: t('desk_addr_keep_mine'),
                  use_pin: t('desk_addr_use_pin'),
                  search: t('desk_district_search'), allCities: t('desk_district_all_cities'),
                  empty: t('desk_district_no_match'), hint: t('desk_district_tree_hint'),
                }} />
                  {/* T39 (0847): for LAND, the road and places an agent marks on the map. Shown
                      once the property exists (a mark needs a row to hang on) and only for land —
                      a flat's distance to the road is not a fact anyone asks for. The distance
                      it shows is the server's (property_road_distance_m), never computed here. */}
                  {isLand && (existing?.propertyId || saved?.propertyId) && (
                    <div className="mt-3">
                      <p className="text-xs text-muted-foreground mb-1">{t('desk_mark_section_title')}</p>
                      {/* The pin is prop.lat/prop.lng here (the map picker writes them) — a bare `lat` in
                          this scope threw ReferenceError and took the whole Desk to its error boundary
                          on 2026-09-03; the import audit only watches verb-prefixed names. */}
                      <RoadMarkPicker propertyId={existing?.propertyId || saved?.propertyId} agentId={agentId}
                        lat={prop.lat != null && prop.lat !== '' && Number.isFinite(Number(prop.lat)) ? Number(prop.lat) : null}
                        lng={prop.lng != null && prop.lng !== '' && Number.isFinite(Number(prop.lng)) ? Number(prop.lng) : null} />
                    </div>
                  )}
            </div>
            <label className="text-xs text-muted-foreground col-span-2 sm:col-span-1">{t('desk_property_cadastral')}
              <input className={input} value={prop.cadastral_code} onChange={e => setProp(p => ({ ...p, cadastral_code: e.target.value }))} placeholder="01.14.03.041.052" />
            </label>
          </div>
          <p className="text-xs text-muted-foreground mt-3 mb-1">{t('desk_property_map_section')}</p>
          {/* The map moved INSIDE AddressBlock (2026-08-19) — it is rendered there via
              renderMap, so a pin drag now also re-derives the district and can offer the
              building it landed on. Leaving this second instance would have put two maps
              in one facet, both writing prop.lat/lng and neither knowing about the other. */}
        </Facet>
        </PmSection>
        {!embedded && (
        <PmSection id="terms">
        <Facet num="02" title={t('desk_property_terms_facet_title')} labelKey="desk_pm_nav_terms"
          hint={t('desk_property_terms_facet_hint')}
          summary={tLog('desk_property_terms_summary', { intent: LISTING_INTENT_OPTIONS.find(o => o.value === offer.intent)?.label || offer.intent, price: offer.price, currency: offer.currency })}>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <p className="text-xs text-muted-foreground mb-1">{t('desk_property_intent_label')}</p>
              <div className="flex flex-wrap gap-1.5" data-pm-intent>
                {LISTING_INTENT_OPTIONS.map(o => (
                  <button key={o.value} type="button" aria-pressed={offer.intent === o.value} onClick={() => setOffer(x => ({ ...x, intent: o.value }))}
                    className={pill('none', { door: true, extra: offer.intent === o.value ? 'bg-foreground text-background border-foreground' : 'border-border text-muted-foreground hover:bg-muted' })}>
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
            <label className="text-xs text-muted-foreground">{t('desk_property_price_negotiable')}
              <span className="flex gap-1.5 items-center">
                <input className={input} type="number" min="0" value={offer.price} onChange={e => setOffer(o => ({ ...o, price: e.target.value }))} />
                <span className="flex shrink-0 rounded-md border border-border overflow-hidden" role="group" aria-label={t('desk_property_currency_label')}>
                  {['USD', 'GEL'].map(c => (
                    <button key={c} type="button" aria-pressed={offer.currency === c} onClick={() => setOffer(o => ({ ...o, currency: c }))}
                      className={`px-2.5 py-2 text-xs ${offer.currency === c ? 'bg-foreground text-background' : 'text-muted-foreground hover:bg-muted'}`}>{c}</button>
                  ))}
                </span>
              </span>
              {provLine('price')}
            </label>
          </div>
        </Facet>
        </PmSection>
        )}
        <PmSection id="facts">
        <Facet num="03" title={t('desk_property_facts_title')} labelKey="desk_pm_nav_facts"
          summary={[facts.rooms !== '' && tLog('desk_entry_rooms_abbrev', { rooms: facts.rooms }), facts.area_sqm !== '' && tLog('desk_entry_area_abbrev', { area: facts.area_sqm }), facts.floor !== '' && `fl ${facts.floor}`].filter(Boolean).join(' · ')}>
          {/* "Fill my gaps from this card" (owner 2026-09-03: "what if on market appears better
              version and they want to refill gaps from there so they don't want to write
              everything by a hand"). Copies the opened card's values into the fields the agent
              has NOT written; their own fields are untouched. The filled keys are dropped from
              the baseline so they leave the form as the agent's claims on record — pressing the
              button IS the agent adopting them. Rendered only when there is a card to fill from. */}
          {cardFactsRef.current && (
            <div className="flex items-center gap-2 mb-2">
              <button type="button" className={btn('ghost')} data-fill-gaps
                title={t('desk_facts_fill_gaps_hint')}
                onClick={() => {
                  const { next, filled } = refillGaps(facts, ownKeysRef.current, cardFactsRef.current, baselineRef.current)
                  if (!filled.length) return
                  const rebased = { ...(baselineRef.current || {}) }
                  for (const k of filled) delete rebased[k]
                  baselineRef.current = rebased
                  setFacts(next)
                  setFilledNote(filled.length)
                }}>
                {t('desk_facts_fill_gaps')}
              </button>
              {filledNote > 0 && <span className="text-[11px] text-muted-foreground">{tLog('desk_facts_filled_n', { n: filledNote })}</span>}
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[['rooms', 'desk_facts_rooms'], ['bedrooms', 'desk_facts_bedrooms'], ['bathrooms', 'desk_facts_bathrooms'], ['area_sqm', 'desk_facts_area_sqm'],
              ['kitchen_area', 'desk_facts_kitchen_area'], ['floor', 'desk_facts_floor'], ['floors_total', 'desk_facts_floors_total'], ['ceiling_height', 'desk_facts_ceiling']]
              // which of these a kind has comes from the shared intake definition (core 2404); LAND_HIDDEN_FACTS is the fallback
              .filter(([k]) => (factRules.ready ? shownF(k) : !(isLand && LAND_HIDDEN_FACTS.has(k))) && !(k === 'ceiling_height' && facts.ceiling_variation === 'varies')).map(([k, labelKey]) => (
              <label key={k} className="text-xs text-muted-foreground">{t(labelKey)}{reqMark(k)}{driftMark(k)}{ownKeysRef.current.includes(k) && <span className="ml-1 text-[10px] text-primary/80" data-yours={k}>{t('desk_facts_yours')}</span>}
                <input className={input} type="number" placeholder="—" aria-label={t(labelKey)} value={facts[k]} onChange={e => setFacts(f => ({ ...f, [k]: e.target.value }))} />
                {provLine(k)}
              </label>
            ))}
          </div>
          {/* Does the ceiling vary? Asked, never inferred (owner 2026-09-16). "Varies" swaps the single
              height for the lowest and highest point; a value that cannot be stored says so here. */}
          {/* A ROOM is identified by the flat or house it sits in (owner 2026-09-24, decision C) — never a small apartment. */}
          {shownF('room_unit_kind') && (
            <div className="mt-3" data-room-unit>
              <p className="text-xs text-muted-foreground mb-1">{t('desk_room_unit_kind')}{reqMark('room_unit_kind')}{driftMark('room_unit_kind')}</p>
              <div className="flex flex-wrap gap-1.5">
                {['apartment', 'house'].map(k => (
                  <button key={k} type="button" aria-pressed={facts.room_unit_kind === k}
                    onClick={() => setFacts(f => ({ ...f, room_unit_kind: f.room_unit_kind === k ? '' : k }))}
                    className={pill('none', { door: true, extra: facts.room_unit_kind === k ? 'bg-foreground/85 text-background border-foreground' : 'border-border text-muted-foreground hover:bg-muted' })}>
                    {t(`desk_room_unit_${k}`)}
                  </button>
                ))}
              </div>
              <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-3">
                {[['room_unit_rooms', 'desk_room_unit_rooms'], ['room_unit_area_sqm', 'desk_room_unit_area'], ['room_unit_occupants', 'desk_room_unit_occupants']]
                  .filter(([k]) => shownF(k)).map(([k, labelKey]) => (
                  <label key={k} className="text-xs text-muted-foreground">{t(labelKey)}{reqMark(k)}{driftMark(k)}
                    <input className={input} type="number" min="0" placeholder="—" value={facts[k]} onChange={e => setFacts(f => ({ ...f, [k]: e.target.value }))} />
                  </label>
                ))}
              </div>
            </div>
          )}
          {shownF('ceiling_variation') && (
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3" data-ceiling-variation>
              <label className="text-xs text-muted-foreground">{t('desk_facts_ceiling_variation')}
                <select className={input} value={facts.ceiling_variation} onChange={e => { const v = e.target.value; setFacts(f => withCeilingVariation(f, v)) }}>
                  <option value="">{t('desk_tristate_not_stated')}</option>
                  <option value="uniform">{t('desk_facts_ceiling_uniform')}</option>
                  <option value="varies">{t('desk_facts_ceiling_varies')}</option>
                </select>
              </label>
              {facts.ceiling_variation === 'varies' && [['ceiling_height_min', 'desk_facts_ceiling_min'], ['ceiling_height_max', 'desk_facts_ceiling_max']].map(([k, labelKey]) => (
                <label key={k} className="text-xs text-muted-foreground">{t(labelKey)}{driftMark(k)}
                  <input className={input} type="number" step="0.01" value={facts[k]} onChange={e => setFacts(f => ({ ...f, [k]: e.target.value }))} />
                </label>
              ))}
            </div>
          )}
          {!isLand && ceilingRangeBad && (
            <p className="mt-1 text-xs text-amber-600 dark:text-amber-400" data-ceiling-range-note>{t('desk_facts_ceiling_range_note')}</p>
          )}
          {/* Condition is ONE structured selection across two mutually-exclusive rows
              (replaced the old free-text input 2026-07-10): a frame / unbuilt building
              cannot have an apartment class — class exists only once it's livable. Same
              codes the client form's quality chips use (0078; symmetry rule: one matching
              axis, both sides). Saved as a feature tag via the ingest tagKeys path. */}
          <div className="mt-3">
            {/* Land has no condition concept (no building → no frame state, no class);
                only the per-type tag row below applies. */}
            {(factRules.conditionShown ?? !isLand) && (<>
            {/* Design 15g (2026-09-24): the owner form's vocabulary at the Desk's density — a label column and one row per
                group: building, frame stage, finish, class. Frame stage and finish are ONE answer (the condition), so a
                pick in one greys the other (dashed, still clickable to switch). Class, when a frame or an unbuilt building
                rules it out, stays as a muted line so the agent sees why it is missing. */}
            <div className="border-t border-foreground/80 pt-2" data-cond-block>
              {(() => {
                const FRAMES = new Set(['frame_black', 'frame_white', 'frame_white_plus', 'frame_green'])
                const rows = []
                for (const row of conditionRows) {
                  if (row.key !== 'condition') { rows.push({ ...row, id: row.key, title: t(row.key === 'building' ? 'desk_cond_building' : row.key === 'never_lived_in' ? 'desk_cond_occupancy' : 'desk_cond_class') }); continue }
                  const fr = row.options.filter(o => FRAMES.has(o.code)), fi = row.options.filter(o => !FRAMES.has(o.code))
                  if (fr.length) rows.push({ ...row, id: 'condition_frame', options: fr, title: t('desk_cond_frame'), group: 'frame' })
                  if (fi.length) rows.push({ ...row, id: 'condition_finish', options: fi, title: t('desk_cond_finish'), group: 'finish' })
                }
                const picked = cond.condition ? (FRAMES.has(cond.condition) ? 'frame' : 'finish') : null
                const classAsked = !!deskDef?.questions?.some(q => q.key === 'apartment_class')
                const classHidden = classAsked && !conditionRows.some(r => r.key === 'apartment_class')
                return (<>
                  {rows.map(row => (
                    <div key={row.id} data-cond-row={row.id === 'condition_frame' || row.id === 'condition_finish' ? 'condition' : row.key} data-cond-group={row.group || undefined}
                      className="grid grid-cols-[118px_minmax(0,1fr)] items-start gap-2 py-1.5">
                      <p className="pt-1 text-[12px] text-muted-foreground">{row.title}
                        {row.required && <span className="ml-0.5 text-destructive" aria-hidden="true">*</span>}
                        {row.key === 'condition' && row.group === 'frame' && driftMark('condition')}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {row.options.map(({ code, label }) => {
                          const on = cond[row.key] === code
                          const dim = row.group && picked && picked !== row.group
                          return (
                            <button key={code} type="button" aria-pressed={on} data-cond-code={code} onClick={() => pickCond(row.key, code)}
                              className={`min-h-[28px] border px-2 py-0.5 text-[12px] leading-tight ${on ? 'border-foreground bg-foreground text-background' : dim ? 'border-dashed border-border text-muted-foreground/70' : 'border-border text-foreground hover:bg-muted'}`}>
                              {label}
                              {featureDefOf(defVocab, code) && (
                                <InfoDot active={defKey === code} onTap={() => setDefKey(k => k === code ? null : code)} />
                              )}
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  ))}
                  {classHidden && (
                    <div className="grid grid-cols-[118px_minmax(0,1fr)] gap-2 py-1.5" data-cond-class-na>
                      <p className="text-[12px] text-muted-foreground">{t('desk_cond_class')}</p>
                      <p className="text-[12px] text-muted-foreground">{t('desk_cond_class_na')}</p>
                    </div>
                  )}
                </>)
              })()}
            </div>
            {/* Project series (2026-07-21) — a CLAIM (facts.project), unlike the two feature-tag
                ladders above; single-select, tap again to clear. See PROJECT_TYPES for why
                გუდაური is missing. */}
            {shownF('project') && (<>
            <p className="text-xs text-muted-foreground mt-2 mb-1">{t('desk_facts_project')}</p>
            <div className="flex flex-wrap gap-1.5">
              {PROJECT_TYPES.map(p => (
                <button key={p} type="button" onClick={() => setFacts(f => ({ ...f, project: f.project === p ? '' : p }))}
                  className={pill('none', { door: true, extra: facts.project === p ? TIER_STYLE.must : 'border-border text-muted-foreground hover:bg-muted' })}>
                  {p}
                  {featureDefOf(defVocab, p) && (
                    <InfoDot active={defKey === p} onTap={() => setDefKey(k => k === p ? null : p)} />
                  )}
                </button>
              ))}
            </div>
            </>)}
            {/* Shared ⓘ panel for all three ladders above — one open definition at a time. */}
            <FeatureDefPanel def={featureDefOf(defVocab, defKey)} onClose={() => setDefKey(null)} />
            {/* Year built · lift · parking · heating (2026-09-16). The valuation questions are
                filled with these same controls, so a fact has one form wherever it is asked. */}
            <BuildingFacts facts={facts} setFacts={setFacts} featAns={featAns} setFeatAns={setFeatAns} highlight={highlight} />
            {!yearBuiltValid(facts.year_built) && (
              <p className="mt-1 text-xs text-amber-600 dark:text-amber-400" data-year-invalid>{t('desk_facts_year_invalid')}</p>
            )}
            </>)}
            {/* Apart-hotel terms (core 2856, owner 2026-10-02): this unit's own statement — registered status, operator,
                brand, programme, income, return, contract, own-use days. Rows come from the shared definition
                (deskAparthotelRows), the same questions the owner form asks; services sit in the feature panel. A
                return figure appears only once its kind is chosen, so it is never recorded without one. */}
            {(() => {
              const ahRows = deskAparthotelRows(deskDef, facts, {
                isAparthotel: prop.kind === 'apartment' && typeTags.includes('type_aparthotel_unit'),
                lang: getLanguage() === 'ka' ? 'ka' : getLanguage() === 'ru' ? 'ru' : 'en' })
              if (!ahRows.length) return null
              const setF = (k, v) => setFacts(f => ({ ...f, [k]: v }))
              return (
                <div className="mt-3 rounded-md border border-border p-3" data-pm-aparthotel>
                  <p className="text-xs font-medium text-foreground mb-2">{t('desk_aparthotel_details')}</p>
                  <div className="grid grid-cols-2 gap-3">
                    {ahRows.map(r => (
                      <div key={r.key} className={r.control === 'choice' ? 'col-span-2' : ''}>
                        <p className="text-xs text-muted-foreground mb-1">{r.label}{r.unit ? ` (${r.unit})` : ''}</p>
                        {r.control === 'choice' ? (
                          <div className="flex flex-wrap gap-1.5">
                            {r.options.map(o => (
                              <button key={o.code} type="button" aria-pressed={r.value === o.code}
                                onClick={() => setF(r.key, r.value === o.code ? '' : o.code)}
                                className={pill('none', { door: true, extra: r.value === o.code ? 'bg-foreground text-background border-foreground' : 'border-border text-muted-foreground hover:bg-muted' })}>
                                {o.label}
                              </button>
                            ))}
                          </div>
                        ) : (
                          <input className={input} type={r.control === 'number' ? 'number' : 'text'}
                            {...(r.control === 'number' ? { min: r.min ?? undefined, max: r.max ?? undefined, step: 'any' } : { maxLength: 120 })}
                            value={r.value} onChange={e => setF(r.key, e.target.value)} />
                        )}
                        {r.hint && <p className="mt-0.5 text-[11px] text-muted-foreground">{r.hint}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )
            })()}
            {/* Tier A: per-type tags (multi-toggle) — the supply twin of the client
                form's type chips, so demand and supply meet on the same codes. */}
            {/* apart-hotel is answered by the Type row above, so it never renders here a second time */}
            {(PROP_TYPE_TAGS[prop.kind] || []).some(([code]) => code !== 'type_aparthotel_unit') && (<>
              <p className="text-xs text-muted-foreground mt-2 mb-1">{t('desk_criteria_type_features')}</p>
              <div className="flex flex-wrap gap-1.5">
                {PROP_TYPE_TAGS[prop.kind].filter(([code]) => !['land_agricultural', 'land_non_agricultural', 'type_aparthotel_unit'].includes(code)).map(([code, label]) => (
                  <button key={code} type="button" onClick={() => toggleTypeTag(code)}
                    className={pill('none', { door: true, extra: typeTags.includes(code) ? TIER_STYLE.must : 'border-border text-muted-foreground hover:bg-muted' })}>
                    {label}
                  </button>
                ))}
              </div>
            </>)}
          </div>
          {/* Balcony: count + area (2026-07-11) — can be 0/1/2+, and count/size is what
              actually matters to a BUYER (renters just want yes/no, captured on the
              client side as a plain feature chip, not a number here). Terrace stays a
              plain yes/no — a property has one or it doesn't, never "how many". Blank =
              not observed (omitted); has_balcony/has_terrace booleans are derived at
              save time so existing search/AI plumbing still gets them. */}
          <div className="grid grid-cols-2 gap-3 mt-3">
            {shownF('balcony_count') && (
            <label className="text-xs text-muted-foreground">{t('desk_facts_balcony_count')}
              <input className={input} type="number" min="0" value={facts.balcony_count}
                onChange={e => setFacts(f => ({ ...f, balcony_count: e.target.value }))} /></label>)}
            {shownF('balcony_area_sqm') && (
            <label className="text-xs text-muted-foreground">{t('desk_facts_balcony_area')}
              <input className={input} type="number" min="0" placeholder={t('desk_facts_area_each_or_total')}
                value={facts.balcony_area_sqm} onChange={e => setFacts(f => ({ ...f, balcony_area_sqm: e.target.value }))} /></label>)}
            {shownF('has_terrace') && (
            <label className="text-xs text-muted-foreground">{t('desk_facts_terrace')}
              <select className={input} value={facts.has_terrace} onChange={e => setFacts(f => ({ ...f, has_terrace: e.target.value }))}>
                <option value="">—</option><option value="true">{t('desk_tristate_yes')}</option><option value="false">{t('desk_tristate_no')}</option>
              </select></label>)}
            {/* 0083 Tier C: power feed (commercial/office) + raw zoning label (land too) —
                claim-backed facts; power is what the scorer compares vs required_power_kw */}
            {shownF('power_capacity_kw') && (
              <label className="text-xs text-muted-foreground">{t('desk_facts_power_kw')}
                <input className={input} type="number" min="0" value={facts.power_capacity_kw}
                  onChange={e => setFacts(f => ({ ...f, power_capacity_kw: e.target.value }))} /></label>
            )}
            {shownF('functional_zone') && (
              <label className="text-xs text-muted-foreground">{t('desk_facts_functional_zone')}
                <input className={input} value={facts.functional_zone}
                  onChange={e => setFacts(f => ({ ...f, functional_zone: e.target.value }))} /></label>
            )}
          </div>
          {/* The remaining fact attributes the server stores (2026-09-16). Land gets only its plot area. */}
          <p className="text-xs text-muted-foreground mt-3 mb-1">{t('desk_facts_areas_more')}</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-more-facts>
            {/* Plot area in the unit the owner used; stored as m² with the unit recorded beside it. */}
            {shownF('land_area_sqm') && (
            <label className="text-xs text-muted-foreground" data-land-area>{t('desk_attr_land_area_sqm')}{reqMark('land_area_sqm')}{driftMark('land_area_sqm')}
              {isLand && facts.area_sqm !== '' && facts.land_area_sqm === '' && (
                <span className="block text-[11px] text-amber-700 dark:text-amber-300">{tLog('desk_pm_land_area_hint', { n: facts.area_sqm })}</span>
              )}
              <span className="flex gap-1">
                <input className={input} type="number" min="0"
                  value={landText != null && landAreaToSqm(landText, facts.land_area_unit) === facts.land_area_sqm ? landText : sqmToLandUnit(facts.land_area_sqm, facts.land_area_unit)}
                  onChange={e => { const v = e.target.value; setLandText(v); setFacts(f => ({ ...f, land_area_sqm: landAreaToSqm(v, f.land_area_unit) })) }} />
                <select className={input + ' w-24'} aria-label={t('desk_facts_land_unit')} value={facts.land_area_unit}
                  onChange={e => { const u = e.target.value; const shown = landText != null ? landText : sqmToLandUnit(facts.land_area_sqm, facts.land_area_unit)
                    setFacts(f => ({ ...f, land_area_unit: u, land_area_sqm: landAreaToSqm(shown, u) })) }}>
                  <option value="">{t('desk_unit_sqm')}</option>
                  <option value="ari">{t('desk_unit_ari')}</option>
                  <option value="ha">{t('desk_unit_ha')}</option>
                </select>
              </span>
            </label>)}
            {shownF('metro_station') && (
              <label className="text-xs text-muted-foreground">{t('desk_attr_metro_station')}{driftMark('metro_station')}
                <select className={input} value={facts.metro_station} onChange={e => setFacts(f => ({ ...f, metro_station: e.target.value }))}>
                  <option value="">{t('desk_facts_material_unset')}</option>
                  {METRO_STATIONS.concat(facts.metro_station && !METRO_STATIONS.includes(facts.metro_station) ? [facts.metro_station] : [])
                    .map(m => <option key={m} value={m}>{m}</option>)}
                </select></label>
            )}
            {['living_room_area_sqm', 'loggia_area_sqm', 'storeroom_area_sqm', 'porch_area_sqm'].filter(shownF).map(k => (
              <label key={k} className="text-xs text-muted-foreground">{t('desk_attr_' + k)}{driftMark(k)}
                <input className={input} type="number" min="0" value={facts[k]} onChange={e => setFacts(f => ({ ...f, [k]: e.target.value }))} />
              </label>
            ))}
            {shownF('building_material') && (
              <label className="text-xs text-muted-foreground">{t('desk_attr_building_material')}{driftMark('building_material')}
                <select className={input} value={facts.building_material} onChange={e => setFacts(f => ({ ...f, building_material: e.target.value }))}>
                  <option value="">{t('desk_facts_material_unset')}</option>
                  {BUILDING_MATERIALS.concat(facts.building_material && !BUILDING_MATERIALS.includes(facts.building_material) ? [facts.building_material] : [])
                    .map(m => <option key={m} value={m}>{m}</option>)}
                </select></label>)}
            {shownF('parking_deeded') && (
              <label className="text-xs text-muted-foreground">{t('desk_facts_parking_deeded')}
                <select className={input} value={facts.parking_deeded} onChange={e => setFacts(f => ({ ...f, parking_deeded: e.target.value }))}>
                  <option value="">—</option><option value="1">{t('desk_tristate_yes')}</option><option value="0">{t('desk_tristate_no')}</option>
                </select></label>)}
          </div>
          <FeatureCapture vocab={defVocab} kind={prop.kind} kindCaps={kindCaps} exclude={captureExclude}
            answers={featStates} setAnswers={setFeatStates} pendingCount={Object.keys(pendingChipStates() || {}).length}
            aiCodes={aiFeatureCodes} disagree={featDisagree} />
        </Facet>
        </PmSection>
        {!embedded && (
        <PmSection id="photos">
        {(() => {
          // On a capture `images` ARE the ad's — shown read-only; after the version exists they are the
          // version's own and the ad's sit in basePhotos (see "A CAPTURE IS IMMUTABLE" at the state).
          const adPhotos = onCapture ? images.filter(i => (i.kind || 'photo') === 'photo') : basePhotos
          const photos = onCapture ? [] : images.filter(i => (i.kind || 'photo') === 'photo')
          const docs = onCapture ? [] : images.filter(i => i.kind === 'document')
          const pendPhotos = pendingImgs.filter(p => p.kind === 'photo')
          const pendDocs = pendingImgs.filter(p => p.kind === 'document')
          const isImgUrl = u => /\.(jpg|jpeg|png|webp|gif|avif|heic)$/i.test(u)
          const rmBtn = 'absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-black/70 text-white text-xs leading-none'
          const mvBtn = 'absolute -bottom-1.5 -right-1.5 w-5 h-5 rounded-full bg-black/70 text-white text-xs leading-none'
          const dropPending = p => setPendingImgs(prev => { if (p.preview) URL.revokeObjectURL(p.preview); return prev.filter(x => x !== p) })
          const total = adPhotos.length + photos.length + docs.length + pendingImgs.length
          return (
        <Facet num="04" title={t('desk_property_photos_docs_title')} labelKey="desk_pm_nav_photos"
          // desk_property_photos_docs_summary reuses the placeholder name {n} for two different
          // counts (photo count, doc count) — tLog's replaceAll would stamp both with the same
          // value, so substitute positionally instead of via tLog.
          summary={total ? (() => {
            let i = 0
            return t('desk_property_photos_docs_summary').replace(/\{n\}/g, () => (i++ === 0 ? adPhotos.length + photos.length + pendPhotos.length : docs.length + pendDocs.length))
          })() : ''}>
          {/* Header row: the section label, and — only when the portal actually published a
              second rendition — a view switch. `originals` is empty for korter/bog, so the
              switch stays absent rather than offering a toggle that changes nothing. */}
          <div className="flex items-center justify-between gap-2 mb-1">
            <p className="text-xs text-muted-foreground">{t('desk_property_photos_section')}</p>
            {originals.length > 0 && (
              <div className="flex rounded border border-border overflow-hidden text-xs shrink-0">
                {[['clean', t('desk_property_photos_view_clean')], ['original', t('desk_property_photos_view_original')]].map(([v, label]) => (
                  <button key={v} type="button" onClick={() => setImgView(v)}
                    className={`px-2 py-0.5 transition-colors ${imgView === v
                      ? 'bg-primary/15 text-foreground'
                      : 'text-muted-foreground hover:text-foreground'}`}>
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* ⚠️ READ-ONLY in 'original'. These rows carry the portal's watermark and are
              excluded from every publisher by kind; they exist for dedup/pHash. Reordering
              or removing them here would put the watermark back on the path 0332 cleared,
              so the whole editing affordance — drag, ×, move-to-docs, and the add tile —
              is simply not rendered in this view. */}
          {imgView === 'original' ? (
            <>
              <div className="flex flex-wrap gap-2">
                {originals.map(img => (
                  <div key={img.id} className="relative w-32 h-32">
                    <img src={img.url} className="w-32 h-32 object-cover rounded border border-border" alt="" />
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">{t('desk_property_photos_original_note')}</p>
            </>
          ) : (
          <>
          {adPhotos.length > 0 && (
            <div className="mb-2" data-pm-ad-photos>
              <p className="text-[11px] text-muted-foreground mb-1">{t('desk_pm_photos_from_ad')}</p>
              <div className="flex flex-wrap gap-2">
                {adPhotos.map(img => (
                  <div key={img.id} className="relative w-32 h-32">
                    <img src={img.url} className="w-32 h-32 object-cover rounded border border-border opacity-90" alt="" />
                  </div>
                ))}
              </div>
              {onCapture && <p className="text-[11px] text-muted-foreground mt-1">{t('desk_pm_photos_to_version')}</p>}
              {!onCapture && <p className="text-[11px] text-muted-foreground mt-2 mb-1">{t('desk_pm_photos_mine')}</p>}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {photos.map((img, i) => (
              <div key={img.id} className="relative w-32 h-32 cursor-grab" draggable
                onDragStart={() => { dragIdx.current = i }}
                onDragOver={e => e.preventDefault()}
                onDrop={() => dropPhotoAt(i)}>
                <img src={img.url} className="w-32 h-32 object-cover rounded border border-border pointer-events-none" alt="" />
                <button type="button" title={t('desk_property_remove_photo')} className={rmBtn} onClick={() => removeImg(img.id)}>×</button>
                <button type="button" title={t('desk_property_move_to_docs')}
                  className={mvBtn} onClick={() => moveImg(img.id, 'document')}><Icon name="lock" /></button>
              </div>
            ))}
            {pendPhotos.map(p => (
              <div key={p.preview || p.file.name} className="relative w-32 h-32 opacity-70">
                <img src={p.preview} className="w-32 h-32 object-cover rounded border border-dashed border-border" alt="" />
                <button type="button" title={t('desk_property_remove_photo')} className={rmBtn} onClick={() => dropPending(p)}>×</button>
              </div>
            ))}
            <label className="w-32 h-32 rounded border border-dashed border-border cursor-pointer flex items-center justify-center text-2xl text-muted-foreground hover:border-primary/40 transition-colors" title={t('desk_property_add_photos')}>
              +
              <input type="file" accept="image/*,.heic,.heif" multiple className="hidden"
                onChange={e => { handlePickImages(e.target.files, 'photo'); e.target.value = '' }} />
            </label>
          </div>
          </>
          )}

          {/* Upload size — asked, not decided for the agent. Hidden in the read-only
              'original' view, which uploads nothing. The estimate is real (files are encoded
              to measure it), so it only appears once there are queued photos to measure. */}
          {imgView !== 'original' &&
            <PhotoSizePicker value={photoSize} onPick={pickPhotoSize} est={sizeEst} />}

          <p className="text-xs text-muted-foreground mt-3 mb-1">
            {t('desk_property_docs_section')}
          </p>
          <div className="flex flex-wrap gap-2">
            {docs.map(img => (
              <div key={img.id} className="relative w-32 h-32">
                {isImgUrl(img.url)
                  ? <img src={img.url} className="w-32 h-32 object-cover rounded border border-amber-500/50" alt="" />
                  : <a href={img.url} target="_blank" rel="noreferrer"
                      className="w-32 h-32 rounded border border-amber-500/50 flex flex-col items-center justify-center text-muted-foreground hover:border-amber-500 transition-colors">
                      <span className="text-2xl"><Icon name="file-text" size={24} /></span>
                      <span className="text-xs uppercase">{img.url.split('.').pop()}</span>
                    </a>}
                <button type="button" title={t('desk_property_remove_photo')} className={rmBtn} onClick={() => removeImg(img.id)}>×</button>
                {isImgUrl(img.url) && (
                  <button type="button" title={t('desk_property_move_to_photos')}
                    className={mvBtn} onClick={() => moveImg(img.id, 'photo')}>🖼</button>
                )}
              </div>
            ))}
            {pendDocs.map(p => (
              <div key={p.preview || p.file.name} className="relative w-32 h-32 opacity-70">
                {p.preview
                  ? <img src={p.preview} className="w-32 h-32 object-cover rounded border border-dashed border-amber-500/50" alt="" />
                  : <div className="w-32 h-32 rounded border border-dashed border-amber-500/50 flex flex-col items-center justify-center text-muted-foreground">
                      <span className="text-2xl"><Icon name="file-text" size={24} /></span>
                      <span className="text-xs truncate max-w-full px-1">{p.file.name}</span>
                    </div>}
                <button type="button" title={t('desk_property_remove_photo')} className={rmBtn} onClick={() => dropPending(p)}>×</button>
              </div>
            ))}
            <label className="w-32 h-32 rounded border border-dashed border-border cursor-pointer flex items-center justify-center text-2xl text-muted-foreground hover:border-amber-500/60 transition-colors" title={t('desk_property_add_documents')}>
              +
              <input type="file" accept="image/*,.pdf,.xls,.xlsx,.doc,.docx,.csv,.txt" multiple className="hidden"
                onChange={e => { handlePickImages(e.target.files, 'document'); e.target.value = '' }} />
            </label>
          </div>

          {heicBusy > 0 && <p className="text-xs text-muted-foreground mt-2"><IconText>{tLog('desk_property_heic_converting', { n: heicBusy })}</IconText></p>}
          {imgBusy && <p className="text-xs text-muted-foreground mt-2"><IconText>{t('desk_property_uploading')}</IconText></p>}
          {imgErr && <p className="text-xs text-destructive mt-2"><IconText>{tLog('desk_property_upload_failed', { err: imgErr })}</IconText></p>}
          {saved?.listingId && pendingImgs.length > 0 && !imgBusy && (
            <button type="button" className={`${btn('ghost')} mt-2`} onClick={retryUploads} data-pm-retry-uploads>
              {tLog('desk_pm_retry_uploads', { n: pendingImgs.length })}
            </button>
          )}
          {!saved && pendingImgs.length > 0 && !imgErr &&
            <p className="text-xs text-muted-foreground mt-2">{t('desk_property_files_upload_note')}</p>}
        </Facet>
          )
        })()}
        </PmSection>
        )}
        <PmSection id="owner">
        <Facet num="05" title={t('desk_owner_contact_title')} labelKey="desk_pm_nav_owner"
          summary={[owner.name.trim(), owner.phone.trim()].filter(Boolean).join(' · ')}>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs text-muted-foreground">{t('desk_owner_contact_name')}
              <input className={input} value={owner.name} onChange={e => setOwner(w => ({ ...w, name: e.target.value }))} />
              {provLine('owner_name')}
            </label>
            <label className="text-xs text-muted-foreground">{t('desk_owner_contact_phone')}
              <input className={input} value={owner.phone} onChange={e => setOwner(w => ({ ...w, phone: e.target.value }))} />
              {provLine('owner_phone')}
            </label>
            <label className="text-xs text-muted-foreground col-span-2">{t('desk_owner_contact_email')}
              <input className={input} type="email" value={owner.email} onChange={e => setOwner(w => ({ ...w, email: e.target.value }))} />
            </label>
          </div>
        </Facet>
        </PmSection>
        {!embedded && (
        <PmSection id="observed">
        <div className={card}>
          <p className="text-xs font-semibold text-muted-foreground">06 · {t('desk_observed_from_section')}</p>
          <p className="text-[11px] text-muted-foreground mb-2">{t('desk_observed_from_hint')}</p>
          <div className="flex flex-wrap gap-2">
            {OBSERVED_FROM_OPTIONS.map(o => (
              <button key={o.value} onClick={() => setSourceKind(o.value)}
                aria-pressed={sourceKind === o.value}
                className={pill('none', { door: true, extra: sourceKind === o.value ? 'bg-foreground text-background border-foreground' : 'border-border text-muted-foreground hover:bg-muted' })}>
                {o.label}
              </button>
            ))}
          </div>
        </div>
        </PmSection>
        )}
        {(!embedded || !saved || parseConflicts.length > 0) && (
        <PmSection id="paste">
          <div className={card}>
            <p className="text-xs font-semibold text-muted-foreground mb-2">07 · {t('desk_pm_nav_paste')}</p>
            <div className="space-y-3 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-4 lg:items-start">
        {/* Three ways in, top to bottom: send the owner a link / AI parse a paste /
            fill the form yourself (2026-07-15 design). Fresh adds only — an existing
            or AI-drafted property is already past the "how does data get in" fork. */}
        {!proposalId && !saved && !existing && (
          <FillLinkSection kind="property" agentId={agentId} agentName={agentName}
            getPrefill={() => ({ name: owner.name, phone: owner.phone, kind: prop.kind, intent: offer.intent,
              // what the agent already typed about the place and size starts the owner's form (2026-10-16); the server whitelists it
              address: prop.address, street: prop.street, street_number: prop.street_number, district_code: prop.district_code,
              city: districtCity, lat: prop.lat, lng: prop.lng, rooms: facts.rooms === '' ? undefined : facts.rooms })} />
        )}
        {/* ⚠️ This used to read `!proposalId && !saved`, which UNMOUNTED the box as soon as the
            first parse returned a proposal id — so "paste, parse, paste more" was impossible and
            the conflict panel below could only ever compare a parse against typed-in values. The
            owner's flow is explicitly iterative: "paste text, parse, save, come back, paste
            another text for more details, parse, enrich". An AI draft opened for review shows it
            for the same reason. `!saved` stays: after Save this modal is showing what was
            written, and more text belongs to the next visit (which reopens with `existing`,
            where the box is shown again). */}
        {(!saved || batchPropertyId) && (
          <div>
            <p className="text-xs font-semibold text-muted-foreground mb-2">{t('desk_modal_paste_heading')}</p>
            {/* text 1, 2, … — a WhatsApp message, an ad, the agent's own description; one press reads them all */}
            <div className="space-y-2" data-pm-paste-texts>
              {pasteTexts.map((txt, i) => (
                <div key={i}>
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-medium text-muted-foreground">{tLog('desk_modal_paste_text_n', { n: i + 1 })}</span>
                    {pasteTexts.length > 1 && (
                      <button type="button" className="text-[11px] text-muted-foreground underline"
                        onClick={() => setPasteTexts(ts => ts.filter((_, j) => j !== i))}>{t('desk_modal_paste_text_remove')}</button>
                    )}
                  </div>
                  <textarea className={`${input} font-mono text-sm`} rows={3} value={txt}
                    aria-label={tLog('desk_modal_paste_text_n', { n: i + 1 })}
                    placeholder={i === 0 ? t('desk_modal_paste_owner_ph') : t('desk_modal_paste_more_ph')}
                    onChange={e => { const v = e.target.value; setPasteTexts(ts => ts.map((s, j) => (j === i ? v : s))) }} />
                </div>
              ))}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <button type="button" className={btn('ghost')} disabled={parsing}
                onClick={() => setPasteTexts(ts => [...ts, ''])}>{t('desk_modal_paste_add_text')}</button>
              <button className={btn('ghost')} disabled={parsing || !pasteText.trim() || pasteText === parsedText} onClick={handlePasteParse}>
                {parsing ? t('desk_modal_parsing') : t('desk_modal_parse_to_draft')}
              </button>
            </div>
          </div>
        )}

        {/* ── the parse disagreed with something already here ─────────────────────────────
            Owner: "user must be asked do you want to replace this with this? yes or no. if
            there are many ask on each one."

            ⚠️ THE COMMON CASE IS NOT TWO PASTES. The paste box only renders before a proposal
            exists, so parsing twice in one sitting is not even reachable — the conflict that
            actually happens is pasting into a property opened for EDIT, where every field is
            already filled from the database. That is the worse case and the one this catches:
            before today a paste silently rewrote stored values the agent never looked at.

            ⚠️ DEFAULT IS KEEP. Nothing is replaced until it is ticked, so closing the modal,
            missing the panel, or pressing the button without reading all change nothing — a
            confirmation that approves by default is the silent overwrite wearing a button. */}
        {/* ⚠️ ONE ROW PER FIELD (owner: "if there are many ask on each one"). Extracted to its
            own file so it can be RENDERED — inline here it lived inside a ~14,000-line
            login-gated modal that mounts a map and a gallery, so "one row per field" was a
            claim about code rather than about markup anyone had seen. */}
        <ParseConflictPanel
          conflicts={parseConflicts}
          answers={parseAnswers}
          cardClass={card}
          onToggle={(key, next) => setParseAnswers(a => ({ ...a, [key]: next }))}
          onApply={() => {
            applyParsePatch(acceptedPatch(parseConflicts, parseAnswers))
            setParseConflicts([]); setParseAnswers({})
          }}
          onKeepAll={() => { setParseConflicts([]); setParseAnswers({}) }}
        />
        {draft?.missing_fields?.length > 0 && (
          <p className="text-xs text-amber-600 dark:text-amber-400"><IconText>{tLog('desk_modal_ai_couldnt_fill', { 'list of field names': draft.missing_fields.join('; ') })}</IconText></p>
        )}
            </div>
            {batches.length > 0 && (
              <div className="mt-3" id="pm-paste-history">
                <p className="text-xs font-semibold text-muted-foreground mb-1">{t('desk_batch_history_title')}</p>
                <PasteBatchHistory batches={batches} sourceLabel={batchSourceLabel} fieldLabel={batchFieldLabel}
                  onDecide={decideBatch} onUndo={undoBatch} busy={batchBusy} />
              </div>
            )}
          </div>
        </PmSection>
        )}
        <PmSection id="notes-ad">

        {/* B46: the owner's freeform note from the fill-property form (stamped onto
            listing.meta.owner_note) — was previously stranded in intake_link.submission. */}
        {lineage?.listing?.owner_note && (
          <div className={`${card} border-amber-500/40`}>
            <p className="text-xs font-semibold text-muted-foreground mb-1"><Icon name="sticky-note" /> {t('desk_owner_note_label')}</p>
            <p className="text-sm text-foreground whitespace-pre-wrap">{lineage.listing.owner_note}</p>
          </div>
        )}

        {/* THE OWNER'S OWN WORDS (owner 2026-07-30) — the ad's scraped description. Sits
            beside the intake-form note above because they answer the same question from two
            sources: what the owner actually said. Same renderer as the lineage/leads modal;
            renders nothing when the ad carried no text. */}
        {(() => { const ad = adDescriptionOf(lineage); return ad ? <AdDescriptionCard {...ad} /> : null })()}
        </PmSection>
        {!embedded && (<>
        <p className="text-[11px] font-medium text-muted-foreground pt-2">{t('desk_pm_group_work')}</p>
        <PmSection id="agreement">
        <Facet num="09" title={t('desk_agreement_facet_title')} labelKey="desk_pm_nav_agreement" badge={<span className="ml-2 rounded-full border border-amber-500/50 bg-amber-500/10 px-1.5 text-[10px] font-normal leading-4 text-amber-700 dark:text-amber-300">🔒 {t('desk_pm_agent_only')}</span>}
          hint={t('desk_agreement_facet_hint')}
          locked={!saved} lockedNote={t('desk_terms_record_property_first')}
          summary={coops.length ? tLog('desk_agreement_summary', { n: coops.length, s: coops.length === 1 ? '' : 's' }).split(' / ')[0] : t('desk_agreement_summary').split(' / ')[1]}>
          {coops.length === 0 && !coopContact ? (
            <p className="text-sm text-muted-foreground">
              {t('desk_agreement_no_contact')}
            </p>
          ) : (
            <CooperationChips cooperations={coops} privileged={privileged}
              onOpen={c => setCoopModal({ propertyId: c.property_id, propertyLabel: prop.address.trim() || null, cooperationId: c.id })}
              onAdd={coopContact && saved?.propertyId
                ? () => setCoopModal({ propertyId: saved.propertyId, propertyLabel: prop.address.trim() || null,
                    contactPartyId: coopContact.party_id, contactName: coopContact.name || owner.name.trim() || null,
                    seedIntent: coopIntentOf(offer.intent), seedCurrency: offer.currency || null,
                    seedPrice: offer.price !== '' && offer.price != null ? offer.price : null })
                : null} />
          )}
        </Facet>
        </PmSection>
        <PmSection id="conditions">
        {/* Sale listings get the same click-to-open panel with the sale-shaped fields
            (0080: cash-only, occupancy, appraisal...) — every intent has
            owner-stated conditions worth recording before viewings. */}
        {['rent', 'daily_rent', 'sale', 'girao', 'ijara'].includes(offer.intent) && (() => {
          // Hand-entered property: there is no ad to disagree with, so the offer's own
          // intent IS the deal — no cooperation override needed here (0219).
          const termsMode = termsModeFor(offer.intent)
          const titleKey = termsMode === 'sale' ? 'desk_sale_terms_title'
            : termsMode === 'girao' ? 'desk_girao_terms_title' : 'desk_terms_facet_title'
          return saved?.listingId ? (
            /* Click-to-open chip like the meeting/cooperation sections (2026-07-11). */
            <div className={card} data-pm-row="conditions">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs font-semibold text-muted-foreground" title={t(titleKey)}>
                  <span className="tabular-nums">10 · </span><span className="text-foreground">{t('desk_pm_nav_conditions')}</span>
                  <span className="ml-2 rounded-full border border-amber-500/50 bg-amber-500/10 px-1.5 text-[10px] font-normal leading-4 text-amber-700 dark:text-amber-300">🔒 {t('desk_pm_agent_only')}</span>
                </span>
              <div className="flex flex-wrap items-center gap-2">
                <button title={t('desk_terms_facet_hint')} onClick={() => setShowTerms(true)}
                  className={pill('add', { door: true })}>
                  <Icon name="house" />＋
                </button>
                <RentalTermsSummary listingId={saved.listingId} refreshKey={tlRefresh} />
              </div>
              </div>
              {showTerms && (
                <RentalTermsModal listingId={saved.listingId} propertyId={saved.propertyId} mode={termsMode} agentId={agentId}
                  rent={offer.price} rentCurrency={offer.currency}
                  onClose={() => setShowTerms(false)} onRecorded={() => setTlRefresh(n => n + 1)} />
              )}
            </div>
          ) : (
            <Facet num="10" labelKey="desk_pm_nav_conditions" title={t(titleKey)}
              locked lockedNote={t('desk_terms_record_property_first')} />
          )
        })()}
        </PmSection>
        <PmSection id="meeting">
        {/* MEETING — its own section right after the agreement (2026-07-08): property +
            resolved contact + agreed commission arrive pre-filled in the viewing form.
            Same little dashed chip as the 🤝＋ cooperation add, not a big button. */}
        {(onAddMeeting || onCreateContract) && saved?.propertyId && (
          <div className={`${card} flex flex-wrap items-center justify-between gap-2`} data-pm-row="meeting">
            <span className="text-xs font-semibold text-muted-foreground">
              <span className="tabular-nums">11 · </span><span className="text-foreground">{t('desk_pm_nav_meeting')}</span>
              {tIn(getLanguage() === 'ka' ? 'en' : 'ka', 'desk_pm_nav_meeting') && <span className="ml-2 font-normal">{tIn(getLanguage() === 'ka' ? 'en' : 'ka', 'desk_pm_nav_meeting')}</span>}
            </span>
            <div className="flex flex-wrap gap-1.5">
              {onCreateContract && <button title="ხელშეკრულება · Contract"
                onClick={() => onCreateContract(saved.propertyId)}
                className={pill('add', { door: true })}>
                <Icon name="file-text" />＋
              </button>}
              {onAddMeeting &&
              <button title={t('desk_meeting_add_tooltip')}
                onClick={() => onAddMeeting(buildMeetingSeed())}
                className={pill('add', { door: true })}>
                <Icon name="calendar" />＋
              </button>}
            </div>
          </div>
        )}
        </PmSection>
        <PmSection id="more">
        {/* BUSINESSES HERE (2026-07-10, BUSINESS_ARCHITECTURE.md §7) — the ONLY add-business
            entry point in the whole Desk, by design (property-first: every registered business
            has an address). Visible-but-locked until the property is saved (2026-07-11, was
            fully hidden — agents couldn't discover it existed); a business needs its
            property_id. */}
        <Facet num="12" labelKey="desk_pm_nav_business" title={t('desk_biz_section_title')} defaultOpen={false}
          summary={t('desk_biz_facet_summary')}
          locked={!saved?.propertyId} lockedNote={t('desk_terms_record_property_first')}>
          {saved?.propertyId && <BusinessSection propertyId={saved.propertyId} />}
        </Facet>
        {(() => {
          const gaps = propertyGaps(prop, offer, facts, owner).filter(Boolean)
          return gaps.length > 0 && (
            <Facet num="13" labelKey="desk_pm_nav_better" title={t('desk_gap_heading')} defaultOpen={false}
              summary={tLog('desk_pm_gaps_n', { n: gaps.length })} summaryTone="warn">
              <BetterToKnow items={gaps} />
            </Facet>
          )
        })()}
        {saved?.propertyId && <PrivateNotesPanel propertyId={saved.propertyId} agentId={agentId} key={`notes-${tlRefresh}`} pm={{ num: '14', labelKey: 'desk_pm_nav_notes' }} />}
        {(existing?.propertyId || saved?.propertyId) && <AssumptionsFacet propertyId={existing?.propertyId || saved?.propertyId} pm={{ num: '15', labelKey: 'desk_pm_nav_assumed' }} />}
        {(existing?.propertyId || saved?.propertyId) && <PromisesFacet propertyId={existing?.propertyId || saved?.propertyId} pm={{ num: '16', labelKey: 'desk_pm_nav_promises' }} />}
        {/* T38/B1184: the batches behind this flat — pasted messages, our uploads, typed notes,
            assembled CLUSTER-WIDE by running_note_v1 (a bare property_id would show one advert's
            batches and hide the rest). Renders nothing when there are none. */}
        {(existing?.propertyId || saved?.propertyId) && (
          <RunningNote kind="property" id={existing?.propertyId || saved?.propertyId}
            refreshKey={tlRefresh} cardClass={card} />
        )}
        {/* ბირჟა (PublishToPoolFacet) removed 2026-07-21 — see the note at the lineage
            modal's workbench block. The Pool tab and its server endpoints stay. */}
        {saved?.propertyId && <AvailabilityFacet propertyId={saved.propertyId} agentId={agentId} pm={{ num: '17', labelKey: 'desk_pm_nav_avail' }} />}
        {/* Provenance — how this property is known to the market (unified card: shown
            when the property arrived via a scrape/reshare; a hand-entered property
            simply has no platform provenance to show — that's the only difference). */}
        {lineage && (() => {
          const { listing: ln, origin, siblings = [], price_history = [] } = lineage
          const rows = [ln, origin, ...siblings].filter(Boolean)
          const seen = new Set()
          const posted = rows.filter(r => { const id = r.listing_id || r.id; if (!id || seen.has(id)) return false; seen.add(id); return true })
          return (
            <Facet num="18" labelKey="desk_pm_nav_posted" title={t('desk_posted_on_facet_title')} defaultOpen={false}
              summary={`${tLog('desk_posted_on_summary', { n: posted.length, s: posted.length === 1 ? '' : 's' })}${price_history.length ? ` · ${tLog('desk_price_changes_summary', { n: price_history.length, s: price_history.length === 1 ? '' : 's' })}` : ''}`}>
              <div className="space-y-1">
                {posted.map(r => (
                  <div key={r.listing_id || r.id} className="flex items-center justify-between text-sm py-1 border-b border-border/40 last:border-0">
                    <span className="font-medium"><PlatformPill source={r.source} />
                      {r.address && <span className="block text-xs text-muted-foreground font-normal">{r.address}</span>}
                    </span>
                    <span className="flex items-center gap-2">
                      {r.price != null && <Money amount={r.price} currency={r.currency} className="text-muted-foreground text-xs" />}
                      {r.source_url && <a href={r.source_url} target="_blank" rel="noreferrer" className="text-xs text-primary"><IconText>{t('desk_open_link')}</IconText></a>}
                    </span>
                  </div>
                ))}
              </div>
              {price_history.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs font-semibold text-muted-foreground mb-1">{t('desk_price_history_section')}</p>
                  {price_history.map((h, i) => (
                    <div key={i} className="flex items-center justify-between text-sm py-0.5">
                      <span className={h.direction === 'down' ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}>
                        {h.direction === 'down' ? '↓' : '↑'} {h.from ?? '—'} {h.currency || ''} → {h.to ?? '—'} {h.currency || ''}
                        {h.pct != null && <span className="ml-1 tabular-nums">({h.pct > 0 ? '+' : ''}{h.pct}%)</span>}
                      </span>
                      <span className="text-xs text-muted-foreground">{timeAgo(h.observed_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </Facet>
          )
        })()}
        {saved?.propertyId && <PropertyDuplicates propertyId={saved.propertyId} agentId={agentId} agentName={agentName} privileged={privileged} pm={{ num: '19', labelKey: 'desk_pm_nav_dups' }} />}
        {saved?.propertyId && <PropertyTimeline propertyId={saved.propertyId} refreshKey={tlRefresh} pm={{ num: '20', labelKey: 'desk_pm_nav_timeline' }} />}
        {/* Archive — "I'm not working this one" (unified card). Soft + reversible:
            just flips the represents_property inventory edge (0064) to dismissed;
            nothing about the property/claims/publications is touched. */}
        {saved?.propertyId && agentId && <InventoryControls propertyId={saved.propertyId} agentId={agentId} />}
        </PmSection>
        </>)}
        </div>
      </div>

      {/* Sticky footer (redesign stage 1): the save button, errors and "did not land" warnings stay in
          reach at any scroll depth, and each message jumps back to the section it is about. */}
      <div className={embedded ? 'mt-4 space-y-2' : 'sticky bottom-0 z-20 -mx-5 -mb-5 mt-4 px-5 py-3 bg-card border-t border-border space-y-2'} data-pm-footer>
        {err && (
          <p className="text-sm text-destructive flex flex-wrap items-center gap-x-2"><span><Icon name="alert-triangle" /> {err}</span>
            {errSection && <button type="button" className="underline text-xs" onClick={() => jumpToSection(errSection)}>{t('desk_pm_go_to_field')}</button>}
          </p>
        )}
        {saveWarnings.length > 0 && (
          <div className="rounded-lg border border-amber-500/50 bg-amber-500/5 p-2 text-xs text-amber-700 dark:text-amber-300 max-h-32 overflow-y-auto" data-save-warnings>
            <p className="font-medium mb-1"><Icon name="alert-triangle" /> {t('desk_save_warn_title')}</p>
            <ul className="space-y-0.5">
              {saveWarnings.map(([k, detail]) => (
                <li key={k}><span className="font-medium">{tOr('desk_save_warn_' + k, k)}</span>: {detail}
                  {' '}<button type="button" className="underline" onClick={() => jumpToSection(WARNING_SECTION[k] || 'facts')}>{t('desk_pm_go_to_field')}</button></li>
              ))}
            </ul>
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-2">
          {/* Archive only exists for a persisted draft (proposalId) that hasn't been saved yet:
              it dismisses the pending proposal so junk drafts leave the review list. */}
          {proposalId && !saved ? (
            // shown on phones too: hidden there, a stray draft could not be archived from a phone at all (2026-11-03)
            <button className={`${btn('ghost')} inline-flex`} disabled={archiving || saving}
              title={t('desk_archive_draft_tooltip')}
              onClick={handleDismiss}>
              {archiving ? t('desk_archive_draft_busy') : t('desk_archive_draft_button')}
            </button>
          ) : <span className="hidden sm:block" />}
          {/* Phone: the header ✕ closes, so the footer is just the save button, full width (mockup). */}
          <div className="flex gap-2 w-full sm:w-auto">
            {prefillMissing && (
              <span className="self-center text-xs text-muted-foreground" data-pm-prefill-missing>
                {/* inline pair: i18n.js is outside this change — move to a key when it is next edited */}
                {getLanguage() === 'ka'
                  ? 'ქონების მონაცემები არ ჩაიტვირთა — ჩაწერა შეუძლებელია'
                  : "This property's facts have not loaded — recording is disabled"}
              </span>
            )}
            {!embedded && (
              <button type="button" className="hidden sm:inline text-xs text-muted-foreground underline self-center" onClick={() => jumpToSection('observed')} data-pm-observed-reminder>
                {t('desk_observed_from_section').split('—')[0].trim()}: {OBSERVED_FROM_OPTIONS.find(o => o.value === sourceKind)?.label || sourceKind}
              </button>
            )}
            {!embedded && <button className={`${btn('ghost')} hidden sm:inline-flex`} onClick={requestClose}>{saved ? t('desk_done_button') : t('desk_property_cancel')}</button>}
            <button className={`${btn('primary')} flex-1 sm:flex-none justify-center`} disabled={saving || archiving || heicBusy > 0 || !canSave || (saved && !dirty)} onClick={handleSave}>
              {saving ? t('desk_property_saving')
                : saved ? (dirty ? t('desk_property_record_update') : t('desk_property_recorded'))
                : proposalId ? t('desk_property_accept_save') : t('desk_property_record_button')}
            </button>
          </div>
        </div>
      </div>
      {coopModal && (
        <CooperationModal
          propertyId={coopModal.propertyId}
          propertyLabel={coopModal.propertyLabel}
          cooperationId={coopModal.cooperationId}
          contactPartyId={coopModal.contactPartyId}
          contactName={coopModal.contactName}
          seedIntent={coopModal.seedIntent || null}
          seedCurrency={coopModal.seedCurrency || null}
          seedPrice={coopModal.seedPrice ?? null}
          onClose={() => setCoopModal(null)}
          onSaved={() => {
            if (saved?.propertyId) listCooperations({ propertyId: saved.propertyId }).then(setCoops).catch(() => {})
            setTlRefresh(n => n + 1)
          }} />
      )}
    </Shell>
  )
}

// ── Exports — everything IntakeTab.jsx (or a sibling desk module) still references ──
export {
  PointMapPicker,
  PROPERTY_KIND_OPTIONS, LISTING_INTENT_OPTIONS, OBSERVED_FROM_OPTIONS,
  districtReach, DistrictOptions, Facet,   // cityKey/districtInCity keep their inline `export` (they were exported from IntakeTab already)
  RentalTermsSummary, fieldMaturity, RentalTermsByAgent, RentalTermsModal,
  // evLabel moved to ./PropertyTimeline.jsx (2026-07-27) — nothing imported it from here.
  PropertyTimeline, PrivateNotesPanel, AssumptionsFacet, PromisesFacet, AvailabilityFacet, AvailabilityLine, PublicPageFacet,
  InventoryControls, PropertyDuplicates, ManualPropertyModal,
}
